/**
 * 메인 컨트롤러
 * 전체 프로그램 흐름 제어 및 모듈 통합
 */
import { InputModule } from '../modules/InputModule.js';
import { LayoutSelectorModule } from '../modules/LayoutSelectorModule.js';
import { OutputModule } from '../modules/OutputModule.js';
import { StudentModel } from '../models/Student.js';
import { LayoutService } from '../services/LayoutService.js';
import { RandomService } from '../services/RandomService.js';
import { PairingService } from '../services/PairingService.js';
import { SeatOccupancyService } from '../services/SeatOccupancyService.js';
import { SeatReorderService } from '../services/SeatReorderService.js';
import { BackupService } from '../services/BackupService.js';
import { CustomLayoutService, SeatPosition } from '../services/CustomLayoutService.js';
import { CustomLayoutManager } from '../managers/CustomLayoutManager.js';
// import { SeatType } from '../models/Seat.js'; // 향후 사용 예정
import { Student } from '../models/Student.js';
import { Seat } from '../models/Seat.js';
import { LayoutRenderer, LayoutRendererDependencies } from '../managers/LayoutRenderer.js';
import { AnimationManager, AnimationManagerDependencies } from '../managers/AnimationManager.js';
import { StorageManager, StorageManagerDependencies } from '../managers/StorageManager.js';
import { CSVFileHandler, CSVFileHandlerDependencies } from '../managers/CSVFileHandler.js';
import { PrintExportManager, PrintExportManagerDependencies } from '../managers/PrintExportManager.js';
import { UIManager, UIManagerDependencies } from '../managers/UIManager.js';
import { StudentTableManager, StudentTableManagerDependencies } from '../managers/StudentTableManager.js';
import { logger } from '../utils/logger.js';
import { ErrorHandler } from '../utils/errorHandler.js';
import { ErrorCode } from '../types/errors.js';
import { InputValidator, ValidationRules } from '../utils/inputValidator.js';
import { KeyboardNavigation } from '../utils/keyboardNavigation.js';
import { KeyboardDragDropManager } from '../managers/KeyboardDragDropManager.js';
import { ClassManager, ClassManagerDependencies } from '../managers/ClassManager.js';
import { CloudMigrationModule } from '../modules/CloudMigrationModule.js';
import { VisitorCounterModule } from '../modules/VisitorCounterModule.js';

/**
 * 히스토리 데이터 타입
 */
interface HistoryData {
    seats?: Seat[];
    students?: Student[];
    layout?: string;
    [key: string]: unknown; // 확장 가능한 구조
}

/**
 * 히스토리 항목 타입
 */
interface HistoryItem {
    type: string;
    data: HistoryData;
}

/**
 * 레이아웃 결과 데이터 타입
 */
interface LayoutResultData {
    seats?: Seat[];
    students?: Student[];
    [key: string]: unknown;
}

/**
 * 좌석 이력 항목 타입
 */
interface SeatHistoryItem {
    id: string;
    date: string;
    layout: Array<{seatId: number, studentName: string, gender: 'M' | 'F'}>;
    pairInfo?: Array<{student1: string, student2: string}>;
    timestamp: number;
    // 배치 형태 정보 (복원을 위해 필요)
    layoutType?: string; // 'single-uniform' | 'pair-uniform' | 'group' | 'custom'
    singleMode?: string; // 'basic-row' | 'gender-row' | 'gender-symmetric-row'
    pairMode?: string; // 'gender-pair' | 'same-gender-pair'
    partitionCount?: number; // 분단 수
    groupSize?: string; // 'group-3' | 'group-4' | 'group-5' | 'group-6'
    classId?: string; // 반 ID (검증을 위해 저장)
    /**
     * 사용자 구성 배치의 책상 위치.
     * 이것을 저장하지 않으면 확정한 이력을 불러왔을 때 교사가 만든 교실 모양이
     * 기본 격자로 되돌아간다.
     */
    customPositions?: SeatPosition[];
}

/**
 * 공유 학생 데이터 타입 (압축 형식 또는 객체 형식)
 */
type SharedStudentData = [string, 'M' | 'F'] | {name: string, gender: 'M' | 'F'};

/**
 * 공유 정보 타입
 */
interface ShareInfo {
    t?: string;
    type?: string;
    s?: SharedStudentData[];
    students?: SharedStudentData[];
    n?: string[];
    names?: string[];
    l?: string;
    layout?: string;
    e?: string | number;
    p?: string;
    [key: string]: unknown; // 확장 가능한 구조
}


/**
 * 옵션 설정 타입
 */
interface OptionsData {
    layoutType?: string;
    pairMode?: string;
    groupSize?: string;
    groupGenderMix?: boolean;
    seatCount?: number;
    [key: string]: unknown; // 확장 가능한 구조
}

/**
 * 메인 컨트롤러 클래스
 * 전체 프로그램의 흐름을 제어하고 모듈들을 조율합니다.
 */
export class MainController {
    /** 사용자 구성 배치에서 좌석 영역 폭을 알 수 없을 때 쓰는 기본값 */
    private static readonly CARD_AREA_MIN_WIDTH_FALLBACK = 900;

    /** 진행 중인 Firebase 동기화 (중복 실행 방지) */
    private syncInFlight: Promise<void> | null = null;
    /** 자리 배치가 진행 중인지 (연출이 끝나기 전 재실행 방지) */
    private arrangingSeats: boolean = false;
    /** 사용자 구성 배치의 책상 위치 관리 */
    private customLayoutManager!: CustomLayoutManager;
    /**
     * 이력을 불러올 때 복원할 책상 위치.
     * renderExampleCards가 한 번 사용하고 비운다.
     */
    private pendingCustomPositions: SeatPosition[] | null = null;
    /**
     * 지금 화면에 놓인 책상 위치.
     *
     * 명렬표를 저장하거나 인원수를 바꾸면 미리보기가 통째로 다시 그려진다.
     * 저장소에만 의존하면 반을 선택하지 않은 상태에서는 저장되지 않으므로,
     * 재렌더 때마다 교사가 만든 배치가 기본 격자로 돌아가 버린다.
     */
    private customPositions: SeatPosition[] | null = null;

    private inputModule!: InputModule;
    private layoutSelectorModule!: LayoutSelectorModule;
    private outputModule!: OutputModule;
    private layoutRenderer!: LayoutRenderer;
    private animationManager!: AnimationManager;
    private storageManager!: StorageManager;
    private csvFileHandler!: CSVFileHandler;
    private printExportManager!: PrintExportManager;
    private uiManager!: UIManager;
    private studentTableManager!: StudentTableManager;
    private inputValidator!: InputValidator;
    private keyboardDragDropManager!: KeyboardDragDropManager;
    private classManager!: ClassManager;
    private cloudMigrationModule!: CloudMigrationModule;
    private visitorCounterModule!: VisitorCounterModule;
    
    private students: Student[] = [];
    private seats: Seat[] = [];
    private isInitialized: boolean = false;
    private fixedSeatIds: Set<number> = new Set(); // 고정 좌석 ID 목록
    private nextSeatId: number = 1; // 좌석 카드 고유 ID 생성기
    private dragSourceCard: HTMLElement | null = null; // 드래그 시작 카드 참조
    private dragOverIndicator: HTMLElement | null = null; // 드롭 위치 인디케이터
    private touchStartCard: HTMLElement | null = null; // 터치 시작 카드 참조 (모바일)
    private touchStartPosition: { x: number, y: number } | null = null; // 터치 시작 위치
    private isSyncing: boolean = false; // 동기화 중 플래그 (무한 루프 방지)
    private layoutHistory: HistoryItem[] = []; // 통합 히스토리 (모든 액션 추적)
    private historyIndex: number = -1; // 현재 히스토리 인덱스
    private isReadOnlyMode: boolean = false; // 읽기 전용 모드 (이력에서 불러온 경우)
    
    // 메모리 누수 방지를 위한 추적 변수
    private eventListeners: Array<{element: EventTarget, event: string, handler: EventListener | ((e: Event) => void)}> = [];
    private timers: Set<number> = new Set(); // setTimeout ID 추적

    constructor() {
        try {
            // 모듈 초기화
            this.inputModule = new InputModule('input-section');
            this.layoutSelectorModule = new LayoutSelectorModule('layout-section');
            
            this.outputModule = new OutputModule('output-section');
            
            // LayoutRenderer 초기화
            const layoutRendererDeps: LayoutRendererDependencies = {
                getStudents: () => this.students,
                getSeats: () => this.seats,
                getNextSeatId: () => this.nextSeatId,
                setNextSeatId: (id: number) => { this.nextSeatId = id; },
                incrementNextSeatId: () => { return this.nextSeatId++; },
                getFixedSeatIds: () => this.fixedSeatIds,
                outputModule: this.outputModule,
                isDevelopmentMode: () => this.isDevelopmentMode(),
                addEventListenerSafe: (element, event, handler, options) => 
                    this.addEventListenerSafe(element, event, handler, options),
                setupFixedSeatClickHandler: (card, seatId) => 
                    this.setupFixedSeatClickHandler(card, seatId),
                enableSeatSwapDragAndDrop: () => this.enableSeatSwapDragAndDrop(),
                setTimeoutSafe: (callback, delay) => this.setTimeoutSafe(callback, delay),
                saveLayoutToHistory: () => this.saveLayoutToHistory()
            };
            this.layoutRenderer = new LayoutRenderer(layoutRendererDeps);
            
            // AnimationManager 초기화
            const animationManagerDeps: AnimationManagerDependencies = {
                setTimeoutSafe: (callback, delay) => this.setTimeoutSafe(callback, delay),
                isDevelopmentMode: () => this.isDevelopmentMode()
            };
            this.animationManager = new AnimationManager(animationManagerDeps);
            
            // StorageManager 초기화
            const storageManagerDeps: StorageManagerDependencies = {
                outputModule: this.outputModule,
                isDevelopmentMode: () => this.isDevelopmentMode()
            };
            this.storageManager = new StorageManager(storageManagerDeps);
            
            // CSVFileHandler 초기화
            const csvFileHandlerDeps: CSVFileHandlerDependencies = {
                outputModule: this.outputModule,
                isDevelopmentMode: () => this.isDevelopmentMode(),
                setTimeoutSafe: (callback, delay) => this.setTimeoutSafe(callback, delay),
                addEventListenerSafe: (element, event, handler, options) => 
                    this.addEventListenerSafe(element, event, handler, options),
                handleCreateStudentTable: (count) => this.studentTableManager.createStudentTable(count),
                handleLoadClassNames: () => this.studentTableManager.loadClassNames(),
                handleDeleteStudentRow: (row) => this.handleDeleteStudentRow(row),
                updateStudentTableStats: () => this.updateStudentTableStats(),
                getFixedSeatIds: () => this.fixedSeatIds,
                getStudents: () => this.students,
                setStudents: (students) => { 
                    this.students = students.map((s, index) => {
                        const student: Student = {
                            id: index + 1,
                            name: s.name,
                            gender: s.gender,
                            fixedSeatId: s.fixedSeatId
                        };
                        return student;
                    });
                },
                syncSidebarToTable: (maleCount, femaleCount) => this.syncSidebarToTable(maleCount, femaleCount),
                moveToCell: (tbody, currentRow, columnName, direction) => this.moveToCell(tbody, currentRow, columnName, direction)
            };
            this.csvFileHandler = new CSVFileHandler(csvFileHandlerDeps);
            
            // PrintExportManager 초기화
            const printExportManagerDeps: PrintExportManagerDependencies = {
                outputModule: this.outputModule,
                isDevelopmentMode: () => this.isDevelopmentMode(),
                setTimeoutSafe: (callback, delay) => this.setTimeoutSafe(callback, delay),
                getSeats: () => this.seats
            };
            this.printExportManager = new PrintExportManager(printExportManagerDeps);
            
            // UIManager 초기화
            const uiManagerDeps: UIManagerDependencies = {
                outputModule: this.outputModule,
                isDevelopmentMode: () => this.isDevelopmentMode(),
                setTimeoutSafe: (callback, delay) => this.setTimeoutSafe(callback, delay),
                addEventListenerSafe: (element, event, handler, options) => 
                    this.addEventListenerSafe(element, event, handler, options),
                storageManager: this.storageManager,
                getStudents: () => this.students,
                setStudents: (students) => { this.students = students; },
                getSeats: () => this.seats,
                setSeats: (seats) => { this.seats = seats; },
                validateAndFixStudentInput: (input, inputType) => this.validateAndFixStudentInput(input, inputType),
                renderExampleCards: () => this.renderExampleCards(),
                getSeatHistory: () => this.getSeatHistory(),
                deleteHistoryItem: (historyId) => this.deleteHistoryItem(historyId),
                loadHistoryItem: (historyId) => this.loadHistoryItem(historyId)
            };
            this.uiManager = new UIManager(uiManagerDeps);
            
            // StudentTableManager 초기화
            const studentTableManagerDeps: StudentTableManagerDependencies = {
                outputModule: this.outputModule,
                isDevelopmentMode: () => this.isDevelopmentMode(),
                setTimeoutSafe: (callback, delay) => this.setTimeoutSafe(callback, delay),
                addEventListenerSafe: (element, event, handler, options) => 
                    this.addEventListenerSafe(element, event, handler, options),
                storageManager: this.storageManager,
                csvFileHandler: this.csvFileHandler,
                uiManager: this.uiManager,
                getFixedSeatIds: () => this.fixedSeatIds,
                getStudents: () => this.students,
                setStudents: (students) => { this.students = students; },
                handleDeleteStudentRow: (row) => this.handleDeleteStudentRow(row),
                moveToCell: (tbody, currentRow, columnName, direction) => this.moveToCell(tbody, currentRow, columnName, direction),
                updateRowNumbers: () => this.updateRowNumbers(),
                syncSidebarToTable: (maleCount, femaleCount) => this.syncSidebarToTable(maleCount, femaleCount),
                updatePreviewForGenderCounts: () => this.updatePreviewForGenderCounts()
            };
            this.studentTableManager = new StudentTableManager(studentTableManagerDeps);
            
            // InputValidator 초기화
            this.inputValidator = new InputValidator();
            
            // ClassManager 초기화
            const classManagerDeps: ClassManagerDependencies = {
                storageManager: this.storageManager,
                outputModule: this.outputModule,
                getCurrentSeats: () => this.seats,
                getCurrentStudents: () => this.students,
                setSeats: (seats) => { this.seats = seats; },
                setStudents: (students) => { this.students = students; },
                renderLayout: () => this.renderFinalLayout()
            };
            this.classManager = new ClassManager(classManagerDeps);

            // 사용자 구성 배치 (교사가 책상을 직접 옮기는 배치)
            this.customLayoutManager = new CustomLayoutManager({
                onPositionsChanged: (positions) => {
                    // 반이 선택되지 않았더라도 이번 세션 동안은 배치를 유지해야 한다
                    this.customPositions = positions;
                    this.saveCustomPositions(positions);
                }
            });
            
            // 저장 방식 변경 안내 및 예전 클라우드 자료 가져오기
            // (Firebase는 사용자가 '가져오기'를 눌렀을 때만 동적으로 불러온다)
            this.cloudMigrationModule = new CloudMigrationModule({
                outputModule: this.outputModule,
                storageManager: this.storageManager,
                onDataImported: () => {
                    this.updateClassSelect();
                    this.updateHistoryDropdown();
                }
            });
            
            // 방문자 수 표시 (Firebase는 이 경로에서만 동적으로 불러온다)
            this.visitorCounterModule = new VisitorCounterModule();
            this.visitorCounterModule.init();
            
            // 입력 필드 검증 설정
            this.setupInputValidation();
            
            // 이벤트 리스너 설정
            this.initializeEventListeners();
            
            // 이력 드롭다운 초기화
            this.uiManager.initializeHistoryDropdown();
            
            // 반 관리 초기화
            this.initializeClassManagement();
            
            // 사용자 구성 배치 안내 및 분단 개수 사용 가능 여부 반영
            this.updateCustomLayoutHint();
            this.togglePartitionSection(
                (document.querySelector('input[name="layout-type"]:checked') as HTMLInputElement | null)?.value !== 'custom'
            );
            
            // 저장 방식 변경 안내 (처음 한 번만)
            this.cloudMigrationModule.showNoticeIfNeeded();
            
            // 모바일 반응형 초기화
            this.initializeMobileResponsive();
            
            // 키보드 네비게이션 초기화
            this.initializeKeyboardNavigation();
            
            // 저장된 옵션 설정 불러오기
            this.storageManager.loadOptions((callback, delay) => this.setTimeoutSafe(callback, delay));
            
            // 초기 상태에서도 4단계 비활성화 체크 및 분단 개수 제한 적용
            const checkedLayoutType = document.querySelector('input[name="layout-type"]:checked') as HTMLInputElement;
            if (checkedLayoutType) {
                if (checkedLayoutType.value === 'single-uniform') {
                    this.updatePartitionLimitForSingleUniform();
                    // '이전 짝 금지' 비활성화
                    this.toggleAvoidPrevPartnerOption(false);
                } else if (checkedLayoutType.value === 'pair-uniform') {
                    this.updatePartitionLimitForPair();
                    // '이전 짝 금지' 활성화
                    this.toggleAvoidPrevPartnerOption(true);
                } else if (checkedLayoutType.value === 'group') {
                    // '이전 짝 금지' 비활성화
                    this.toggleAvoidPrevPartnerOption(false);
                }
            }
            
            // 초기 상태에서 고정 좌석 모드 확인
            const checkedFixedRandomMode = document.querySelector('input[name="custom-mode-2"][value="fixed-random"]:checked') as HTMLInputElement;
            if (checkedFixedRandomMode) {
                this.enableFixedSeatMode();
            }
            
            this.isInitialized = true;
            
            // URL 파라미터에서 공유 데이터 확인
            const urlParams = new URLSearchParams(window.location.search);
            // 뷰어 모드 파라미터 확인 (?v=)
            const viewParam = urlParams.get('v');
            // 하위 호환성: 'share' 또는 's' 파라미터 지원
            const shareParam = urlParams.get('s') || urlParams.get('share');
            
            if (viewParam) {
                // 뷰어 모드: 자리 배치도만 표시
                this.enableViewerMode(viewParam);
            } else if (shareParam) {
                // 공유된 배치 데이터 로드 (기존 방식)
                this.loadSharedLayout(shareParam);
            } else {
                // 저장된 데이터 불러오기
                this.loadSavedLayoutResult();
                
                
                
                if (this.seats.length > 0 && this.students.length > 0) {
                    
                    this.outputModule.showInfo('저장된 배치 결과가 로드되었습니다.');
                    // 저장된 배치 결과 렌더링
                    this.renderFinalLayout();
                } else {
                    
                    // 초기 예시 레이아웃 표시 (24명, 5분단)
                    this.renderInitialExampleLayout();
                    
                    // 초기값으로 미리보기 자동 실행
                    this.setTimeoutSafe(() => {
                        this.updatePreviewForGenderCounts();
                    }, 100);
                }
            }
        } catch (error) {
            ErrorHandler.handleAndShow(
                error,
                ErrorCode.INITIALIZATION_FAILED,
                (message) => this.outputModule.showError(message),
                { method: 'initialize' }
            );
        }
    }

    /**
     * 초기화 시 이력 드롭다운 업데이트
     */

    /**
     * 앱 초기 상태로 되돌리기
     */
    private resetApp(): void {
        try {
            // 읽기 전용 모드 해제
            this.disableReadOnlyMode();
            
            // 로컬 스토리지 정리 (앱 관련 데이터만)
            try {
                localStorage.removeItem('layoutResult');
                localStorage.removeItem('studentData');
            } catch {}

            // 입력값 초기화
            const maleInput = document.getElementById('male-students') as HTMLInputElement | null;
            const femaleInput = document.getElementById('female-students') as HTMLInputElement | null;
            const partitionInput = document.getElementById('number-of-partitions') as HTMLInputElement | null;
            if (maleInput) maleInput.value = '12';
            if (femaleInput) femaleInput.value = '12';
            if (partitionInput) partitionInput.value = '5';

            // 라디오 기본값 복원
            const singleUniform = document.querySelector('input[name="layout-type"][value="single-uniform"]') as HTMLInputElement | null;
            if (singleUniform) singleUniform.checked = true;

            const pairSubmenu = document.getElementById('pair-submenu');
            if (pairSubmenu) pairSubmenu.style.display = 'none';

            const pairModeGender = document.querySelector('input[name="pair-mode"][value="gender-pair"]') as HTMLInputElement | null;
            if (pairModeGender) pairModeGender.checked = true;

            const customRandom = document.querySelector('input[name="custom-mode-2"][value="random"]') as HTMLInputElement | null;
            if (customRandom) customRandom.checked = true;

            // 고정 좌석 모드 해제
            this.disableFixedSeatMode();
            this.fixedSeatIds.clear();
            this.nextSeatId = 1;

            // 좌석 영역 초기화
            const seatsArea = document.getElementById('seats-area');
            if (seatsArea) seatsArea.innerHTML = '';

            // 학생 테이블 제거 (존재한다면)
            const outputSection = document.getElementById('output-section');
            if (outputSection) {
                const tables = outputSection.querySelectorAll('table');
                tables.forEach(t => t.remove());
            }

            // card-layout-container 숨김 (초기화면으로 복귀)
            const cardContainer = document.getElementById('card-layout-container');
            if (cardContainer) {
                cardContainer.style.display = 'none';
            }

            // 액션 버튼 숨김
            const actionButtons = document.getElementById('layout-action-buttons');
            if (actionButtons) actionButtons.style.display = 'none';
            

            // 내부 상태 초기화
            this.students = [];
            this.seats = [];

            // 초기 예시 레이아웃 렌더링 및 미리보기 갱신
            this.renderInitialExampleLayout();
            this.updatePreviewForGenderCounts();

            this.outputModule.showInfo('초기화되었습니다. 기본 설정으로 돌아갑니다.');
        } catch (error) {
            ErrorHandler.handleAndShow(
                error,
                ErrorCode.RESET_FAILED,
                (message) => this.outputModule.showError(message),
                { method: 'resetApp' }
            );
        }
    }


    /**
     * 이벤트 리스너 초기화
     */
    /**
     * 입력 필드 검증 설정
     */
    private setupInputValidation(): void {
        // 남학생 수 입력 필드 검증
        const maleInput = document.getElementById('male-students') as HTMLInputElement;
        if (maleInput) {
            this.inputValidator.setupValidation(maleInput, {
                rules: [
                    ValidationRules.numeric('남학생 수'),
                    ValidationRules.range(0, 100, '남학생 수')
                ],
                showMessage: true,
                showIcon: true,
                highlightBorder: true
            });
        }

        // 여학생 수 입력 필드 검증
        const femaleInput = document.getElementById('female-students') as HTMLInputElement;
        if (femaleInput) {
            this.inputValidator.setupValidation(femaleInput, {
                rules: [
                    ValidationRules.numeric('여학생 수'),
                    ValidationRules.range(0, 100, '여학생 수')
                ],
                showMessage: true,
                showIcon: true,
                highlightBorder: true
            });
        }

        // 분단 수 입력 필드 검증
        const partitionInput = document.getElementById('number-of-partitions') as HTMLInputElement;
        if (partitionInput) {
            this.inputValidator.setupValidation(partitionInput, {
                rules: [
                    ValidationRules.numeric('분단 수'),
                    ValidationRules.range(1, 10, '분단 수')
                ],
                showMessage: true,
                showIcon: true,
                highlightBorder: true
            });
        }
    }

    private initializeEventListeners(): void {
        // 라디오 버튼 변경 이벤트 직접 리스닝
        const layoutInputs = document.querySelectorAll('input[name="layout-type"]');
        layoutInputs.forEach(input => {
            this.addEventListenerSafe(input, 'change', (e) => {
                const target = e.target as HTMLInputElement;
                const layoutType = target.value;
                
                // '1명 한 줄로 배치' 선택 시 4단계 비활성화 및 분단 개수 제한
                if (layoutType === 'single-uniform') {
                    this.toggleSingleSubmenu(true);
                    this.updatePartitionLimitForSingleUniform();
                } else {
                    this.toggleSingleSubmenu(false);
                }
                
                // '2명씩 짝꿍 배치' 선택 시 서브 메뉴 표시 및 분단 개수 제한
                if (layoutType === 'pair-uniform') {
                    this.togglePairSubmenu(true);
                    this.updatePartitionLimitForPair();
                    // '이전 짝 금지' 체크박스 활성화
                    this.toggleAvoidPrevPartnerOption(true);
                } else {
                    this.togglePairSubmenu(false);
                    // '1명씩 한 줄로 배치' 또는 '모둠 배치' 선택 시 '이전 짝 금지' 비활성화
                    if (layoutType === 'single-uniform' || layoutType === 'group') {
                        this.toggleAvoidPrevPartnerOption(false);
                    }
                }
                
                // '모둠 배치' 선택 시 서브 메뉴 표시 및 분단 개수 제한
                if (layoutType === 'group') {
                    this.toggleGroupSubmenu(true);
                    this.toggleGroupGenderMixOption(true);
                    // 모둠 배치가 선택되면 현재 선택된 group-size에 따라 분단 개수 제한 적용
                    const selectedGroupSize = document.querySelector('input[name="group-size"]:checked') as HTMLInputElement;
                    if (selectedGroupSize) {
                        this.updatePartitionLimitForGroup(selectedGroupSize.value);
                    } else {
                        // 아직 선택되지 않았으면 제한 해제
                        this.resetPartitionLimit();
                    }
                } else {
                    this.toggleGroupSubmenu(false);
                    this.toggleGroupGenderMixOption(false);
                    // 모둠 배치가 아니고 다른 배치 형태도 아니면 분단 개수 제한 해제
                    if (layoutType !== 'single-uniform' && layoutType !== 'pair-uniform') {
                        this.resetPartitionLimit();
                    }
                }
                
                // 사용자 구성 배치는 책상을 직접 놓으므로 분단 개수가 의미가 없다
                this.togglePartitionSection(layoutType !== 'custom');
                
                // 배치 형태 변경 시 미리보기 업데이트
                this.updateCustomLayoutHint();
                this.updatePreviewForGenderCounts();
            });
        });

        // 책상 위치 초기화 버튼 (사용자 구성 배치)
        const resetCustomBtn = document.getElementById('custom-layout-reset');
        if (resetCustomBtn) {
            this.addEventListenerSafe(resetCustomBtn, 'click', () => this.handleResetCustomLayout());
        }

        // 1명씩 한 줄로 배치 모드 라디오 버튼 변경 이벤트
        const singleModeInputs = document.querySelectorAll('input[name="single-mode"]');
        singleModeInputs.forEach(input => {
            this.addEventListenerSafe(input, 'change', (e) => {
                // 읽기 전용 모드에서는 옵션 변경 이벤트 무시
                if (this.isReadOnlyMode) {
                    e.stopPropagation();
                    e.preventDefault();
                    return;
                }
                // 배치 형태 변경 시 미리보기 업데이트
                this.updatePreviewForGenderCounts();
            });
        });
        
        // '남녀 순서 바꾸기' 체크박스 이벤트 리스너
        const reverseGenderOrderCheckbox = document.getElementById('reverse-gender-order');
        if (reverseGenderOrderCheckbox) {
            this.addEventListenerSafe(reverseGenderOrderCheckbox, 'change', (e) => {
                // 읽기 전용 모드에서는 옵션 변경 이벤트 무시
                if (this.isReadOnlyMode) {
                    e.stopPropagation();
                    e.preventDefault();
                    return;
                }
                // 체크박스 변경 시 미리보기 업데이트
                this.updatePreviewForGenderCounts();
            });
        }

        // 모둠 크기 라디오 버튼 변경 이벤트
        const groupSizeInputs = document.querySelectorAll('input[name="group-size"]');
        groupSizeInputs.forEach(input => {
            this.addEventListenerSafe(input, 'change', (e) => {
                // 읽기 전용 모드에서는 옵션 변경 이벤트 무시
                if (this.isReadOnlyMode) {
                    e.stopPropagation();
                    e.preventDefault();
                    return;
                }
                const target = e.target as HTMLInputElement;
                const groupSize = target.value;
                
                // 분단 개수 제한 적용
                this.updatePartitionLimitForGroup(groupSize);
                // 미리보기 업데이트
                this.updatePreviewForGenderCounts();
            });
        });
        
        // 짝꿍 모드 라디오 버튼 변경 이벤트
        const pairModeInputs = document.querySelectorAll('input[name="pair-mode"]');
        pairModeInputs.forEach(input => {
            this.addEventListenerSafe(input, 'change', (e) => {
                // 읽기 전용 모드에서는 옵션 변경 이벤트 무시
                if (this.isReadOnlyMode) {
                    e.stopPropagation();
                    e.preventDefault();
                    return;
                }
                // 짝꿍 모드 변경됨
                // 분단 개수 제한 적용 (짝꿍 배치 선택 시)
                const layoutTypeInput = document.querySelector('input[name="layout-type"]:checked') as HTMLInputElement;
                if (layoutTypeInput && layoutTypeInput.value === 'pair-uniform') {
                    this.updatePartitionLimitForPair();
                }
                // 현재 학생 수 가져오기
                this.updatePreviewForGenderCounts();
            });
        });
        
        // 모둠 배치 남녀 섞기 체크박스 변경 이벤트
        const genderMixCheckbox = document.getElementById('group-gender-mix');
        if (genderMixCheckbox) {
            this.addEventListenerSafe(genderMixCheckbox, 'change', () => {
                // 남녀 섞기 옵션 변경됨
                // 미리보기 업데이트
                this.updatePreviewForGenderCounts();
            });
        }

        // 인원수 설정 이벤트
        this.addEventListenerSafe(document, 'studentCountSet', (e: Event) => {
            const customEvent = e as CustomEvent;
            const count = customEvent.detail.count;
            this.studentTableManager.createStudentTable(count);
            // 미리보기 업데이트
            this.updatePreviewForStudentCount(count);
        });

        // 남학생 수 입력 필드 이벤트
        const maleCountInput = document.getElementById('male-students') as HTMLInputElement;
        if (maleCountInput) {
            // 숫자만 입력되도록 제한
            this.addEventListenerSafe(maleCountInput, 'input', (e: Event) => {
                const input = e.target as HTMLInputElement;
                // 숫자가 아닌 문자 제거
                const cleanedValue = input.value.replace(/[^0-9]/g, '');
                if (input.value !== cleanedValue) {
                    input.value = cleanedValue;
                }
                this.updatePreviewForGenderCounts();
                this.updateStudentTableStats(); // 통계 업데이트
            });
            
            this.addEventListenerSafe(maleCountInput, 'keydown', (e: Event) => {
                const ke = e as KeyboardEvent;
                if (ke.key === 'Enter') {
                    this.updatePreviewForGenderCounts();
                }
                // 숫자, 백스페이스, 삭제, 화살표 키 등만 허용
                if (!/[0-9]/.test(ke.key) && 
                    !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'].includes(ke.key) &&
                    !(ke.ctrlKey || ke.metaKey || ke.altKey)) {
                    ke.preventDefault();
                }
            });

            this.addEventListenerSafe(maleCountInput, 'change', () => {
                this.validateAndFixStudentInput(maleCountInput, 'male');
                this.updatePreviewForGenderCounts();
            });
        }

        // 좌석 카드 드래그&드롭(스왑) 활성화
        this.enableSeatSwapDragAndDrop();

        // 옵션 설정 저장 버튼
        const saveOptionsBtn = document.getElementById('save-options');
        if (saveOptionsBtn) {
            this.addEventListenerSafe(saveOptionsBtn, 'click', () => {
                this.storageManager.saveOptions();
            });
        }

        // 초기화 버튼 (확인 대화상자 추가)
        const resetBtn = document.getElementById('reset-app');
        if (resetBtn) {
            this.addEventListenerSafe(resetBtn, 'click', () => {
                if (confirm('모든 설정 및 데이터가 초기화됩니다. 계속하시겠습니까?')) {
                    this.resetApp();
                }
            });
        }

        // 여학생 수 입력 필드 이벤트
        const femaleCountInput = document.getElementById('female-students') as HTMLInputElement;
        if (femaleCountInput) {
            // 숫자만 입력되도록 제한
            this.addEventListenerSafe(femaleCountInput, 'input', (e: Event) => {
                const input = e.target as HTMLInputElement;
                // 숫자가 아닌 문자 제거
                const cleanedValue = input.value.replace(/[^0-9]/g, '');
                if (input.value !== cleanedValue) {
                    input.value = cleanedValue;
                }
                this.updatePreviewForGenderCounts();
                this.updateStudentTableStats(); // 통계 업데이트
            });
            
            this.addEventListenerSafe(femaleCountInput, 'keydown', (e: Event) => {
                const ke = e as KeyboardEvent;
                // 숫자, 백스페이스, 삭제, 화살표 키 등만 허용
                if (!/[0-9]/.test(ke.key) && 
                    !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'].includes(ke.key) &&
                    !(ke.ctrlKey || ke.metaKey || ke.altKey)) {
                    ke.preventDefault();
                }
            });
            
            this.addEventListenerSafe(femaleCountInput, 'change', () => {
                this.validateAndFixStudentInput(femaleCountInput, 'female');
                this.updatePreviewForGenderCounts();
            });
        }

        // 학생 정보 입력 테이블 생성 버튼
        const createTableBtn = document.getElementById('create-student-table');
        if (createTableBtn) {
            this.addEventListenerSafe(createTableBtn, 'click', () => {
                this.studentTableManager.createStudentTable();
            });
        }
        
        // 분단 수 입력 필드에 엔터 키 이벤트 추가
        const partitionInput = document.getElementById('number-of-partitions') as HTMLInputElement;
        if (partitionInput) {
            this.addEventListenerSafe(partitionInput, 'keydown', (e: Event) => {
                const ke = e as KeyboardEvent;
                if (ke.key === 'Enter') {
                    // 분단 수가 입력되면 자동으로 저장되도록 (현재는 change 이벤트만 사용)
                    partitionInput.blur(); // 포커스 제거
                }
            });
            
            // 남학생/여학생 수 입력 필드에 검증 이벤트 추가 (중복 제거 - 이미 위에서 처리됨)
            
            // 분단 수 변경 시 미리보기 업데이트
            this.addEventListenerSafe(partitionInput, 'change', (e) => {
                // 읽기 전용 모드에서는 옵션 변경 이벤트 무시
                if (this.isReadOnlyMode) {
                    e.stopPropagation();
                    e.preventDefault();
                    return;
                }
                // 입력 검증
                this.validateAndFixPartitionInput(partitionInput);
                // 현재 학생 수 가져오기
                this.updatePreviewForGenderCounts();
            });
            this.addEventListenerSafe(partitionInput, 'input', () => {
                // 실시간 업데이트
                this.updatePreviewForGenderCounts();
            });
        }




        // 양식 파일 다운로드 버튼
        const downloadTemplateBtn = document.getElementById('download-template');
        if (downloadTemplateBtn) {
            this.addEventListenerSafe(downloadTemplateBtn, 'click', () => this.csvFileHandler.downloadTemplateFile());
        }

        // 엑셀 파일 업로드 버튼 (눌러서 파일 선택 트리거)
        const uploadFileBtn = document.getElementById('upload-file');
        if (uploadFileBtn) {
            this.addEventListenerSafe(uploadFileBtn, 'click', () => {
                const fileInput = document.getElementById('upload-file-input') as HTMLInputElement;
                if (fileInput) {
                    fileInput.click();
                }
            });
        }

        // 명단·배치 내보내기 버튼
        const exportBackupBtn = document.getElementById('backup-export-btn');
        if (exportBackupBtn) {
            this.addEventListenerSafe(exportBackupBtn, 'click', () => this.handleExportBackup());
        }

        // 명단·배치 불러오기 버튼 (숨겨진 파일 입력을 대신 연다)
        const importBackupBtn = document.getElementById('backup-import-btn');
        const importBackupInput = document.getElementById('backup-import-input') as HTMLInputElement | null;
        if (importBackupBtn && importBackupInput) {
            this.addEventListenerSafe(importBackupBtn, 'click', () => importBackupInput.click());
            this.addEventListenerSafe(importBackupInput, 'change', (e) => this.handleImportBackup(e));
        }

        // 엑셀 파일 업로드 입력 필드
        const uploadFileInput = document.getElementById('upload-file-input');
        if (uploadFileInput) {
            this.addEventListenerSafe(uploadFileInput, 'change', (e) => this.csvFileHandler.handleFileUpload(e));
        }

        // 라디오 버튼 이벤트 리스너
        this.initializeRadioListeners();
        
        // 이벤트 위임을 사용하여 동적으로 생성되는 버튼들 처리
        this.addEventListenerSafe(document, 'click', (e) => {
            const target = e.target as HTMLElement;
            
            // 자리 배치하기 버튼 클릭 (버튼 내부 텍스트 클릭도 처리)
            const arrangeBtn = target.id === 'arrange-seats' ? target : target.closest('#arrange-seats');
            if (arrangeBtn) {
                e.preventDefault();
                this.handleArrangeSeats();
                return;
            }
            
            // 자리 확정 버튼 클릭 (버튼 내부 텍스트 클릭도 처리)
            const confirmBtn = target.id === 'confirm-seats' ? target : target.closest('#confirm-seats');
            if (confirmBtn) {
                e.preventDefault();
                this.handleConfirmSeats().catch((error) => {
                    logger.error('자리 확정 처리 실패:', error);
                    this.outputModule.showError('자리 확정 중 오류가 발생했습니다.');
                });
                return;
            }
            
            // 확정된 자리 이력 드롭다운 버튼 클릭
            const dropdown = document.getElementById('history-dropdown-content');
            const dropdownContainer = document.getElementById('history-dropdown');
            
            if (target.id === 'history-dropdown-btn' || target.closest('#history-dropdown-btn')) {
                // 드롭다운 버튼 클릭 시 현재 반의 이력으로 업데이트 후 토글
                this.updateHistoryDropdown();
                if (dropdown) {
                    const isOpening = dropdown.style.display !== 'block';
                    dropdown.style.display = isOpening ? 'block' : 'none';
                }
            } else if (dropdown && dropdownContainer) {
                // 드롭다운이 열려있고, 클릭된 요소가 드롭다운 내부가 아니면 닫기
                if (dropdown.style.display === 'block' && !dropdownContainer.contains(target)) {
                    dropdown.style.display = 'none';
                }
            }
            
            // 이력 항목 삭제 버튼 클릭 (이력 항목 클릭보다 먼저 처리)
            if (target.classList.contains('history-delete-btn')) {
                e.stopPropagation();
                const deleteId = target.dataset.historyId;
                if (deleteId) {
                    this.deleteHistoryItem(deleteId);
                }
                return;
            }
            
            // 이력 항목 클릭
            if (target.classList.contains('history-item')) {
                const historyId = target.dataset.historyId;
                if (historyId) {
                    this.loadHistoryItem(historyId);
                }
            }
            
            // 행 추가 버튼 클릭
            if (target.id === 'add-student-row-btn') {
                this.studentTableManager.addStudentRow();
            }
            
            // 저장 버튼 클릭
            if (target.id === 'save-student-table-btn') {
                this.handleSaveStudentTable();
            }
            
            // 인쇄하기 버튼 클릭
            if (target.id === 'print-layout') {
                this.printExportManager.printLayout();
            }
            
            // 교탁용 인쇄하기 버튼 클릭
            if (target.id === 'print-layout-teacher') {
                this.printExportManager.printLayoutForTeacher();
            }
            
            // 되돌리기 버튼 클릭
            if (target.id === 'undo-layout') {
                this.handleUndoLayout();
            }
            
            // 다시 실행하기 버튼 클릭
            if (target.id === 'redo-layout') {
                this.handleRedoLayout();
            }
            
            // 저장하기 버튼 클릭 (제거됨 - 자리 확정하기에서 통합)
            if (target.id === 'save-layout') {
                this.handleSaveLayout();
            }
            
            // 사용설명서 버튼 클릭
            if (target.id === 'user-manual-btn') {
                this.showUserManual();
            }
            
            // 반 추가 버튼 클릭
            if (target.id === 'add-class-btn') {
                this.handleAddClass();
            }
            
            // 반 삭제 버튼 클릭
            if (target.id === 'delete-class-btn') {
                this.handleDeleteClass();
            }
            
            // 자리 배치도 저장 버튼 클릭 (제거됨 - 자리 확정하기에서 통합)
            
            // 사이드바 토글 버튼 클릭
            if (target.id === 'sidebar-toggle-btn' || target.closest('#sidebar-toggle-btn')) {
                this.toggleSidebar();
            }
        });
        
        // 키보드 단축키: Ctrl+Z / Cmd+Z (되돌리기), Ctrl+Y / Cmd+Y (다시 실행하기)
        this.addEventListenerSafe(document, 'keydown', (e) => {
            const ke = e as KeyboardEvent;
            
            // 입력 필드에 포커스가 있으면 기본 동작 허용 (텍스트 입력 되돌리기/다시 실행하기)
                const activeElement = document.activeElement as HTMLElement;
            const isInputFocused = activeElement && (
                    activeElement.tagName === 'INPUT' || 
                    activeElement.tagName === 'TEXTAREA' ||
                    (activeElement.isContentEditable === true)
            );
            
            // Ctrl+Z / Cmd+Z (되돌리기)
            if ((ke.ctrlKey || ke.metaKey) && ke.key === 'z' && !ke.shiftKey) {
                if (isInputFocused) {
                    return; // 기본 동작 허용
                }
                
                e.preventDefault();
                this.handleUndoLayout();
            }
            
            // Ctrl+Y / Cmd+Y (다시 실행하기)
            if ((ke.ctrlKey || ke.metaKey) && ke.key === 'y' && !ke.shiftKey) {
                if (isInputFocused) {
                    return; // 기본 동작 허용
                }
                
                e.preventDefault();
                this.handleRedoLayout();
            }
            
            // Ctrl+Shift+Z / Cmd+Shift+Z (다시 실행하기 - 대체 단축키)
            if ((ke.ctrlKey || ke.metaKey) && ke.key === 'z' && ke.shiftKey) {
                if (isInputFocused) {
                    return; // 기본 동작 허용
                }
                
                e.preventDefault();
                this.handleRedoLayout();
            }
        });
    }

    /**
     * 라디오 버튼 이벤트 리스너 초기화
     */
    private initializeRadioListeners(): void {
        // 배치 유형 라디오 버튼
        const layoutRadios = document.querySelectorAll('input[name="layout-type"]');
        // layout-type 변경 이벤트는 initializeEventListeners에서 처리하므로 여기서는 제거

        // 고정 좌석 모드 라디오 버튼
        const customModeRadios = document.querySelectorAll('input[name="custom-mode-2"]');
        customModeRadios.forEach(radio => {
            this.addEventListenerSafe(radio, 'change', (e) => {
                const target = e.target as HTMLInputElement;
                if (target.value === 'fixed-random') {
                    // 고정 좌석 지정 후 랜덤 배치 모드 활성화
                    this.enableFixedSeatMode();
                } else {
                    // 일반 랜덤 배치 모드
                    this.disableFixedSeatMode();
                }
            });
        });
    }

    /**
     * 고정 좌석 모드 활성화
     * 기존 좌석 카드들에 클릭 이벤트를 다시 설정
     */
    private enableFixedSeatMode(): void {
        
        
        // 고정 좌석 모드 도움말 표시
        const fixedSeatHelp = document.getElementById('fixed-seat-help');
        if (fixedSeatHelp) {
            fixedSeatHelp.style.display = 'block';
        }
        
        // 좌석 카드에 클릭 이벤트 추가 (이벤트 위임)
        const seatsArea = document.getElementById('seats-area');
        if (seatsArea) {
            seatsArea.style.cursor = 'pointer';
            this.addEventListenerSafe(seatsArea, 'click', this.handleSeatCardClick as (e: Event) => void);
            
            // 기존 좌석 카드들에 스타일 및 시각적 표시 업데이트
            const cards = seatsArea.querySelectorAll('.student-seat-card');
            cards.forEach((card) => {
                const cardElement = card as HTMLElement;
                const seatIdStr = cardElement.getAttribute('data-seat-id');
                if (seatIdStr) {
                    const seatId = parseInt(seatIdStr, 10);
                    cardElement.style.cursor = 'pointer';
                    
                    // 이미 고정된 좌석인 경우 시각적 표시
                    if (this.fixedSeatIds.has(seatId)) {
                        cardElement.classList.add('fixed-seat');
                        cardElement.title = '고정 좌석 (클릭하여 해제)';
                        
                        if (!cardElement.querySelector('.fixed-seat-lock')) {
                            const lockIcon = document.createElement('div');
                            lockIcon.className = 'fixed-seat-lock';
                            lockIcon.textContent = '🔒';
                            lockIcon.style.cssText = 'position: absolute; top: 5px; right: 5px; font-size: 1.2em; z-index: 10; pointer-events: none;';
                            cardElement.appendChild(lockIcon);
                        }
                    } else {
                        cardElement.title = '클릭하여 고정 좌석 지정/해제';
                    }
                }
            });
        }
    }

    /**
     * 고정 좌석 모드 비활성화
     */
    private disableFixedSeatMode(): void {
        
        
        // 고정 좌석 모드 도움말 숨김
        const fixedSeatHelp = document.getElementById('fixed-seat-help');
        if (fixedSeatHelp) {
            fixedSeatHelp.style.display = 'none';
        }
        
        // 고정 좌석 초기화
        this.fixedSeatIds.clear();
        
        // 모든 좌석 카드에서 고정 표시 제거
        const fixedSeats = document.querySelectorAll('.student-seat-card.fixed-seat');
        fixedSeats.forEach(seat => {
            seat.classList.remove('fixed-seat');
            const lockIcon = seat.querySelector('.fixed-seat-lock');
            if (lockIcon) {
                lockIcon.remove();
            }
        });

        const seatsArea = document.getElementById('seats-area');
        if (seatsArea) {
            seatsArea.style.cursor = 'default';
            seatsArea.removeEventListener('click', this.handleSeatCardClick);
        }
    }

    /**
     * 좌석 카드 클릭 이벤트 핸들러
     */
    private handleSeatCardClick = (e: MouseEvent): void => {
        // 드래그가 발생했으면 클릭 이벤트 무시
        if (this.dragSourceCard) {
            return;
        }
        
        const target = e.target as HTMLElement;
        const card = target.closest('.student-seat-card') as HTMLElement;
        
        if (!card) return;

        // 고정 좌석 모드가 활성화되어 있는지 확인
        const fixedRandomMode = document.querySelector('input[name="custom-mode-2"][value="fixed-random"]:checked') as HTMLInputElement;
        if (!fixedRandomMode) return;

        const seatIdStr = card.getAttribute('data-seat-id');
        if (!seatIdStr) return;

        const seatId = parseInt(seatIdStr, 10);
        
        // 이벤트 전파 중단 (다른 이벤트 핸들러와의 충돌 방지)
        e.stopPropagation();
        e.preventDefault();
        
        // 고정 좌석 토글
        if (this.fixedSeatIds.has(seatId)) {
            // 고정 해제
            this.fixedSeatIds.delete(seatId);
            card.classList.remove('fixed-seat');
            card.title = '클릭하여 고정 좌석 지정';
            
            const lockIcon = card.querySelector('.fixed-seat-lock');
            if (lockIcon) {
                lockIcon.remove();
            }
            
        } else {
            // 고정 설정
            this.fixedSeatIds.add(seatId);
            card.classList.add('fixed-seat');
            card.title = '고정 좌석 (클릭하여 해제)';
            
            // 🔒 아이콘 추가 (없는 경우만)
            if (!card.querySelector('.fixed-seat-lock')) {
                const lockIcon = document.createElement('div');
                lockIcon.className = 'fixed-seat-lock';
                lockIcon.textContent = '🔒';
                lockIcon.style.cssText = 'position: absolute; top: 5px; right: 5px; font-size: 1.2em; z-index: 10; pointer-events: none;';
                card.appendChild(lockIcon);
            }
            
            
        }

        // 테이블의 고정 좌석 드롭다운 업데이트
        this.studentTableManager.updateFixedSeatDropdowns();
    }

    /**
     * 최종 자리 배치도 렌더링
     */
    private renderFinalLayout(): void {
        // LayoutRenderer로 위임
        this.layoutRenderer.renderFinalLayout(this.seats);
    }

    /**
     * 초기 예시 레이아웃 렌더링
     */
    private renderInitialExampleLayout(): void {
        
        
        // 카드 컨테이너 표시
        const cardContainer = document.getElementById('card-layout-container');
        if (!cardContainer) {
            ErrorHandler.logOnly(
                new Error('카드 컨테이너를 찾을 수 없습니다.'),
                ErrorCode.LAYOUT_NOT_FOUND,
                { method: 'renderInitialExampleLayout' }
            );
            return;
        }
        
        cardContainer.style.display = 'block';
        
        // 학생 및 좌석 배열 초기화
        this.students = [];
        this.seats = [];
        
        // 좌석 번호를 1부터 시작하도록 초기화
        this.nextSeatId = 1;
        
        // 예시 좌석 생성 (24개)
        const exampleSeats: Seat[] = [];
        for (let i = 0; i < 24; i++) {
            const student = StudentModel.create(
                `학생${i + 1}`,
                (i % 2 === 0) ? 'M' : 'F'
            );
            this.students.push(student);
            
            // 좌석 생성 (더미)
            const seat = {
                id: i + 1,
                position: { x: 0, y: 0 },
                isActive: true,
                isFixed: false,
                studentId: student.id,
                studentName: student.name
            } as Seat;
            exampleSeats.push(seat);
        }
        
        this.seats = exampleSeats;
        
        // 예시 카드 렌더링
        this.renderExampleCards();
    }

    /**
     * 사용자 구성 배치를 선택했을 때만 안내를 보여준다.
     */
    private updateCustomLayoutHint(): void {
        const hint = document.getElementById('custom-layout-hint');
        if (!hint) return;

        const layoutType = (document.querySelector('input[name="layout-type"]:checked') as HTMLInputElement | null)?.value;
        hint.style.display = layoutType === 'custom' ? 'block' : 'none';
    }

    /**
     * 책상 위치를 기본 격자로 되돌린다.
     */
    private handleResetCustomLayout(): void {
        const key = this.customPositionsKey();
        if (key) {
            try {
                localStorage.removeItem(key);
            } catch (error) {
                logger.error('책상 위치 초기화 실패:', error);
            }
        }

        this.pendingCustomPositions = null;
        this.customPositions = null;
        this.updatePreviewForGenderCounts();
        this.outputModule.showInfo('책상 위치를 기본 배치로 되돌렸습니다.');
    }

    /**
     * 사용자 구성 배치: 책상을 좌표에 놓고 끌어 옮길 수 있게 한다.
     *
     * 다른 배치 형태는 정해진 틀에 카드를 채우지만, 이 배치는 교사가
     * 책상을 직접 옮겨 ㄷ자·원형 같은 임의의 교실 모양을 만든다.
     */
    private renderCustomLayoutCards(seatsArea: HTMLElement): void {
        const areaWidth = Math.max(seatsArea.clientWidth, MainController.CARD_AREA_MIN_WIDTH_FALLBACK);

        // 이력 복원 → 화면에 있던 배치 → 저장해 둔 배치 → 기본 격자 순으로 사용한다
        const stored = this.pendingCustomPositions ?? this.customPositions ?? this.loadCustomPositions();
        this.pendingCustomPositions = null;

        const positions = stored
            ? CustomLayoutService.fitToCount(stored, this.students.length, areaWidth)
            : CustomLayoutService.defaultPositions(this.students.length, areaWidth);

        // 다음 재렌더에서도 같은 배치를 유지한다
        this.customPositions = positions;

        this.students.forEach((student, index) => {
            const card = this.createStudentCard(student, index);
            seatsArea.appendChild(card);
        });

        this.customLayoutManager.applyLayout(positions);

        // 자리를 배정하기 전에는 카드를 끌면 책상이 움직인다.
        // 배정한 뒤에는 기존과 같이 학생이 서로 바뀐다.
        this.customLayoutManager.setDeskEditing(true);

        this.enableSeatSwapDragAndDrop();
        this.setTimeoutSafe(() => this.saveLayoutToHistory(), 100);
    }

    /**
     * 저장해 둔 책상 위치를 읽는다. (반별로 따로 보관한다)
     */
    private loadCustomPositions(): SeatPosition[] | null {
        const key = this.customPositionsKey();
        if (!key) return null;

        const raw = this.storageManager.safeGetItem(key);
        if (!raw) return null;

        try {
            return CustomLayoutService.parsePositions(JSON.parse(raw));
        } catch (error) {
            logger.error('책상 위치 불러오기 실패:', error);
            return null;
        }
    }

    /**
     * 책상 위치를 저장한다. 반이 선택되지 않았으면 저장하지 않는다.
     */
    private saveCustomPositions(positions: SeatPosition[]): void {
        const key = this.customPositionsKey();
        if (!key) return;

        this.storageManager.safeSetItem(key, JSON.stringify(positions));
    }

    private customPositionsKey(): string | null {
        const classId = this.classManager?.getCurrentClassId();
        return classId ? `customLayout_${classId}` : null;
    }

    /**
     * 예시 카드 렌더링
     */
    private renderExampleCards(): void {
        const seatsArea = document.getElementById('seats-area');
        if (!seatsArea) return;

        // 기존 카드 제거
        seatsArea.innerHTML = '';

        // 좌석 번호를 1부터 시작하도록 초기화
        this.nextSeatId = 1;
        
        // 대용량 데이터 처리: 학생 수가 많으면 로딩 표시
        const maleCount = parseInt((document.getElementById('male-students') as HTMLInputElement)?.value || '0', 10);
        const femaleCount = parseInt((document.getElementById('female-students') as HTMLInputElement)?.value || '0', 10);
        const totalCount = maleCount + femaleCount;
        
        // 대용량 데이터 처리: DocumentFragment 사용 및 배치 렌더링
        const useBatchRendering = totalCount > 100;
        if (useBatchRendering) {
            this.outputModule.showInfo('대량의 좌석을 렌더링하는 중입니다. 잠시만 기다려주세요...');
        }
        
        // DocumentFragment를 사용하여 DOM 조작 최소화
        const fragment = useBatchRendering ? document.createDocumentFragment() : null;

        // 선택된 배치 형태 확인
        const layoutTypeInput = document.querySelector('input[name="layout-type"]:checked') as HTMLInputElement;
        const layoutType = layoutTypeInput?.value;
        const groupSizeInput = document.querySelector('input[name="group-size"]:checked') as HTMLInputElement;
        const groupSize = groupSizeInput ? groupSizeInput.value : '';
        
        // 분단 수 가져오기
        const partitionInput = document.getElementById('number-of-partitions') as HTMLInputElement;
        const partitionCount = partitionInput ? parseInt(partitionInput.value || '1', 10) : 1;
        
        // 사용자 구성 배치인 경우: 카드를 좌표로 놓고 책상을 끌어 옮길 수 있게 한다
        if (layoutType === 'custom') {
            this.renderCustomLayoutCards(seatsArea);
            return;
        }

        // 다른 배치 형태로 돌아왔다면 좌표 기반 설정을 걷어낸다
        this.customLayoutManager?.clearLayout();

        // 모둠 배치인 경우
        
        if (layoutType === 'group' && (groupSize === 'group-3' || groupSize === 'group-4' || groupSize === 'group-5' || groupSize === 'group-6')) {
            
            const groupSizeNumber = groupSize === 'group-3' ? 3 : groupSize === 'group-4' ? 4 : groupSize === 'group-5' ? 5 : 6;
            // 예시 학생 데이터 생성 (this.students가 비어있을 경우)
            if (this.students.length === 0) {
                const maleCount = parseInt((document.getElementById('male-students') as HTMLInputElement)?.value || '0', 10);
                const femaleCount = parseInt((document.getElementById('female-students') as HTMLInputElement)?.value || '0', 10);
                const totalCount = maleCount + femaleCount;
                
                
                
                // 임시 학생 데이터 생성
                const tempStudents: Student[] = [];
                for (let i = 0; i < totalCount; i++) {
                    const gender = i < maleCount ? 'M' : 'F';
                    tempStudents.push({
                        id: i + 1,
                        name: gender === 'M' ? `남학생${i + 1}` : `여학생${i - maleCount + 1}`,
                        gender: gender as 'M' | 'F'
                    });
                }
                this.students = tempStudents;
                
            }
            
            // 모둠 배치로 렌더링 (LayoutRenderer를 통해 처리)
            const dummySeats: Seat[] = this.students.map((_, index) => ({
                id: index + 1,
                position: { x: 0, y: 0 },
                studentId: undefined,
                studentName: undefined,
                isFixed: false,
                isActive: true
            }));
            
            // LayoutRenderer를 통해 모둠 배치 렌더링
            this.layoutRenderer.renderFinalLayout(dummySeats);
            return;
        }
        
        // 2명씩 짝꿍 배치인 경우
        if (layoutType === 'pair-uniform') {
            // seatsArea의 그리드 설정 먼저
            seatsArea.style.gridTemplateColumns = `repeat(${partitionCount}, 1fr)`;
            seatsArea.style.gap = '10px 40px';
            
            // 분단 레이블을 각 컬럼에 직접 추가 (중첩 그리드 구조 제거)
            for (let i = 1; i <= partitionCount; i++) {
                const label = document.createElement('div');
                label.textContent = `${i}분단`;
                label.style.textAlign = 'center';
                label.style.fontWeight = 'bold';
                label.style.color = '#667eea';
                label.style.fontSize = '0.9em';
                label.style.marginBottom = '5px';
                // 각 레이블이 해당 분단 컬럼에 직접 배치되도록 grid-column 지정 안함 (자동으로 배치됨)
                seatsArea.appendChild(label);
            }
            
            // 선택된 짝꿍 모드 확인
            const pairModeInput = document.querySelector('input[name="pair-mode"]:checked') as HTMLInputElement;
            const pairMode = pairModeInput?.value || 'gender-pair'; // 기본값: 남녀 짝꿍
            
            
            if (pairMode === 'same-gender-pair') {
                // 같은 성끼리 짝꿍하기: 각 행에서 분단을 넘나들며 같은 성별끼리 짝꿍
                // 성별별로 학생 분류
                const maleStudents = this.students.filter(s => s.gender === 'M');
                const femaleStudents = this.students.filter(s => s.gender === 'F');
                
                const studentsPerPartition = Math.ceil(this.students.length / partitionCount);
                const rowsPerPartition = Math.ceil(studentsPerPartition / 2);
                
                let maleIndex = 0;
                let femaleIndex = 0;
                
                // 가로로 배치 (각 행을 분단별로 채움)
                for (let row = 0; row < rowsPerPartition; row++) {
                    for (let partition = 0; partition < partitionCount; partition++) {
                        const pairContainer = document.createElement('div');
                        pairContainer.style.display = 'flex';
                        pairContainer.style.gap = '0px';
                        pairContainer.style.width = '100%';
                        pairContainer.style.justifyContent = 'center';
                        
                        // 각 행마다 남녀가 번갈아 오도록 배치한다.
                        // 첫 번째 행: 남남 -> 여여 -> 남남
                        // 두 번째 행: 여여 -> 남남 -> 여여  
                        // 다만 한쪽 성별이 먼저 소진되면 남은 성별로 채운다.
                        // 패턴만 따르면 남는 학생의 자리가 아예 만들어지지 않는다.
                        const preferMale = (row + partition) % 2 === 0;
                        const malesLeft = maleIndex < maleStudents.length;
                        const femalesLeft = femaleIndex < femaleStudents.length;
                        if (!malesLeft && !femalesLeft) {
                            break; // 남은 학생이 없으면 빈 컨테이너를 만들지 않는다
                        }
                        const shouldBeMale = preferMale ? malesLeft : !femalesLeft;
                        
                        if (shouldBeMale) {
                            // 남학생 짝꿍
                            if (maleIndex < maleStudents.length) {
                                const card1 = this.createStudentCard(maleStudents[maleIndex], this.students.indexOf(maleStudents[maleIndex]));
                                pairContainer.appendChild(card1);
                                maleIndex++;
                                
                                if (maleIndex < maleStudents.length) {
                                    const card2 = this.createStudentCard(maleStudents[maleIndex], this.students.indexOf(maleStudents[maleIndex]));
                                    pairContainer.appendChild(card2);
                                    maleIndex++;
                                }
                            }
                        } else {
                            // 여학생 짝꿍
                            if (femaleIndex < femaleStudents.length) {
                                const card1 = this.createStudentCard(femaleStudents[femaleIndex], this.students.indexOf(femaleStudents[femaleIndex]));
                                pairContainer.appendChild(card1);
                                femaleIndex++;
                                
                                if (femaleIndex < femaleStudents.length) {
                                    const card2 = this.createStudentCard(femaleStudents[femaleIndex], this.students.indexOf(femaleStudents[femaleIndex]));
                                    pairContainer.appendChild(card2);
                                    femaleIndex++;
                                }
                            }
                        }
                        
                        seatsArea.appendChild(pairContainer);
                    }
                }
            } else {
                // 남녀 짝꿍 하기인 경우 - 명확하게 남녀 교대로 짝꿍 + 남은 남자 처리
                const maleStudents = this.students.filter(s => s.gender === 'M');
                const femaleStudents = this.students.filter(s => s.gender === 'F');
                
                // 1~2단계: 남녀 짝꿍을 만들고 남는 학생도 모두 자리를 받도록 묶는다.
                // (규칙은 PairingService.buildPairs에 있고 단위 테스트로 고정되어 있다)
                const pairs = PairingService.buildPairs(maleStudents, femaleStudents);
                
                // 3단계: 전체 짝꿍을 분단별로 배치
                const rowsPerPartition = Math.ceil(pairs.length / partitionCount);
                
                let pairIndex = 0;
                for (let row = 0; row < rowsPerPartition; row++) {
                    if (pairIndex >= pairs.length) break; // 외부 루프도 종료
                    for (let partition = 0; partition < partitionCount; partition++) {
                        if (pairIndex >= pairs.length) break;
                        
                        const pair = pairs[pairIndex++];
                        const pairContainer = document.createElement('div');
                        pairContainer.style.display = 'flex';
                        pairContainer.style.gap = '0px';
                        pairContainer.style.width = '100%';
                        pairContainer.style.justifyContent = 'center';
                        
                        if (pair.first) {
                            const card1 = this.createStudentCard(pair.first, this.students.indexOf(pair.first));
                            pairContainer.appendChild(card1);
                        }
                        
                        if (pair.second) {
                            const card2 = this.createStudentCard(pair.second, this.students.indexOf(pair.second));
                            pairContainer.appendChild(card2);
                        }
                        
                        seatsArea.appendChild(pairContainer);
                    }
                }
            }
        } else {
            // '1명씩 한 줄로 배치' - 선택된 모드에 따라 배치
            // seatsArea의 그리드 설정 먼저
            seatsArea.style.gridTemplateColumns = `repeat(${partitionCount}, 1fr)`;
            seatsArea.style.gap = '5px 10px'; // 카드 간 좁은 간격 (세로 5px, 가로 10px)
            seatsArea.style.justifyItems = 'center'; // 각 분단 컬럼 내에서 중앙 정렬
            
            // 분단 레이블을 각 컬럼에 직접 추가 (중첩 그리드 구조 제거)
            for (let i = 1; i <= partitionCount; i++) {
                const label = document.createElement('div');
                label.textContent = `${i}분단`;
                label.style.textAlign = 'center';
                label.style.fontWeight = 'bold';
                label.style.color = '#667eea';
                label.style.fontSize = '0.9em';
                label.style.marginBottom = '5px';
                label.style.width = '100%'; // 레이블이 컬럼 전체 너비를 차지하도록
                // 각 레이블이 해당 분단 컬럼에 직접 배치되도록 grid-column 지정 안함 (자동으로 배치됨)
                seatsArea.appendChild(label);
            }
            
            // 선택된 배치 모드 확인
            const singleModeInput = document.querySelector('input[name="single-mode"]:checked') as HTMLInputElement;
            const singleMode = singleModeInput ? singleModeInput.value : 'basic-row';
            
            // '남녀 순서 바꾸기' 체크박스 상태 확인
            const reverseGenderOrderCheckbox = document.getElementById('reverse-gender-order') as HTMLInputElement;
            const reverseGenderOrder = reverseGenderOrderCheckbox ? reverseGenderOrderCheckbox.checked : false;
            
            // 남학생과 여학생 분리
            let maleStudents = this.students.filter(s => s.gender === 'M');
            let femaleStudents = this.students.filter(s => s.gender === 'F');
            
            // '남녀 순서 바꾸기'가 체크되면 남학생과 여학생 배열을 교환
            if (reverseGenderOrder) {
                [maleStudents, femaleStudents] = [femaleStudents, maleStudents];
            }
            
            if (singleMode === 'basic-row') {
                // '기본 1줄 배치' 모드
                // 각 행에서 분단 순서대로 남학생과 여학생을 교대로 순차적으로 배치
                // 홀수 분단(1, 3, 5, ...): 홀수 행에서 남학생, 짝수 행에서 여학생
                // 짝수 분단(2, 4, 6, ...): 홀수 행에서 여학생, 짝수 행에서 남학생
                
                // 전체 학생 수를 고려하여 필요한 행 수 계산
                const totalStudents = maleStudents.length + femaleStudents.length;
                const studentsPerRow = partitionCount; // 각 행당 분단 수만큼의 학생
                const totalRows = Math.ceil(totalStudents / studentsPerRow);
                
                let maleIndex = 0;
                let femaleIndex = 0;
                
                for (let row = 0; row < totalRows; row++) {
                    for (let partition = 0; partition < partitionCount; partition++) {
                        const partitionNumber = partition + 1; // 1-based 분단 번호
                        const isOddPartition = partitionNumber % 2 === 1; // 홀수 분단인지 확인
                        const isOddRow = row % 2 === 0; // 0-based이므로 row % 2 === 0이 홀수 행
                        
                        if (isOddPartition) {
                            // 홀수 분단: 홀수 행에서 남학생, 짝수 행에서 여학생
                            if (isOddRow) {
                                // 홀수 행: 남학생 배치
                                if (maleIndex < maleStudents.length) {
                                    const card = this.createStudentCard(maleStudents[maleIndex], this.students.indexOf(maleStudents[maleIndex]));
                                    card.style.width = '100%';
                                    card.style.maxWidth = '120px';
                                    card.style.margin = '0 auto';
                                    seatsArea.appendChild(card);
                                    maleIndex++;
                                }
                            } else {
                                // 짝수 행: 여학생 배치
                                if (femaleIndex < femaleStudents.length) {
                                    const card = this.createStudentCard(femaleStudents[femaleIndex], this.students.indexOf(femaleStudents[femaleIndex]));
                                    card.style.width = '100%';
                                    card.style.maxWidth = '120px';
                                    card.style.margin = '0 auto';
                                    seatsArea.appendChild(card);
                                    femaleIndex++;
                                }
                            }
                        } else {
                            // 짝수 분단: 홀수 행에서 여학생, 짝수 행에서 남학생
                            if (isOddRow) {
                                // 홀수 행: 여학생 배치
                                if (femaleIndex < femaleStudents.length) {
                                    const card = this.createStudentCard(femaleStudents[femaleIndex], this.students.indexOf(femaleStudents[femaleIndex]));
                                    card.style.width = '100%';
                                    card.style.maxWidth = '120px';
                                    card.style.margin = '0 auto';
                                    seatsArea.appendChild(card);
                                    femaleIndex++;
                                }
                            } else {
                                // 짝수 행: 남학생 배치
                                if (maleIndex < maleStudents.length) {
                                    const card = this.createStudentCard(maleStudents[maleIndex], this.students.indexOf(maleStudents[maleIndex]));
                                    card.style.width = '100%';
                                    card.style.maxWidth = '120px';
                                    card.style.margin = '0 auto';
                                    seatsArea.appendChild(card);
                                    maleIndex++;
                                }
                            }
                        }
                    }
                }
            } else if (singleMode === 'gender-row') {
                // '남녀 1줄 배치' 모드 - 세로(열) 방향으로 배치
                // 홀수 분단(1, 3, 5, ...): 남학생을 세로로 순차적으로 배치
                // 짝수 분단(2, 4, 6, ...): 여학생을 세로로 순차적으로 배치
                // 마지막 분단이 홀수일 경우: 남학생 먼저 세로로, 그 다음 여학생 세로로
                // 예: 남학생 12명, 여학생 12명, 5분단
                // 1분단: 남1, 남2, 남3, 남4, 남5 (세로로)
                // 2분단: 여1, 여2, 여3, 여4, 여5 (세로로)
                // 3분단: 남6, 남7, 남8, 남9, 남10 (세로로)
                // 4분단: 여6, 여7, 여8, 여9, 여10 (세로로)
                // 5분단: 남11, 남12, 여11, 여12 (세로로)
                // 
                // 그리드 레이아웃에서 세로 방향 배치를 위해서는 각 분단의 모든 학생을 먼저 배치해야 함
                // 각 분단별로 컨테이너를 만들거나, 행 단위로 배치해야 함
                
                const isLastPartitionOdd = partitionCount % 2 === 1; // 마지막 분단이 홀수인지 확인
                
                // 마지막 분단 제외한 홀수 분단 수와 짝수 분단 수 계산
                const regularPartitionCount = partitionCount - 1;
                const regularOddPartitionCount = Math.ceil(regularPartitionCount / 2); // 홀수 분단 수 (1, 3, 5, ...)
                const regularEvenPartitionCount = Math.floor(regularPartitionCount / 2); // 짝수 분단 수 (2, 4, 6, ...)
                
                // 각 분단당 배치할 학생 수 계산
                // 마지막 분단을 제외한 분단에 균등하게 배치하고, 마지막 분단에 나머지 배치
                const maleStudentsPerOddPartition = regularOddPartitionCount > 0 
                    ? Math.floor(maleStudents.length / (regularOddPartitionCount + 1)) + 1
                    : 0;
                const femaleStudentsPerEvenPartition = regularEvenPartitionCount > 0
                    ? Math.floor(femaleStudents.length / (regularEvenPartitionCount + 1)) + 1
                    : 0;
                
                // 각 분단별 학생 배열을 먼저 구성
                const partitionStudents: Array<Array<{student: Student, index: number}>> = [];
                for (let partition = 0; partition < partitionCount; partition++) {
                    partitionStudents.push([]);
                }
                
                let maleIndex = 0;
                let femaleIndex = 0;
                
                // 각 분단별로 학생을 배열에 추가
                for (let partition = 0; partition < partitionCount; partition++) {
                    const partitionNumber = partition + 1; // 1-based 분단 번호
                    const isOddPartition = partitionNumber % 2 === 1; // 홀수 분단인지 확인
                    const isLastPartition = partition === partitionCount - 1; // 마지막 분단인지 확인
                    
                    if (isLastPartition && isLastPartitionOdd) {
                        // 마지막 분단이 홀수일 경우: 남학생 먼저 세로로, 그 다음 여학생 세로로
                        // 남학생 배치 (나머지 모두 세로로)
                        while (maleIndex < maleStudents.length) {
                            partitionStudents[partition].push({
                                student: maleStudents[maleIndex],
                                index: this.students.indexOf(maleStudents[maleIndex])
                            });
                            maleIndex++;
                        }
                        // 여학생 배치 (나머지 모두 세로로)
                        while (femaleIndex < femaleStudents.length) {
                            partitionStudents[partition].push({
                                student: femaleStudents[femaleIndex],
                                index: this.students.indexOf(femaleStudents[femaleIndex])
                            });
                            femaleIndex++;
                        }
                    } else if (isLastPartition && !isLastPartitionOdd) {
                        // 마지막 분단이 짝수일 경우: 여학생 먼저 세로로, 그 다음 남학생 세로로
                        // 여학생 배치 (나머지 모두 세로로)
                        while (femaleIndex < femaleStudents.length) {
                            partitionStudents[partition].push({
                                student: femaleStudents[femaleIndex],
                                index: this.students.indexOf(femaleStudents[femaleIndex])
                            });
                            femaleIndex++;
                        }
                        // 남학생 배치 (나머지 모두 세로로)
                        while (maleIndex < maleStudents.length) {
                            partitionStudents[partition].push({
                                student: maleStudents[maleIndex],
                                index: this.students.indexOf(maleStudents[maleIndex])
                            });
                            maleIndex++;
                        }
                    } else if (isOddPartition) {
                        // 홀수 분단: 남학생을 세로로 배치
                        const currentOddPartitionIndex = Math.floor(partition / 2); // 현재 홀수 분단의 인덱스 (0, 1, 2, ...)
                        const startIndex = currentOddPartitionIndex * maleStudentsPerOddPartition;
                        const endIndex = Math.min(startIndex + maleStudentsPerOddPartition, maleStudents.length);
                        
                        for (let i = startIndex; i < endIndex; i++) {
                            partitionStudents[partition].push({
                                student: maleStudents[i],
                                index: this.students.indexOf(maleStudents[i])
                            });
                            maleIndex++;
                        }
                    } else {
                        // 짝수 분단: 여학생을 세로로 배치
                        const currentEvenPartitionIndex = Math.floor((partition - 1) / 2); // 현재 짝수 분단의 인덱스 (0, 1, 2, ...)
                        const startIndex = currentEvenPartitionIndex * femaleStudentsPerEvenPartition;
                        const endIndex = Math.min(startIndex + femaleStudentsPerEvenPartition, femaleStudents.length);
                        
                        for (let i = startIndex; i < endIndex; i++) {
                            partitionStudents[partition].push({
                                student: femaleStudents[i],
                                index: this.students.indexOf(femaleStudents[i])
                            });
                            femaleIndex++;
                        }
                    }
                }
                
                // 각 분단의 학생들을 세로 방향으로 배치 (행 단위로 배치)
                // 최대 행 수 계산
                const maxRows = Math.max(...partitionStudents.map(students => students.length));
                
                // 각 행별로 배치 (세로 방향)
                for (let row = 0; row < maxRows; row++) {
                    for (let partition = 0; partition < partitionCount; partition++) {
                        if (row < partitionStudents[partition].length) {
                            const {student, index} = partitionStudents[partition][row];
                            const card = this.createStudentCard(student, index);
                            card.style.width = '100%';
                            card.style.maxWidth = '120px';
                            card.style.margin = '0 auto';
                            seatsArea.appendChild(card);
                        }
                    }
                }
            } else if (singleMode === 'gender-symmetric-row') {
                // '남녀 대칭 1줄 배치' 모드 - 세로(열) 방향으로 배치
                // 남학생을 먼저 앞쪽 분단부터 순차적으로 배치
                // 남학생이 다 배치되면 여학생을 나머지 자리에 배치
                // 예: 남학생 12명, 여학생 12명, 5분단
                // 1분단: 남1, 남2, 남3, 남4, 남5 (5명)
                // 2분단: 남6, 남7, 남8, 남9, 남10 (5명)
                // 3분단: 남11, 남12, 여1, 여2, 여3 (5명)
                // 4분단: 여4, 여5, 여6, 여7, 여8 (5명)
                // 5분단: 여9, 여10, 여11, 여12 (4명)
                
                const totalStudents = maleStudents.length + femaleStudents.length;
                const studentsPerPartition = Math.ceil(totalStudents / partitionCount);
                
                // 각 분단별 학생 배열을 먼저 구성
                const partitionStudents: Array<Array<{student: Student, index: number}>> = [];
                for (let partition = 0; partition < partitionCount; partition++) {
                    partitionStudents.push([]);
                }
                
                let maleIndex = 0;
                let femaleIndex = 0;
                
                // 먼저 남학생을 앞쪽 분단부터 순차적으로 배치
                for (let partition = 0; partition < partitionCount; partition++) {
                    // 각 분단의 최대 용량 계산
                    const remainingStudents = totalStudents - (maleIndex + femaleIndex);
                    const remainingPartitions = partitionCount - partition;
                    const maxCapacity = partition === partitionCount - 1 
                        ? remainingStudents 
                        : Math.min(studentsPerPartition, remainingStudents);
                    
                    // 남학생을 먼저 배치
                    while (maleIndex < maleStudents.length && partitionStudents[partition].length < maxCapacity) {
                        partitionStudents[partition].push({
                            student: maleStudents[maleIndex],
                            index: this.students.indexOf(maleStudents[maleIndex])
                        });
                        maleIndex++;
                    }
                }
                
                // 남학생이 다 배치된 후, 여학생을 나머지 자리에 배치
                for (let partition = 0; partition < partitionCount; partition++) {
                    const remainingStudents = totalStudents - (maleIndex + femaleIndex);
                    const remainingPartitions = partitionCount - partition;
                    const maxCapacity = partition === partitionCount - 1 
                        ? remainingStudents 
                        : Math.min(studentsPerPartition, remainingStudents);
                    
                    // 여학생을 나머지 자리에 배치
                    while (femaleIndex < femaleStudents.length && partitionStudents[partition].length < maxCapacity) {
                        partitionStudents[partition].push({
                            student: femaleStudents[femaleIndex],
                            index: this.students.indexOf(femaleStudents[femaleIndex])
                        });
                        femaleIndex++;
                    }
                }
                
                // 각 분단의 학생들을 세로 방향으로 배치 (행 단위로 배치)
                // 최대 행 수 계산
                const maxRows = Math.max(...partitionStudents.map(students => students.length));
                
                // 각 행별로 배치 (세로 방향)
                for (let row = 0; row < maxRows; row++) {
                    for (let partition = 0; partition < partitionCount; partition++) {
                        if (row < partitionStudents[partition].length) {
                            const {student, index} = partitionStudents[partition][row];
                            const card = this.createStudentCard(student, index);
                            card.style.width = '100%';
                            card.style.maxWidth = '120px';
                            card.style.margin = '0 auto';
                            seatsArea.appendChild(card);
                        }
                    }
                }
            } else {
                // 기존 로직 (다른 모드가 추가될 경우를 위해)
                // 각 분단별 행 수 계산
                const rowsPerPartition = Math.ceil(maleStudents.length / partitionCount);
                
                // 각 행별로 배치
                for (let row = 0; row < rowsPerPartition; row++) {
                    // 각 분단의 남학생과 여학생을 교대로 배치
                    for (let partition = 0; partition < partitionCount; partition++) {
                        const maleIndex = row * partitionCount + partition;
                        const femaleIndex = row * partitionCount + partition;
                        
                        // 남학생 카드 배치
                        if (maleIndex < maleStudents.length) {
                            const card = this.createStudentCard(maleStudents[maleIndex], this.students.indexOf(maleStudents[maleIndex]));
                            // 카드 너비를 일정하게 설정하여 분단 이름과 정렬되도록
                            card.style.width = '100%';
                            card.style.maxWidth = '120px'; // 최대 너비 제한
                            card.style.margin = '0 auto'; // 중앙 정렬
                            seatsArea.appendChild(card);
                        }
                        
                        // 여학생 카드 배치
                        if (femaleIndex < femaleStudents.length) {
                            const card = this.createStudentCard(femaleStudents[femaleIndex], this.students.indexOf(femaleStudents[femaleIndex]));
                            // 카드 너비를 일정하게 설정하여 분단 이름과 정렬되도록
                            card.style.width = '100%';
                            card.style.maxWidth = '120px'; // 최대 너비 제한
                            card.style.margin = '0 auto'; // 중앙 정렬
                            seatsArea.appendChild(card);
                        }
                    }
                }
            }
        }
    }
    
    /**
     * 학생 카드 생성 헬퍼 메서드
     */
    private createStudentCard(student: Student, index: number): HTMLDivElement {
        const card = document.createElement('div');
        card.className = 'student-seat-card';
        card.setAttribute('draggable', 'true');
        
        // 좌석 고유 ID 부여
        const seatId = this.nextSeatId++;
        card.setAttribute('data-seat-id', seatId.toString());
        
        // 접근성 개선: ARIA 레이블 추가
        card.setAttribute('role', 'button');
        // 성별 아이콘 정보 포함
        const genderLabel = student.gender === 'M' ? '남학생 ♂' : '여학생 ♀';
        card.setAttribute('aria-label', `좌석 ${seatId}: ${student.name} (${genderLabel})`);
        card.setAttribute('tabindex', '0');
        
        // 좌석 번호 표시 (좌측 상단)
        const seatNumberDiv = document.createElement('div');
        seatNumberDiv.className = 'seat-number-label';
        seatNumberDiv.textContent = `#${seatId}`;
        seatNumberDiv.setAttribute('aria-hidden', 'true');
        seatNumberDiv.style.cssText = `
            position: absolute;
            top: 5px;
            left: 5px;
            font-size: 0.8em;
            font-weight: bold;
            color: #667eea;
            background: rgba(255, 255, 255, 0.9);
            padding: 2px 6px;
            border-radius: 4px;
            z-index: 5;
        `;
        card.appendChild(seatNumberDiv);
        
        const nameDiv = document.createElement('div');
        nameDiv.className = 'student-name';
        
        // 긴 이름 처리: 20자 이상이면 말줄임표 표시 및 툴팁 추가
        const displayName = student.name.length > 20 ? student.name.substring(0, 20) + '...' : student.name;
        nameDiv.textContent = displayName;
        if (student.name.length > 20) {
            nameDiv.setAttribute('title', student.name);
            nameDiv.setAttribute('aria-label', student.name);
        }
        
        nameDiv.style.display = 'flex';
        nameDiv.style.alignItems = 'center';
        nameDiv.style.justifyContent = 'center';
        nameDiv.style.height = '100%';
        nameDiv.style.width = '100%';
        nameDiv.style.overflow = 'hidden';
        nameDiv.style.textOverflow = 'ellipsis';
        nameDiv.style.whiteSpace = 'nowrap';
        nameDiv.style.padding = '0 5px';
        
        // 성별에 따라 클래스 추가
        if (student.gender === 'M') {
            card.classList.add('gender-m');
            // ARIA 레이블에 성별 아이콘 정보 추가
            card.setAttribute('aria-label', `좌석 ${seatId}: ${student.name} (남학생 ♂)`);
        } else {
            card.classList.add('gender-f');
            // ARIA 레이블에 성별 아이콘 정보 추가
            card.setAttribute('aria-label', `좌석 ${seatId}: ${student.name} (여학생 ♀)`);
        }
        
        card.appendChild(nameDiv);
        
        // 이미 고정된 좌석인 경우 시각적 표시
        if (this.fixedSeatIds.has(seatId)) {
            card.classList.add('fixed-seat');
            const genderLabel = student.gender === 'M' ? '남학생 ♂' : '여학생 ♀';
            card.setAttribute('aria-label', `고정 좌석 ${seatId}: ${student.name} (${genderLabel}) - 클릭하여 해제`);
            card.title = '고정 좌석 (클릭하여 해제)';
            
            // 🔒 아이콘 추가 (색상 외 시각적 구분)
            const lockIcon = document.createElement('div');
            lockIcon.className = 'fixed-seat-lock';
            lockIcon.textContent = '🔒';
            lockIcon.setAttribute('aria-hidden', 'true');
            lockIcon.setAttribute('aria-label', '고정 좌석');
            lockIcon.style.cssText = 'position: absolute; top: 5px; right: 5px; font-size: 1.2em; z-index: 10; pointer-events: none;';
            card.appendChild(lockIcon);
        }
        
        // 키보드 네비게이션 지원
        this.addEventListenerSafe(card, 'keydown', (e: Event) => {
            const ke = e as KeyboardEvent;
            if (ke.key === 'Enter' || ke.key === ' ') {
                e.preventDefault();
                card.click();
            }
        });
        
        // 고정 좌석 모드일 때 클릭 이벤트 추가
        this.setupFixedSeatClickHandler(card, seatId);
        
        return card;
    }

    /**
     * 좌석 카드 드래그&드롭 스왑 기능 활성화 (이벤트 위임)
     */
    private enableSeatSwapDragAndDrop(): void {
        const seatsArea = document.getElementById('seats-area');
        if (!seatsArea) return;

        // dragstart
        this.addEventListenerSafe(seatsArea, 'dragstart', (ev) => {
            const e = ev as DragEvent;
            const target = (e.target as HTMLElement)?.closest('.student-seat-card') as HTMLElement | null;
            if (!target) return;
            
            // 읽기 전용 모드에서는 드래그 불가
            if (this.isReadOnlyMode) {
                e.preventDefault();
                return;
            }
            
            // 자리 배치가 완료되었는지 확인 (액션 버튼이 표시되어 있으면 배치 완료 상태)
            const actionButtons = document.getElementById('layout-action-buttons');
            const isLayoutComplete = actionButtons && actionButtons.style.display !== 'none';
            
            // 배치가 완료되지 않은 상태에서 고정 좌석 모드가 활성화되어 있으면 드래그 비활성화
            // (미리보기 단계에서 좌석을 클릭해서 고정할 수 있도록)
            if (!isLayoutComplete) {
                const fixedRandomMode = document.querySelector('input[name="custom-mode-2"][value="fixed-random"]:checked') as HTMLInputElement;
                if (fixedRandomMode) {
                    e.preventDefault();
                    return;
                }
            }
            
            // 고정 좌석은 드래그 불가
            if (target.classList.contains('fixed-seat')) {
                e.preventDefault();
                return;
            }
            this.dragSourceCard = target;
            
            // 드래그 피드백: 드래그 중인 카드 스타일 변경
            target.style.opacity = '0.5';
            target.style.transform = 'scale(0.95)';
            target.style.transition = 'all 0.2s ease';
            target.style.cursor = 'grabbing';
            target.classList.add('dragging');
            
            try { e.dataTransfer?.setData('text/plain', 'swap'); } catch {}
            if (e.dataTransfer) {
                e.dataTransfer.effectAllowed = 'move';
                // 드래그 이미지 설정 (투명한 이미지로 커스텀 커서 효과)
                const dragImage = target.cloneNode(true) as HTMLElement;
                dragImage.style.position = 'absolute';
                dragImage.style.top = '-1000px';
                document.body.appendChild(dragImage);
                e.dataTransfer.setDragImage(dragImage, e.offsetX, e.offsetY);
                this.setTimeoutSafe(() => {
                    document.body.removeChild(dragImage);
                }, 0);
            }
        });
        
        // dragend - 드래그가 끝나면 dragSourceCard 초기화 (드롭되지 않은 경우 대비)
        this.addEventListenerSafe(seatsArea, 'dragend', (ev) => {
            const e = ev as DragEvent;
            const target = (e.target as HTMLElement)?.closest('.student-seat-card') as HTMLElement | null;
            
            // 드래그 피드백 복원
            if (target) {
                target.style.opacity = '';
                target.style.transform = '';
                target.style.cursor = '';
                target.classList.remove('dragging');
            }
            
            // 모든 하이라이트 및 인디케이터 제거
            seatsArea.querySelectorAll('.drag-over').forEach(el => {
                el.classList.remove('drag-over');
            });
            seatsArea.classList.remove('drag-over-area');
            if (this.dragOverIndicator) {
                this.dragOverIndicator.remove();
                this.dragOverIndicator = null;
            }
            this.dragSourceCard = null;
        });

        // dragover - 빈 공간과 카드 모두에서 드롭 가능하도록
        this.addEventListenerSafe(seatsArea, 'dragover', (ev) => {
            const e = ev as DragEvent;
            if (this.dragSourceCard) {
                e.preventDefault();
                if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
                
                // 기존 하이라이트 및 인디케이터 제거
                seatsArea.querySelectorAll('.drag-over').forEach(el => {
                    el.classList.remove('drag-over');
                });
                if (this.dragOverIndicator) {
                    this.dragOverIndicator.remove();
                    this.dragOverIndicator = null;
                }
                
                // 마우스 위치 기반으로 드롭 위치 계산
                const seatsAreaRect = seatsArea.getBoundingClientRect();
                const mouseX = e.clientX - seatsAreaRect.left;
                const mouseY = e.clientY - seatsAreaRect.top;
                
                // 모든 카드 가져오기 (분단 레이블 제외)
                const allCards = Array.from(seatsArea.querySelectorAll('.student-seat-card')) as HTMLElement[];
                const cardsOnly = allCards.filter(card => 
                    card !== this.dragSourceCard && 
                    !card.classList.contains('partition-label') &&
                    !card.closest('.labels-row')
                );
                
                // 마우스 위치에서 가장 가까운 카드 찾기
                let closestCard: HTMLElement | null = null;
                let minDistance = Infinity;
                let insertPosition: 'before' | 'after' | 'on' = 'on';
                
                for (const card of cardsOnly) {
                    const cardRect = card.getBoundingClientRect();
                    const cardX = cardRect.left - seatsAreaRect.left + cardRect.width / 2;
                    const cardY = cardRect.top - seatsAreaRect.top + cardRect.height / 2;
                    
                    // 카드 영역 내부인지 확인
                    const cardLeft = cardRect.left - seatsAreaRect.left;
                    const cardRight = cardRect.right - seatsAreaRect.left;
                    const cardTop = cardRect.top - seatsAreaRect.top;
                    const cardBottom = cardRect.bottom - seatsAreaRect.top;
                    
                    if (mouseX >= cardLeft && mouseX <= cardRight && 
                        mouseY >= cardTop && mouseY <= cardBottom) {
                        // 카드 위에 마우스가 있으면 카드 하이라이트
                        closestCard = card;
                        insertPosition = 'on';
                        break;
                    }
                    
                    // 카드 근처 거리 계산
                    const distance = Math.sqrt(Math.pow(mouseX - cardX, 2) + Math.pow(mouseY - cardY, 2));
                    
                    if (distance < minDistance) {
                        minDistance = distance;
                        closestCard = card;
                        
                        // 드롭 위치가 카드보다 위쪽이면 앞에, 아래쪽이면 뒤에
                        if (mouseY < cardY - cardRect.height / 4) {
                            insertPosition = 'before';
                        } else if (mouseY > cardY + cardRect.height / 4) {
                            insertPosition = 'after';
                        } else {
                            // 수평 위치로 판단
                            if (mouseX < cardX) {
                                insertPosition = 'before';
                            } else {
                                insertPosition = 'after';
                            }
                        }
                    }
                }
                
                // 시각적 피드백 제공
                if (closestCard) {
                    if (insertPosition === 'on') {
                        // 카드 위에 마우스가 있으면 카드 하이라이트
                        closestCard.classList.add('drag-over');
                    } else {
                        // 카드 앞/뒤에 삽입 인디케이터 표시
                        this.showInsertIndicator(closestCard, insertPosition);
                    }
                } else {
                    // 빈 공간에 드롭할 경우 seats-area에 하이라이트
                    seatsArea.classList.add('drag-over-area');
                }
            }
        });

        // dragleave - 하이라이트 제거
        this.addEventListenerSafe(seatsArea, 'dragleave', (ev) => {
            const e = ev as DragEvent;
            // seats-area를 완전히 벗어난 경우에만 하이라이트 제거
            const relatedTarget = e.relatedTarget as HTMLElement;
            if (!relatedTarget || !seatsArea.contains(relatedTarget)) {
                seatsArea.querySelectorAll('.drag-over').forEach(el => {
                    el.classList.remove('drag-over');
                });
                seatsArea.classList.remove('drag-over-area');
                if (this.dragOverIndicator) {
                    this.dragOverIndicator.remove();
                    this.dragOverIndicator = null;
                }
            }
        });

        // drop -> 카드 교환 또는 이동
        this.addEventListenerSafe(seatsArea, 'drop', (ev) => {
            const e = ev as DragEvent;
            e.preventDefault();
            
            // 하이라이트 및 인디케이터 제거
            seatsArea.querySelectorAll('.drag-over').forEach(el => {
                el.classList.remove('drag-over');
            });
            seatsArea.classList.remove('drag-over-area');
            if (this.dragOverIndicator) {
                this.dragOverIndicator.remove();
                this.dragOverIndicator = null;
            }
            
            const source = this.dragSourceCard;
            this.dragSourceCard = null;
            if (!source) return;
            
            // 타겟이 카드인지 확인 (더 정확한 감지)
            let targetCard: HTMLElement | null = null;
            const targetElement = e.target as HTMLElement;
            
            // target이 카드 자체이거나, 카드의 자식 요소인 경우
            if (targetElement) {
                if (targetElement.classList.contains('student-seat-card')) {
                    targetCard = targetElement;
                } else {
                    targetCard = targetElement.closest('.student-seat-card') as HTMLElement | null;
                }
            }
            
            // 집었다가 제자리에 놓은 경우: 아무 일도 하지 않는다.
            // 이 가드가 없으면 아래 '빈 공간 드롭' 분기로 떨어져 카드가 엉뚱한 위치로 튄다.
            if (targetCard === source) return;

            // 카드에 직접 드롭한 경우: 교환
            if (targetCard) {
                // 고정 좌석은 교환 불가
                if (targetCard.classList.contains('fixed-seat') || source.classList.contains('fixed-seat')) return;

                const srcNameEl = source.querySelector('.student-name') as HTMLElement | null;
                const tgtNameEl = targetCard.querySelector('.student-name') as HTMLElement | null;
                if (!srcNameEl || !tgtNameEl) return;

                // 이름 스왑
                const tmpName = srcNameEl.textContent || '';
                srcNameEl.textContent = tgtNameEl.textContent || '';
                tgtNameEl.textContent = tmpName;

                // 성별 배경 클래스 스왑
                const srcIsM = source.classList.contains('gender-m');
                const srcIsF = source.classList.contains('gender-f');
                const tgtIsM = targetCard.classList.contains('gender-m');
                const tgtIsF = targetCard.classList.contains('gender-f');

                source.classList.toggle('gender-m', tgtIsM);
                source.classList.toggle('gender-f', tgtIsF);
                targetCard.classList.toggle('gender-m', srcIsM);
                targetCard.classList.toggle('gender-f', srcIsF);
            } else if (seatsArea.classList.contains('custom-layout')) {
                // 사용자 구성 배치에서는 책상이 좌표에 놓여 있어 DOM 순서가 화면 순서와 다르다.
                // 빈 공간에 떨어뜨렸을 때 순서를 회전시키면 엉뚱한 자리들이 바뀐다.
                return;
            } else {
                // 빈 공간에 드롭: 이동
                // 드롭 위치 계산 (마우스 좌표 사용)
                const seatsAreaRect = seatsArea.getBoundingClientRect();
                const dropX = e.clientX - seatsAreaRect.left;
                const dropY = e.clientY - seatsAreaRect.top;
                
                // 모든 카드 가져오기 (분단 레이블 제외)
                const allCards = Array.from(seatsArea.querySelectorAll('.student-seat-card')) as HTMLElement[];
                const cardsOnly = allCards.filter(card => 
                    card !== source && 
                    !card.classList.contains('partition-label') &&
                    !card.closest('.labels-row')
                );
                
                if (cardsOnly.length === 0) {
                    // 다른 카드가 없으면 옮길 곳도 없다
                    return;
                }
                
                // 가장 가까운 카드 찾기
                let closestCard: HTMLElement | null = null;
                let minDistance = Infinity;
                let insertPosition: 'before' | 'after' = 'after';
                
                for (const card of cardsOnly) {
                    const cardRect = card.getBoundingClientRect();
                    const cardX = cardRect.left - seatsAreaRect.left + cardRect.width / 2;
                    const cardY = cardRect.top - seatsAreaRect.top + cardRect.height / 2;
                    
                    const distance = Math.sqrt(Math.pow(dropX - cardX, 2) + Math.pow(dropY - cardY, 2));
                    
                    if (distance < minDistance) {
                        minDistance = distance;
                        closestCard = card;
                        
                        // 드롭 위치가 카드보다 위쪽이면 앞에, 아래쪽이면 뒤에
                        if (dropY < cardY - cardRect.height / 4) {
                            insertPosition = 'before';
                        } else if (dropY > cardY + cardRect.height / 4) {
                            insertPosition = 'after';
                        } else {
                            // 수평 위치로 판단
                            if (dropX < cardX) {
                                insertPosition = 'before';
                            } else {
                                insertPosition = 'after';
                            }
                        }
                    }
                }
                
                // 카드 이동: DOM 요소를 옮기지 않고 '내용'만 회전시킨다.
                // 요소를 옮기면 data-seat-id가 학생을 따라다녀서,
                // 화면에 보이는 배치와 저장되는 배치가 어긋난다.
                if (closestCard) {
                    this.moveCardContentTo(allCards, source, closestCard, insertPosition === 'before');
                }
            }
            
            // 드래그&드롭 완료 후 히스토리 저장 (약간의 지연을 두어 DOM 업데이트 완료 후 저장)
            this.setTimeoutSafe(() => {
                this.saveLayoutToHistory();
            }, 50);
        });
        
        // 모바일 터치 이벤트 지원
        this.enableTouchDragAndDrop(seatsArea);
    }
    
    /**
     * 모바일 터치 드래그&드롭 지원
     */
    private enableTouchDragAndDrop(seatsArea: HTMLElement): void {
        let touchStartX = 0;
        let touchStartY = 0;
        let touchMoved = false;
        let touchStartTime = 0;
        const DRAG_THRESHOLD = 15; // 드래그로 인식할 최소 이동 거리 (px)
        const LONG_PRESS_TIME = 500; // 길게 누르기로 인식할 시간 (ms)
        
        this.addEventListenerSafe(seatsArea, 'touchstart', (e: Event) => {
            const te = e as TouchEvent;
            const target = (te.target as HTMLElement)?.closest('.student-seat-card') as HTMLElement | null;
            if (!target) return;
            
            // 자리 배치가 완료되었는지 확인
            const actionButtons = document.getElementById('layout-action-buttons');
            const isLayoutComplete = actionButtons && actionButtons.style.display !== 'none';
            
            if (!isLayoutComplete) {
                const fixedRandomMode = document.querySelector('input[name="custom-mode-2"][value="fixed-random"]:checked') as HTMLInputElement;
                if (fixedRandomMode) return;
            }
            
            // 고정 좌석은 드래그 불가
            if (target.classList.contains('fixed-seat')) return;
            
            this.touchStartCard = target;
            touchMoved = false;
            touchStartTime = Date.now();
            const touch = te.touches[0];
            touchStartX = touch.clientX;
            touchStartY = touch.clientY;
            this.touchStartPosition = { x: touchStartX, y: touchStartY };
            
            // 시각적 피드백 (약간의 지연 후)
            const feedbackTimer = window.setTimeout(() => {
                if (this.touchStartCard === target && !touchMoved) {
                    target.style.opacity = '0.7';
                    target.style.transform = 'scale(1.08)';
                    target.style.transition = 'transform 0.2s ease';
                    target.style.zIndex = '1000';
                }
            }, 100);
            
            // 타이머 정리를 위한 저장
            (target as any).__feedbackTimer = feedbackTimer;
        }, { passive: true });
        
        this.addEventListenerSafe(seatsArea, 'touchmove', (e: Event) => {
            const te = e as TouchEvent;
            if (!this.touchStartCard) return;
            
            const touch = te.touches[0];
            const deltaX = Math.abs(touch.clientX - touchStartX);
            const deltaY = Math.abs(touch.clientY - touchStartY);
            const totalDelta = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
            
            // 최소 이동 거리 체크 (개선된 임계값)
            if (totalDelta > DRAG_THRESHOLD) {
                touchMoved = true;
                
                // 피드백 타이머 취소
                const feedbackTimer = (this.touchStartCard as any).__feedbackTimer;
                if (feedbackTimer) {
                    clearTimeout(feedbackTimer);
                    delete (this.touchStartCard as any).__feedbackTimer;
                }
                
                // 드래그 중 시각적 피드백 강화
                this.touchStartCard.style.opacity = '0.8';
                this.touchStartCard.style.transform = `scale(1.1) translate(${touch.clientX - touchStartX}px, ${touch.clientY - touchStartY}px)`;
                this.touchStartCard.style.transition = 'none';
                
                // 스크롤 방지
                te.preventDefault();
                
                // 드롭 위치 인디케이터 표시
                const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
                const targetCard = (elementBelow as HTMLElement)?.closest('.student-seat-card') as HTMLElement | null;
                
                // 이전 인디케이터 제거
                if (this.dragOverIndicator && this.dragOverIndicator !== targetCard) {
                    this.dragOverIndicator.style.outline = '';
                    this.dragOverIndicator.style.outlineOffset = '';
                }
                
                // 새 인디케이터 표시
                if (targetCard && targetCard !== this.touchStartCard && !targetCard.classList.contains('fixed-seat')) {
                    targetCard.style.outline = '3px dashed #667eea';
                    targetCard.style.outlineOffset = '2px';
                    this.dragOverIndicator = targetCard;
                } else if (this.dragOverIndicator) {
                    this.dragOverIndicator.style.outline = '';
                    this.dragOverIndicator.style.outlineOffset = '';
                    this.dragOverIndicator = null;
                }
            }
        }, { passive: false });
        
        this.addEventListenerSafe(seatsArea, 'touchend', (e: Event) => {
            const te = e as TouchEvent;
            if (!this.touchStartCard) {
                this.touchStartCard = null;
                this.touchStartPosition = null;
                return;
            }
            
            // 피드백 타이머 취소
            const feedbackTimer = (this.touchStartCard as any).__feedbackTimer;
            if (feedbackTimer) {
                clearTimeout(feedbackTimer);
                delete (this.touchStartCard as any).__feedbackTimer;
            }
            
            const touch = te.changedTouches[0];
            const endX = touch.clientX;
            const endY = touch.clientY;
            
            // 원래 스타일 복원
            this.touchStartCard.style.opacity = '';
            this.touchStartCard.style.transform = '';
            this.touchStartCard.style.transition = '';
            this.touchStartCard.style.zIndex = '';
            
            // 드롭 위치 인디케이터 제거
            if (this.dragOverIndicator) {
                this.dragOverIndicator.style.outline = '';
                this.dragOverIndicator.style.outlineOffset = '';
                this.dragOverIndicator = null;
            }
            
            // 이동 거리가 충분하면 드롭 처리
            if (touchMoved) {
                const elementBelow = document.elementFromPoint(endX, endY);
                const targetCard = (elementBelow as HTMLElement)?.closest('.student-seat-card') as HTMLElement | null;
                
                if (targetCard && targetCard !== this.touchStartCard && !targetCard.classList.contains('fixed-seat')) {
                    // 카드 교환
                    const srcNameEl = this.touchStartCard.querySelector('.student-name') as HTMLElement | null;
                    const tgtNameEl = targetCard.querySelector('.student-name') as HTMLElement | null;
                    if (srcNameEl && tgtNameEl) {
                        // 이름 스왑
                        const tmpName = srcNameEl.textContent || '';
                        srcNameEl.textContent = tgtNameEl.textContent || '';
                        tgtNameEl.textContent = tmpName;
                        
                        // 성별 배경 클래스 스왑
                        const srcIsM = this.touchStartCard.classList.contains('gender-m');
                        const srcIsF = this.touchStartCard.classList.contains('gender-f');
                        const tgtIsM = targetCard.classList.contains('gender-m');
                        const tgtIsF = targetCard.classList.contains('gender-f');
                        
                        this.touchStartCard.classList.toggle('gender-m', tgtIsM);
                        this.touchStartCard.classList.toggle('gender-f', tgtIsF);
                        targetCard.classList.toggle('gender-m', srcIsM);
                        targetCard.classList.toggle('gender-f', srcIsF);
                        
                        // 성공 피드백 (시각적)
                        targetCard.style.transform = 'scale(1.05)';
                        setTimeout(() => {
                            targetCard.style.transform = '';
                        }, 200);
                        
                        // 히스토리 저장
                        this.setTimeoutSafe(() => {
                            this.saveLayoutToHistory();
                        }, 50);
                    }
                }
            }
            
            this.touchStartCard = null;
            this.touchStartPosition = null;
            touchMoved = false;
        }, { passive: true });
        
        this.addEventListenerSafe(seatsArea, 'touchcancel', () => {
            // 터치 취소 시 정리
            if (this.touchStartCard) {
                this.touchStartCard.style.opacity = '';
                this.touchStartCard.style.transform = '';
                this.touchStartCard.style.transition = '';
                this.touchStartCard.style.zIndex = '';
                
                if (this.dragOverIndicator) {
                    this.dragOverIndicator.style.outline = '';
                    this.dragOverIndicator.style.outlineOffset = '';
                    this.dragOverIndicator = null;
                }
                
                this.touchStartCard = null;
                this.touchStartPosition = null;
                touchMoved = false;
            }
        }, { passive: true });
    }

    /**
     * 드롭 위치 삽입 인디케이터 표시
     */
    private showInsertIndicator(card: HTMLElement, position: 'before' | 'after'): void {
        // 기존 인디케이터 제거
        if (this.dragOverIndicator) {
            this.dragOverIndicator.remove();
        }

        const seatsArea = document.getElementById('seats-area');
        if (!seatsArea) return;

        // 인디케이터 생성
        const indicator = document.createElement('div');
        indicator.className = 'drag-insert-indicator';
        indicator.style.position = 'absolute';
        indicator.style.pointerEvents = 'none';
        indicator.style.zIndex = '1000';
        
        const cardRect = card.getBoundingClientRect();
        const seatsAreaRect = seatsArea.getBoundingClientRect();
        
        // seats-area가 relative 포지션이 아니면 설정
        const currentPosition = window.getComputedStyle(seatsArea).position;
        if (currentPosition === 'static') {
            seatsArea.style.position = 'relative';
        }
        
        if (position === 'before') {
            // 카드 앞에 표시
            indicator.style.top = `${cardRect.top - seatsAreaRect.top - 3}px`;
            indicator.style.left = `${cardRect.left - seatsAreaRect.left}px`;
            indicator.style.width = `${cardRect.width}px`;
            indicator.style.height = '4px';
        } else {
            // 카드 뒤에 표시
            indicator.style.top = `${cardRect.bottom - seatsAreaRect.top + 3}px`;
            indicator.style.left = `${cardRect.left - seatsAreaRect.left}px`;
            indicator.style.width = `${cardRect.width}px`;
            indicator.style.height = '4px';
        }
        
        seatsArea.appendChild(indicator);
        this.dragOverIndicator = indicator;
    }

    /**
     * 현재 상태를 히스토리에 저장 (통합 히스토리 시스템)
     */
    private saveToHistory(type: string, data: HistoryData): void {
        // 현재 인덱스 이후의 히스토리 제거 (새로운 상태가 추가되면 이후 히스토리는 삭제)
        if (this.historyIndex < this.layoutHistory.length - 1) {
            this.layoutHistory = this.layoutHistory.slice(0, this.historyIndex + 1);
        }
        
        // 새 상태 추가
        this.layoutHistory.push({ type, data });
        
        // 히스토리 크기 제한 (최대 100개)
        if (this.layoutHistory.length > 100) {
            this.layoutHistory.shift();
        } else {
            this.historyIndex++;
        }
        
        // 되돌리기 버튼 활성화/비활성화 업데이트
        this.updateUndoButtonState();
    }
    
    /**
     * 현재 자리 배치 상태를 히스토리에 저장
     */
    private saveLayoutToHistory(): void {
        const seatsArea = document.getElementById('seats-area');
        if (!seatsArea) return;
        
        // 현재 상태를 HTML 문자열로 저장
        const currentState = seatsArea.innerHTML;
        
        // 학생 데이터도 함께 저장
        const studentData = this.inputModule.getStudentData();
        
        this.saveToHistory('layout', {
            seatsAreaHTML: currentState,
            students: JSON.parse(JSON.stringify(studentData)), // 깊은 복사
            gridTemplateColumns: seatsArea.style.gridTemplateColumns
        });
    }
    
    /**
     * 되돌리기 기능 실행 (모든 액션에 대해 작동)
     */
    private handleUndoLayout(): void {
        
        
        if (this.historyIndex <= 0 || this.layoutHistory.length === 0) {
            // 되돌리기할 히스토리가 없음
            const message = ErrorHandler.getUserFriendlyMessage(ErrorCode.UNDO_NOT_AVAILABLE);
            this.outputModule.showError(message);
            return;
        }
        
        // 이전 상태로 복원 (인덱스를 먼저 감소시켜 이전 상태를 가져옴)
        this.historyIndex--;
        const previousState = this.layoutHistory[this.historyIndex];
        
        
        
        // 상태 타입에 따라 복원
        if (previousState && previousState.type === 'layout') {
            const seatsArea = document.getElementById('seats-area');
            if (seatsArea && previousState.data) {
                // HTML 복원
                if (previousState.data.seatsAreaHTML && typeof previousState.data.seatsAreaHTML === 'string') {
                    seatsArea.innerHTML = previousState.data.seatsAreaHTML;
                }
                
                // 그리드 설정 복원
                if (previousState.data.gridTemplateColumns && typeof previousState.data.gridTemplateColumns === 'string') {
                    seatsArea.style.gridTemplateColumns = previousState.data.gridTemplateColumns;
                }
                
                // 복원된 화면에서 배치를 다시 읽어 모델을 맞춘다.
                // 화면만 되돌리고 모델을 그대로 두면, 되돌린 직후 자리를 확정했을 때
                // 방금 되돌린 배치가 아니라 되돌리기 전 배치가 저장된다.
                const restoredLayout = this.collectLayoutFromDom();
                if (restoredLayout.length > 0) {
                    this.updateSeatsAndStudentsFromLayout(restoredLayout);
                }
                
                // 드래그&드롭 기능 다시 활성화 (복원된 카드에 대해)
                this.enableSeatSwapDragAndDrop();
            }
        } else if (previousState && previousState.type === 'options') {
            // 옵션 설정 복원
            if (previousState.data && previousState.data.options) {
                // 옵션 복원 로직 (필요시 구현)
                
            }
        }
        
        // 되돌리기/다시 실행하기 버튼 상태 업데이트
        this.updateUndoRedoButtonState();
        
        
    }
    
    /**
     * 다시 실행하기 기능 실행
     */
    private handleRedoLayout(): void {
        // 다시 실행할 히스토리가 없는 경우
        if (this.historyIndex >= this.layoutHistory.length - 1 || this.layoutHistory.length === 0) {
            const message = ErrorHandler.getUserFriendlyMessage(ErrorCode.UNDO_NOT_AVAILABLE);
            this.outputModule.showError(message);
            return;
        }
        
        // 다음 상태로 복원 (인덱스를 먼저 증가시켜 다음 상태를 가져옴)
        this.historyIndex++;
        const nextState = this.layoutHistory[this.historyIndex];
        
        // 상태 타입에 따라 복원
        if (nextState && nextState.type === 'layout') {
            const seatsArea = document.getElementById('seats-area');
            if (seatsArea && nextState.data) {
                // HTML 복원
                if (nextState.data.seatsAreaHTML && typeof nextState.data.seatsAreaHTML === 'string') {
                    seatsArea.innerHTML = nextState.data.seatsAreaHTML;
                }
                
                // 그리드 설정 복원
                if (nextState.data.gridTemplateColumns && typeof nextState.data.gridTemplateColumns === 'string') {
                    seatsArea.style.gridTemplateColumns = nextState.data.gridTemplateColumns;
                }
                
                // 복원된 화면에서 배치를 다시 읽어 모델을 맞춘다 (되돌리기와 동일)
                const restoredLayout = this.collectLayoutFromDom();
                if (restoredLayout.length > 0) {
                    this.updateSeatsAndStudentsFromLayout(restoredLayout);
                }
                
                // 드래그&드롭 기능 다시 활성화 (복원된 카드에 대해)
                this.enableSeatSwapDragAndDrop();
            }
        } else if (nextState && nextState.type === 'options') {
            // 옵션 설정 복원
            if (nextState.data && nextState.data.options) {
                // 옵션 복원 로직 (필요시 구현)
                
            }
        }
        
        // 되돌리기/다시 실행하기 버튼 상태 업데이트
        this.updateUndoRedoButtonState();
    }
    
    /**
     * 되돌리기/다시 실행하기 버튼 활성화/비활성화 상태 업데이트
     */
    private updateUndoRedoButtonState(): void {
        const undoButton = document.getElementById('undo-layout') as HTMLButtonElement;
        const redoButton = document.getElementById('redo-layout') as HTMLButtonElement;
        
        // 되돌리기 버튼 상태 업데이트
        if (undoButton) {
        // 히스토리가 있고 이전 상태가 있으면 활성화
            if (this.historyIndex > 0 && this.layoutHistory.length > 0) {
            undoButton.disabled = false;
            undoButton.style.opacity = '1';
            undoButton.style.cursor = 'pointer';
        } else {
            undoButton.disabled = true;
            undoButton.style.opacity = '0.5';
            undoButton.style.cursor = 'not-allowed';
            }
        }
        
        // 다시 실행하기 버튼 상태 업데이트
        if (redoButton) {
            // 히스토리가 있고 다음 상태가 있으면 활성화
            if (this.historyIndex < this.layoutHistory.length - 1 && this.layoutHistory.length > 0) {
                redoButton.disabled = false;
                redoButton.style.opacity = '1';
                redoButton.style.cursor = 'pointer';
            } else {
                redoButton.disabled = true;
                redoButton.style.opacity = '0.5';
                redoButton.style.cursor = 'not-allowed';
            }
        }
    }
    
    /**
     * 되돌리기 버튼 활성화/비활성화 상태 업데이트 (하위 호환성)
     */
    private updateUndoButtonState(): void {
        this.updateUndoRedoButtonState();
    }
    
    /**
     * 히스토리 초기화
     */
    private resetHistory(): void {
        this.layoutHistory = [];
        this.historyIndex = -1;
        this.updateUndoButtonState();
    }

    /**
     * 고정 좌석 클릭 핸들러 설정
     */
    private setupFixedSeatClickHandler(card: HTMLDivElement, seatId: number): void {
        // '고정 좌석 지정 후 랜덤 배치' 모드인지 확인
        const fixedRandomMode = document.querySelector('input[name="custom-mode-2"][value="fixed-random"]:checked') as HTMLInputElement;
        
        if (fixedRandomMode) {
            card.style.cursor = 'pointer';
            card.title = '클릭하여 고정 좌석 지정/해제';
            
            // 이미 고정된 좌석인지 확인하여 시각적 표시
            if (this.fixedSeatIds.has(seatId)) {
                card.classList.add('fixed-seat');
                card.title = '고정 좌석 (클릭하여 해제)';
                
                // 🔒 아이콘 추가 (없는 경우만)
                if (!card.querySelector('.fixed-seat-lock')) {
                    const lockIcon = document.createElement('div');
                    lockIcon.className = 'fixed-seat-lock';
                    lockIcon.textContent = '🔒';
                    lockIcon.style.cssText = 'position: absolute; top: 5px; right: 5px; font-size: 1.2em; z-index: 10; pointer-events: none;';
                    card.appendChild(lockIcon);
                }
            }
        }
        // 개별 클릭 이벤트는 추가하지 않음 - 이벤트 위임 방식 사용 (handleSeatCardClick)
    }
    
    /**
     * 고정 좌석 토글
     */
    private toggleFixedSeat(seatId: number, card: HTMLDivElement): void {
        if (this.fixedSeatIds.has(seatId)) {
            // 고정 해제
            this.fixedSeatIds.delete(seatId);
            card.classList.remove('fixed-seat');
            card.title = '클릭하여 고정 좌석 지정';
            
            // 🔒 아이콘 제거
            const lockIcon = card.querySelector('.fixed-seat-lock');
            if (lockIcon) {
                lockIcon.remove();
            }
        } else {
            // 고정 설정
            this.fixedSeatIds.add(seatId);
            card.classList.add('fixed-seat');
            card.title = '고정 좌석 (클릭하여 해제)';
            
            // 🔒 아이콘 추가 (없는 경우만)
            if (!card.querySelector('.fixed-seat-lock')) {
                const lockIcon = document.createElement('div');
                lockIcon.className = 'fixed-seat-lock';
                lockIcon.textContent = '🔒';
                lockIcon.style.cssText = 'position: absolute; top: 5px; right: 5px; font-size: 1.2em; z-index: 10; pointer-events: none;';
                card.appendChild(lockIcon);
            }
        }
        
        // 테이블의 드롭다운 업데이트
        this.studentTableManager.updateFixedSeatDropdowns();
        
        // 고정 좌석 설정/해제됨
    }
    
    /**
     * 테이블의 고정 좌석 드롭다운 업데이트
     */
    private updateFixedSeatDropdowns(): void {
        this.studentTableManager.updateFixedSeatDropdowns();
    }
    

    /**
     * 입력 값 검증 및 수정 (음수, 0, 큰 숫자 처리)
     * InputValidator가 실시간 검증을 처리하므로, 여기서는 값 정규화만 수행
     */
    private validateAndFixStudentInput(input: HTMLInputElement, inputType: 'male' | 'female'): void {
        // 숫자가 아닌 문자 제거
        let cleanedValue = input.value.replace(/[^0-9]/g, '');
        let value = parseInt(cleanedValue || '0', 10);
        
        // NaN 체크
        if (isNaN(value)) {
            value = 0;
        }
        
        // 최소값 제한: 0
        if (value < 0) {
            value = 0;
        }
        
        // 최대값 제한: 100 (InputValidator에서도 검증하지만 여기서도 제한)
        if (value > 100) {
            value = 100;
        }
        
        // 값이 변경되었으면 입력 필드 업데이트
        const currentValue = parseInt(input.value.replace(/[^0-9]/g, '') || '0', 10);
        if (currentValue !== value || input.value !== value.toString()) {
            input.value = value.toString();
            // 값이 변경되면 InputValidator가 자동으로 재검증
            input.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }
    
    /**
     * 분단 수 입력 값 검증 및 수정
     * InputValidator가 실시간 검증을 처리하므로, 여기서는 값 정규화만 수행
     */
    private validateAndFixPartitionInput(input: HTMLInputElement): void {
        let value = parseInt(input.value || '1', 10);
        
        // NaN 체크
        if (isNaN(value)) {
            value = 1;
        }
        
        // 최소값: 1
        if (value < 1) {
            value = 1;
        }
        
        // 최대값: 10
        if (value > 10) {
            value = 10;
            this.outputModule.showInfo('분단 수는 최대 10개까지 입력 가능합니다.');
        }
        
        // 값이 변경되었으면 입력 필드 업데이트
        const currentValue = parseInt(input.value.replace(/[^0-9]/g, '') || '1', 10);
        if (currentValue !== value || input.value !== value.toString()) {
            input.value = value.toString();
            // 값이 변경되면 InputValidator가 자동으로 재검증
            input.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }

    /**
     * 성별별 학생 수에 따라 미리보기 업데이트
     */
    private updatePreviewForGenderCounts(): void {
        this.uiManager.updatePreviewForGenderCounts();
    }

    /**
     * 학생 수에 따라 미리보기 업데이트
     */
    private updatePreviewForStudentCount(count: number): void {
        
        // 학생 및 좌석 배열 초기화
        this.students = [];
        this.seats = [];
        
        // 지정된 수만큼 학생과 좌석 생성
        for (let i = 0; i < count && i < 100; i++) {
            const student = StudentModel.create(
                `학생${i + 1}`,
                (i % 2 === 0) ? 'M' : 'F'
            );
            this.students.push(student);
            
            // 좌석 생성 (더미)
            const seat = {
                id: i + 1,
                position: { x: 0, y: 0 },
                isActive: true,
                isFixed: false,
                studentId: student.id,
                studentName: student.name
            } as Seat;
            this.seats.push(seat);
        }
        
        // 카드 렌더링 (초기 6열 배치 유지)
        this.renderExampleCards();
    }

    /**
     * 학생 데이터로 카드 렌더링
     */
    private renderStudentCards(seats: Seat[]): void {
        const seatsArea = document.getElementById('seats-area');
        if (!seatsArea) return;

        // 기존 카드 제거
        seatsArea.innerHTML = '';
        
        // 새로운 배치 시작 시 히스토리 초기화는 하지 않음
        // (자리 배치 실행 후에도 히스토리를 유지하여 되돌리기 가능하도록)

        // 좌석 번호를 1부터 시작하도록 초기화
        this.nextSeatId = 1;

        // 현재 선택된 배치 형태 확인
        const layoutTypeInput = document.querySelector('input[name="layout-type"]:checked') as HTMLInputElement;
        const layoutType = layoutTypeInput ? layoutTypeInput.value : '';
        const groupSizeInput = document.querySelector('input[name="group-size"]:checked') as HTMLInputElement;
        const groupSize = groupSizeInput ? groupSizeInput.value : '';

        

        // 모둠 배치인지 확인
        const isGroupLayout = layoutType === 'group' && (groupSize === 'group-3' || groupSize === 'group-4' || groupSize === 'group-5' || groupSize === 'group-6');
        const groupSizeNumber = groupSize === 'group-3' ? 3 : groupSize === 'group-4' ? 4 : groupSize === 'group-5' ? 5 : groupSize === 'group-6' ? 6 : 0;

        if (isGroupLayout && groupSizeNumber > 0) {
            // 모둠 배치: LayoutRenderer를 통해 처리
            this.layoutRenderer.renderFinalLayout(seats);
            return;
        }
            
            // 일반 배치: 기존 방식대로 표시
            // 학생 수에 따라 그리드 열 수 결정
            const columnCount = this.students.length <= 20 ? 4 : 6;
            seatsArea.style.gridTemplateColumns = `repeat(${columnCount}, 1fr)`;
            seatsArea.style.gap = '10px';
            seatsArea.style.display = 'grid';

            // 좌석에 기록된 배정 정보를 그대로 사용한다.
            // 명단 순서로 다시 그리면 저장해 둔 배치가 전혀 다른 배치로 복원된다.
            const occupants = SeatOccupancyService.resolveOccupants(seats, this.students);
            occupants.forEach((student, index) => {
                if (!student) return;
                const card = this.createStudentCard(student, index);
                seatsArea.appendChild(card);
            });

        // 렌더 후 드래그&드롭 스왑 핸들러 보장
        this.enableSeatSwapDragAndDrop();
        
        // 초기 렌더링 후 첫 번째 상태를 히스토리에 저장
        this.setTimeoutSafe(() => {
            this.saveLayoutToHistory();
        }, 100);
    }


        
    /**
     * 좌석 배치 결과를 localStorage에 저장
     */
    private saveLayoutResult(): void {
        try {
            const layoutData = {
                seats: this.seats,
                students: this.students,
                timestamp: new Date().toISOString()
            };
            
            const success = this.storageManager.safeSetItem('layoutResult', JSON.stringify(layoutData));
            if (!success) {
                // 저장 실패 시 사용자에게 알림 (이미 safeSetItem에서 표시됨)
            }
        } catch (error) {
            logger.error('배치 결과 저장 중 오류:', error);
            this.outputModule.showError('배치 결과 저장 중 오류가 발생했습니다.');
        }
    }

    /**
     * 저장된 좌석 배치 결과 불러오기
     */
    private loadSavedLayoutResult(): void {
        try {
            const layoutDataStr = this.storageManager.safeGetItem('layoutResult');
            if (!layoutDataStr) {
                return;
            }

            // JSON 파싱 시도 (데이터 손상 처리)
            let layoutData: LayoutResultData;
            try {
                layoutData = JSON.parse(layoutDataStr) as LayoutResultData;
            } catch (parseError) {
                // 데이터 손상 시 저장소에서 제거하고 기본값으로 복구
                try {
                    localStorage.removeItem('layoutResult');
                } catch {}
                this.outputModule.showInfo('저장된 데이터가 손상되어 초기화되었습니다.');
                return;
            }
            
            // 데이터 구조 검증
            if (!layoutData || typeof layoutData !== 'object') {
                try {
                    localStorage.removeItem('layoutResult');
                } catch {}
                return;
            }
            
            if (layoutData.seats && Array.isArray(layoutData.seats) && 
                layoutData.students && Array.isArray(layoutData.students)) {
                this.seats = layoutData.seats;
                this.students = layoutData.students;
            } else {
                // 데이터 구조가 올바르지 않으면 제거
                try {
                    localStorage.removeItem('layoutResult');
                } catch {}
            }
        } catch (error) {
            logger.error('배치 결과 불러오기 중 오류:', error);
            // 에러 발생 시 저장소 정리 시도
            try {
                localStorage.removeItem('layoutResult');
            } catch {}
        }
    }


    /**
     * 결과 내보내기 처리
     */
    private handleExport(): void {
        this.printExportManager.exportAsText();
    }


    /**
     * 배치 미리보기 처리
     * @param layoutType 배치 유형
     * @param groupSize 모둠 크기 (선택적)
     */
    private handleLayoutPreview(layoutType: string, groupSize?: number): void {
        // 미리보기 기능 비활성화됨
        // 사용자가 '자리 배치 생성' 버튼을 클릭할 때만 배치가 표시됩니다.
        return;
    }

    /**
     * 카드 형태로 미리보기 렌더링
     * @param seats 좌석 배열
     */
    private renderPreviewCards(seats: Seat[]): void {
        const seatsArea = document.getElementById('seats-area');
        if (!seatsArea) return;

        // 기존 카드 제거
        seatsArea.innerHTML = '';

        // 그리드 레이아웃 설정 (6열 x 4행)
        seatsArea.style.gridTemplateColumns = 'repeat(6, 1fr)';
        seatsArea.style.gap = '10px';

        seats.forEach((seat, index) => {
            const card = document.createElement('div');
            card.className = 'student-seat-card';
            
            const nameDiv = document.createElement('div');
            nameDiv.className = 'student-name';
            nameDiv.textContent = `학생${index + 1}`;
            
            const genderDiv = document.createElement('div');
            genderDiv.className = 'student-gender';
            genderDiv.textContent = (index % 2 === 0) ? '남' : '여';
            
            const numberDiv = document.createElement('div');
            numberDiv.className = 'student-number';
            numberDiv.textContent = `${index + 1}번`;
            
            // 성별에 따라 클래스 추가
            if (index % 2 === 0) {
                card.classList.add('gender-m');
            } else {
                card.classList.add('gender-f');
            }
            
            card.appendChild(nameDiv);
            card.appendChild(genderDiv);
            card.appendChild(numberDiv);
            
            seatsArea.appendChild(card);
        });
    }

    /**
     * 학생 명렬표 테이블 생성
     * @param count 학생 수 (선택적)
     */
    private handleCreateStudentTable(count?: number): void {
        this.studentTableManager.createStudentTable(count);
    }
    

    /**
     * 학생 행 삭제 처리
     * @param row 삭제할 행
     */
    private handleDeleteStudentRow(row: HTMLTableRowElement): void {
        if (confirm('이 학생을 삭제하시겠습니까?')) {
            row.remove();
            this.updateRowNumbers();
            this.updateStudentTableStats(); // 통계 업데이트
        }
    }

    /**
     * 학생 행 추가 처리 (마지막 행 뒤에 추가)
     */
    private handleAddStudentRow(): void {
        this.studentTableManager.addStudentRow();
    }

    /**
     * 학생 테이블 통계 업데이트
     */
    private updateStudentTableStats(): void {
        this.uiManager.updateStudentTableStats();
    }

    /**
     * 학생 정보 입력 테이블 저장 처리
     * 테이블의 학생 수를 계산하여 1단계 사이드바에 반영하고 미리보기를 업데이트
     * 그리고 localStorage에 학생 데이터를 저장
     */
    private handleSaveStudentTable(): void {
        const outputSection = document.getElementById('output-section');
        const rows = outputSection?.querySelectorAll('.student-input-table tbody tr') || [];
        
        let maleCount = 0;
        let femaleCount = 0;
        const studentData: Array<{name: string, gender: 'M' | 'F', fixedSeatId?: number}> = [];

        rows.forEach((row) => {
            const nameInput = row.querySelector('.student-name-input') as HTMLInputElement;
            const genderSelect = row.querySelector('.student-gender-select') as HTMLSelectElement;
            const fixedSeatSelect = row.querySelector('.fixed-seat-select') as HTMLSelectElement;
            
            if (nameInput && genderSelect) {
                const name = nameInput.value.trim();
                const gender = genderSelect.value as 'M' | 'F';
                
                if (name && gender) {
                    if (gender === 'M') {
                        maleCount++;
                    } else if (gender === 'F') {
                        femaleCount++;
                    }
                    
                    const student: {name: string, gender: 'M' | 'F', fixedSeatId?: number} = { name, gender };
                    
                    // 고정 좌석 정보가 있으면 추가
                    if (fixedSeatSelect && fixedSeatSelect.value) {
                        const fixedSeatId = parseInt(fixedSeatSelect.value, 10);
                        if (!isNaN(fixedSeatId)) {
                            student.fixedSeatId = fixedSeatId;
                        }
                    }
                    
                    studentData.push(student);
                }
            }
        });

        // localStorage에 학생 데이터 저장
        // safeSetItem은 예외를 던지지 않고 false를 반환하므로 반환값을 반드시 확인해야 한다.
        // 확인하지 않으면 저장 실패 시에도 아래에서 '등록되었습니다' 메시지가 표시된다.
        try {
            const studentDataSaved = this.storageManager.safeSetItem('classStudentData', JSON.stringify(studentData));
            if (!studentDataSaved) {
                logger.error('학생 데이터 저장 실패', { count: studentData.length });
                this.outputModule.showError('학생 명단을 저장하지 못했습니다. 브라우저 저장소 설정을 확인해주세요.');
                return;
            }
        } catch (error) {
            logger.error('학생 데이터 저장 중 오류:', error);
            this.outputModule.showError('학생 데이터 저장 중 오류가 발생했습니다.');
            return;
        }

        // 테이블의 학생 수를 1단계 사이드바로 동기화
        this.syncSidebarToTable(maleCount, femaleCount);
        
        this.outputModule.showSuccess(`우리반 학생 ${studentData.length}명이 등록되었습니다!`);
    }

    /**
     * 테이블의 숫자를 1단계 사이드바로 동기화
     * 테이블에 실제 입력된 학생 수를 1단계 입력 필드에 반영하고 미리보기를 업데이트
     */
    private syncSidebarToTable(tableMaleCount: number, tableFemaleCount: number): void {
        this.isSyncing = true; // 동기화 시작
        
        const maleCountInput = document.getElementById('male-students') as HTMLInputElement;
        const femaleCountInput = document.getElementById('female-students') as HTMLInputElement;
        
        if (!maleCountInput || !femaleCountInput) {
            this.outputModule.showError('입력 필드를 찾을 수 없습니다.');
            this.isSyncing = false;
            return;
        }

        // 1단계 입력 필드 업데이트
        maleCountInput.value = tableMaleCount.toString();
        femaleCountInput.value = tableFemaleCount.toString();
        
        // 입력 필드 값 변경 이벤트 수동 발생 (이벤트 리스너가 제대로 작동하도록)
        // 단, 통계 업데이트는 호출하지 않도록 (무한 루프 방지)
        maleCountInput.dispatchEvent(new Event('input', { bubbles: true }));
        femaleCountInput.dispatchEvent(new Event('input', { bubbles: true }));
        maleCountInput.dispatchEvent(new Event('change', { bubbles: true }));
        femaleCountInput.dispatchEvent(new Event('change', { bubbles: true }));
        
        // 미리보기 업데이트 (카드 재생성) - 명시적으로 호출
        this.updatePreviewForGenderCounts();
        
        // 통계 업데이트 (경고 메시지 제거) - 동기화 플래그를 해제하기 전에
        this.setTimeoutSafe(() => {
            this.updateStudentTableStats();
            this.isSyncing = false; // 동기화 완료
        }, 100);
    }

    /**
     * 우리 반 이름 불러오기 처리
     * localStorage에 저장된 학생 데이터를 테이블에 로드
     */
    private handleLoadClassNames(): void {
        this.studentTableManager.loadClassNames();
    }

    /**
     * 저장된 학생 데이터를 테이블에 로드
     */
    private loadStudentDataToTable(studentData: Array<{name: string, gender: 'M' | 'F', fixedSeatId?: number}>): void {
        const outputSection = document.getElementById('output-section');
        if (!outputSection) return;

        // 기존 테이블이 없으면 새로 생성
        let studentTableContainer = outputSection.querySelector('.student-table-container') as HTMLElement | null;
        if (!studentTableContainer) {
            this.studentTableManager.createStudentTable(studentData.length);
            // 테이블이 생성될 때까지 잠시 대기
            this.setTimeoutSafe(() => {
                this.csvFileHandler.loadStudentDataToTable(studentData);
            }, 100);
            return;
        }

        // 모든 테이블의 tbody 가져오기
        const allTbodies = outputSection.querySelectorAll('.student-input-table tbody');
        if (allTbodies.length === 0) {
            // 테이블이 없으면 새로 생성
            this.studentTableManager.createStudentTable(studentData.length);
            this.setTimeoutSafe(() => {
                this.csvFileHandler.loadStudentDataToTable(studentData);
            }, 100);
            return;
        }

        // 학생 수에 따라 10명씩 그룹화
        const studentsPerTable = 10;

        // 각 테이블의 tbody에 데이터 로드
        allTbodies.forEach((tbody, tableIndex) => {
            // 기존 행 모두 제거
            tbody.innerHTML = '';

            const startIndex = tableIndex * studentsPerTable;
            const endIndex = Math.min(startIndex + studentsPerTable, studentData.length);

            // 이 테이블에 해당하는 학생 데이터
            for (let i = startIndex; i < endIndex; i++) {
                const student = studentData[i];
                const globalIndex = i + 1; // 전체 학생 번호 (1부터 시작)

                const row = document.createElement('tr');
                row.dataset.studentIndex = i.toString();

                // 행 번호 셀
                const numCell = document.createElement('td');
                numCell.className = 'row-number';
                numCell.textContent = globalIndex.toString();
                numCell.style.textAlign = 'center';
                numCell.style.padding = '10px';
                numCell.style.background = '#f8f9fa';
                row.appendChild(numCell);

                // 이름 입력 셀
                const nameCell = document.createElement('td');
                const nameInput = document.createElement('input');
                nameInput.type = 'text';
                nameInput.className = 'student-name-input';
                nameInput.id = `student-name-${globalIndex}`;
                nameInput.value = student.name;
                nameInput.placeholder = '학생 이름';
                nameCell.appendChild(nameInput);
                row.appendChild(nameCell);

                // 성별 선택 셀
                const genderCell = document.createElement('td');
                const genderSelect = document.createElement('select');
                genderSelect.className = 'student-gender-select';
                genderSelect.id = `student-gender-${globalIndex}`;
                genderSelect.innerHTML = `
                    <option value="">선택</option>
                    <option value="M" ${student.gender === 'M' ? 'selected' : ''}>남</option>
                    <option value="F" ${student.gender === 'F' ? 'selected' : ''}>여</option>
                `;
                genderCell.appendChild(genderSelect);
                row.appendChild(genderCell);

                // 고정 좌석 선택 셀 (고정 좌석 모드인지 확인)
                const fixedRandomMode = document.querySelector('input[name="custom-mode-2"][value="fixed-random"]:checked') as HTMLInputElement;
                if (fixedRandomMode) {
                    const fixedSeatCell = document.createElement('td');
                    const fixedSeatSelect = document.createElement('select');
                    fixedSeatSelect.className = 'fixed-seat-select';
                    fixedSeatSelect.id = `fixed-seat-${globalIndex}`;
                    fixedSeatSelect.innerHTML = '<option value="">없음</option>';
                    
                    // 고정된 좌석이 있으면 옵션 추가
                    if (this.fixedSeatIds.size > 0) {
                        this.fixedSeatIds.forEach(seatId => {
                            const option = document.createElement('option');
                            option.value = seatId.toString();
                            option.textContent = `좌석 #${seatId}`;
                            if (student.fixedSeatId === seatId) {
                                option.selected = true;
                            }
                            fixedSeatSelect.appendChild(option);
                        });
                    }

                    // 고정 좌석 선택 변경 이벤트
                    this.addEventListenerSafe(fixedSeatSelect, 'change', () => {
                        const selectedSeatId = fixedSeatSelect.value;
                        const studentIndex = parseInt(row.dataset.studentIndex || '0', 10);
                        
                        // 학생 데이터에 고정 좌석 ID 저장
                        if (this.students[studentIndex]) {
                            if (selectedSeatId) {
                                this.students[studentIndex].fixedSeatId = parseInt(selectedSeatId, 10);
                            } else {
                                delete this.students[studentIndex].fixedSeatId;
                            }
                        }
                        
                        // 번호 셀 배경색 변경
                        if (numCell) {
                            if (selectedSeatId) {
                                numCell.style.background = '#667eea';
                                numCell.style.color = 'white';
                                numCell.style.fontWeight = 'bold';
                            } else {
                                numCell.style.background = '#f8f9fa';
                                numCell.style.color = '';
                                numCell.style.fontWeight = '';
                            }
                        }
                    });

                    // 고정 좌석이 있으면 번호 셀 배경색 설정
                    if (student.fixedSeatId !== undefined) {
                        numCell.style.background = '#667eea';
                        numCell.style.color = 'white';
                        numCell.style.fontWeight = 'bold';
                    }

                    fixedSeatCell.appendChild(fixedSeatSelect);
                    row.appendChild(fixedSeatCell);
                }

                // 삭제 버튼 셀
                const deleteCell = document.createElement('td');
                deleteCell.style.textAlign = 'center';
                deleteCell.style.padding = '8px';
                const deleteBtn = document.createElement('button');
                deleteBtn.className = 'delete-row-btn';
                deleteBtn.textContent = '🗑️';
                deleteBtn.type = 'button';
                deleteBtn.title = '삭제';
                deleteBtn.onclick = () => this.handleDeleteStudentRow(row);
                deleteCell.appendChild(deleteBtn);
                row.appendChild(deleteCell);

                tbody.appendChild(row);
            }
        });

        // 고정 좌석 드롭다운 업데이트
        this.studentTableManager.updateFixedSeatDropdowns();

        // 통계 업데이트
        this.updateStudentTableStats();

        // 사이드바 동기화
        const maleCount = studentData.filter(s => s.gender === 'M').length;
        const femaleCount = studentData.filter(s => s.gender === 'F').length;
        this.syncSidebarToTable(maleCount, femaleCount);

        this.outputModule.showSuccess(`우리반 학생 ${studentData.length}명을 불러왔습니다!`);
    }

    /**
     * 1단계 사이드바 값을 테이블로 동기화
     * 1단계에 입력된 숫자에 맞춰 테이블에 행을 추가하거나 삭제
     */
    private syncTableToSidebar(sidebarMaleCount: number, sidebarFemaleCount: number): void {
        const outputSection = document.getElementById('output-section');
        const tbody = outputSection?.querySelector('.student-input-table tbody');
        if (!tbody) {
            this.outputModule.showError('테이블을 찾을 수 없습니다.');
            return;
        }

        const rows = Array.from(tbody.querySelectorAll('tr')) as HTMLTableRowElement[];
        const totalNeeded = sidebarMaleCount + sidebarFemaleCount;
        const currentTotal = rows.length;

        // 현재 행들의 성별 카운트
        let currentMaleCount = 0;
        let currentFemaleCount = 0;
        
        rows.forEach(row => {
            const genderSelect = row.querySelector('.student-gender-select') as HTMLSelectElement;
            if (genderSelect) {
                if (genderSelect.value === 'M') {
                    currentMaleCount++;
                } else if (genderSelect.value === 'F') {
                    currentFemaleCount++;
                }
            }
        });

        // 행 수 조정 (부족하면 추가, 많으면 삭제)
        if (currentTotal < totalNeeded) {
            // 행 추가 필요
            const maleToAdd = Math.max(0, sidebarMaleCount - currentMaleCount);
            const femaleToAdd = Math.max(0, sidebarFemaleCount - currentFemaleCount);
            
            // 남학생 행 먼저 추가
            for (let i = 0; i < maleToAdd; i++) {
                this.studentTableManager.addStudentRow();
                // 추가된 행의 성별을 남자로 설정
                const newRows = Array.from(tbody.querySelectorAll('tr')) as HTMLTableRowElement[];
                const lastRow = newRows[newRows.length - 1];
                const genderSelect = lastRow.querySelector('.student-gender-select') as HTMLSelectElement;
                if (genderSelect) {
                    genderSelect.value = 'M';
                }
            }
            
            // 여학생 행 추가
            for (let i = 0; i < femaleToAdd; i++) {
                this.studentTableManager.addStudentRow();
                // 추가된 행의 성별을 여자로 설정
                const newRows = Array.from(tbody.querySelectorAll('tr')) as HTMLTableRowElement[];
                const lastRow = newRows[newRows.length - 1];
                const genderSelect = lastRow.querySelector('.student-gender-select') as HTMLSelectElement;
                if (genderSelect) {
                    genderSelect.value = 'F';
                }
            }
        } else if (currentTotal > totalNeeded) {
            // 행 삭제 필요 (맨 아래부터 삭제)
            const toDelete = currentTotal - totalNeeded;
            const rowsToDelete = Array.from(tbody.querySelectorAll('tr')) as HTMLTableRowElement[];
            
            // 맨 아래 행부터 삭제
            for (let i = 0; i < toDelete; i++) {
                const lastRow = rowsToDelete[rowsToDelete.length - 1 - i];
                if (lastRow) {
                    lastRow.remove();
                }
            }
            
            // 행 번호 재정렬
            this.updateRowNumbers();
        }

        // 성별 재분배 (필요한 경우)
        const finalRows = Array.from(tbody.querySelectorAll('tr')) as HTMLTableRowElement[];
        let currentMales = 0;
        let currentFemales = 0;
        
        finalRows.forEach(row => {
            const genderSelect = row.querySelector('.student-gender-select') as HTMLSelectElement;
            if (genderSelect) {
                if (genderSelect.value === 'M') {
                    currentMales++;
                } else if (genderSelect.value === 'F') {
                    currentFemales++;
                }
            }
        });

        // 성별이 맞지 않으면 조정
        if (currentMales !== sidebarMaleCount || currentFemales !== sidebarFemaleCount) {
            let maleNeeded = sidebarMaleCount - currentMales;
            let femaleNeeded = sidebarFemaleCount - currentFemales;
            
            finalRows.forEach(row => {
                const genderSelect = row.querySelector('.student-gender-select') as HTMLSelectElement;
                if (!genderSelect) return;
                
                if (maleNeeded > 0 && genderSelect.value !== 'M') {
                    genderSelect.value = 'M';
                    maleNeeded--;
                    if (genderSelect.value === 'F') femaleNeeded++;
                } else if (femaleNeeded > 0 && genderSelect.value !== 'F') {
                    genderSelect.value = 'F';
                    femaleNeeded--;
                    if (genderSelect.value === 'M') maleNeeded++;
                }
            });
        }

        // 통계 업데이트
        this.updateStudentTableStats();
        
        // 완료 메시지 표시
        this.outputModule.showInfo(`테이블이 1단계 입력 값에 맞춰 업데이트되었습니다. (남: ${sidebarMaleCount}명, 여: ${sidebarFemaleCount}명)`);
    }

    /**
     * 행 번호 업데이트
     */
    private updateRowNumbers(): void {
        const outputSection = document.getElementById('output-section');
        const rows = outputSection?.querySelectorAll('.student-input-table tbody tr');
        if (!rows) return;

        rows.forEach((row) => {
            const htmlRow = row as HTMLTableRowElement;
            const numCell = htmlRow.querySelector('td:first-child');
            if (numCell) {
                const rowIndex = Array.from(rows).indexOf(htmlRow);
                numCell.textContent = (rowIndex + 1).toString();
                htmlRow.dataset.studentIndex = rowIndex.toString();
            }
        });
    }


    /**
     * 배치 결과 섹션 생성
     */
    private createLayoutResultSection(outputSection: HTMLElement, students: Array<{name: string, gender: 'M' | 'F'}>): void {
        // 기존 배치 결과 제거
        let layoutResultSection = outputSection.querySelector('.layout-result-section');
        if (layoutResultSection) {
            layoutResultSection.remove();
        }

        // 새 배치 결과 섹션 생성
        layoutResultSection = document.createElement('div');
        layoutResultSection.className = 'layout-result-section';
        
        const title = document.createElement('h3');
        title.textContent = '자리 배치도 생성';
        title.style.marginTop = '30px';
        title.style.marginBottom = '15px';
        title.style.color = '#333';
        layoutResultSection.appendChild(title);

        // 캔버스 컨테이너 생성
        const canvasContainer = document.createElement('div');
        canvasContainer.id = 'dynamic-canvas-container';
        canvasContainer.style.background = '#f8f9fa';
        canvasContainer.style.border = '3px solid #667eea';
        canvasContainer.style.borderRadius = '10px';
        canvasContainer.style.padding = '20px';
        canvasContainer.style.display = 'flex';
        canvasContainer.style.justifyContent = 'center';
        canvasContainer.style.alignItems = 'center';
        canvasContainer.style.marginBottom = '20px';
        canvasContainer.style.minHeight = '450px';

        const canvas = document.createElement('canvas');
        canvas.id = 'dynamic-seat-canvas';
        canvas.width = 800;
        canvas.height = 600;
        canvas.style.background = 'white';
        canvas.style.border = '2px solid #667eea';
        canvas.style.borderRadius = '5px';
        canvas.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';

        canvasContainer.appendChild(canvas);
        layoutResultSection.appendChild(canvasContainer);

        outputSection.appendChild(layoutResultSection);

        // 배치 미리보기 렌더링
        this.renderStudentLayout(students);
    }

    /**
     * 학생 배치 미리보기 렌더링
     */
    private renderStudentLayout(students: Array<{name: string, gender: 'M' | 'F'}>): void {
        const layoutType = this.layoutSelectorModule.getCurrentLayoutType();
        
        if (!layoutType) {
            return;
        }

        const canvas = document.getElementById('dynamic-seat-canvas') as HTMLCanvasElement;
        if (!canvas) return;

        // 분단 수 가져오기
        const partitionInput = document.getElementById('number-of-partitions') as HTMLInputElement;
        const partitionCount = partitionInput ? parseInt(partitionInput.value || '1', 10) : 1;
        
        // 레이아웃 생성
        const layoutResult = LayoutService.createLayout(
            layoutType,
            students.length,
            canvas.width,
            canvas.height,
            partitionCount
        );

        if (layoutResult.success && layoutResult.seats) {
            this.seats = layoutResult.seats;
            
            // 캔버스에 그리기
            const ctx = canvas.getContext('2d');
            if (ctx) {
                // 캔버스 클리어
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                
                // 배경 설정
                ctx.fillStyle = '#f8f9fa';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                
                // 교탁과 칠판 그리기
                this.drawTeacherDeskAndBoard(ctx, canvas);
                
                // 학생 이름과 함께 좌석 그리기
                this.seats.forEach((seat, index) => {
                    if (index < students.length) {
                        this.drawSeatWithStudent(ctx, seat, students[index]);
                    }
                });
            }
        }
    }

    /**
     * 학생 정보와 함께 좌석 그리기
     */
    private drawSeatWithStudent(ctx: CanvasRenderingContext2D, seat: Seat, student: {name: string, gender: 'M' | 'F'}): void {
        const x = seat.position.x;
        const y = seat.position.y + 100; // 교탁 공간 확보
        const width = 50;
        const height = 50;
        
        // 좌석 색상 (성별에 따라)
        ctx.fillStyle = student.gender === 'M' ? '#e3f2fd' : '#fce4ec';
        ctx.fillRect(x, y, width, height);
        
        // 좌석 테두리
        ctx.strokeStyle = '#999';
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, width, height);
        
        // 학생 이름 표시
        ctx.fillStyle = '#333';
        ctx.font = '9px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(student.name, x + width / 2, y + height / 2);
        
        // 성별 표시
        ctx.fillStyle = '#666';
        ctx.font = '8px sans-serif';
        ctx.fillText(student.gender === 'M' ? '♂' : '♀', x + width / 2, y + height - 5);
    }


    /**
     * 셀 간 이동 처리
     * @param tbody tbody 요소
     * @param currentRow 현재 행 번호 (1부터 시작)
     * @param columnName 열 이름 ('name' 또는 'gender')
     * @param direction 이동 방향 ('up' 또는 'down')
     */
    private moveToCell(tbody: HTMLTableSectionElement, currentRow: number, columnName: string, direction: 'up' | 'down'): void {
        const nextRowNum = direction === 'down' ? currentRow + 1 : currentRow - 1;
        const nextRow = tbody.querySelector(`tr:nth-child(${nextRowNum})`);
        
        if (nextRow) {
            const cellInput = columnName === 'name' 
                ? nextRow.querySelector('.student-name-input') as HTMLInputElement
                : nextRow.querySelector('.student-gender-select') as HTMLSelectElement;
            
            if (cellInput) {
                cellInput.focus();
                if (cellInput instanceof HTMLInputElement) {
                    cellInput.select();
                }
            }
        }
    }

    /**
     * 학생 데이터로 테이블 생성 (handleCreateStudentTable에서 사용)
     * @param students 학생 배열
     */
    private createTableWithStudents(students: Array<{name: string, gender: 'M' | 'F'}>): void {
        const outputSection = document.getElementById('output-section');
        if (!outputSection) return;

        // 기존 캔버스 숨기기
        const canvasContainer = outputSection.querySelector('#canvas-container');
        if (canvasContainer) {
            (canvasContainer as HTMLElement).style.display = 'none';
        }

        // 기존 테이블 제거
        let studentTableContainer = outputSection.querySelector('.student-table-container') as HTMLElement | null;
        if (studentTableContainer) {
            studentTableContainer.remove();
        }

        // 새 테이블 컨테이너 생성
        studentTableContainer = document.createElement('div');
        studentTableContainer.className = 'student-table-container';
        // 가로 방향 2-3단 레이아웃을 위한 스타일 적용
        studentTableContainer.style.cssText = `
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 20px;
            margin-bottom: 20px;
        `;

        // 버튼 컨테이너 생성
        const buttonContainer = document.createElement('div') as HTMLElement;
        buttonContainer.style.display = 'flex';
        buttonContainer.style.gap = '10px';
        buttonContainer.style.marginBottom = '15px';
        buttonContainer.style.gridColumn = '1 / -1';
        
        // 양식 다운로드 버튼
        const downloadBtn = document.createElement('button');
        downloadBtn.id = 'download-template';
        downloadBtn.className = 'secondary-btn';
        downloadBtn.textContent = '학생 이름 양식 다운로드';
        downloadBtn.style.flex = 'none';
        downloadBtn.style.width = 'auto';
        downloadBtn.style.whiteSpace = 'nowrap';
        this.addEventListenerSafe(downloadBtn, 'click', () => this.csvFileHandler.downloadTemplateFile());
        buttonContainer.appendChild(downloadBtn);
        
        // 파일 업로드 버튼
        const uploadBtn = document.createElement('button');
        uploadBtn.id = 'upload-file';
        uploadBtn.className = 'secondary-btn';
        uploadBtn.textContent = '학생 이름 엑셀파일에서 가져오기';
        uploadBtn.style.flex = 'none';
        uploadBtn.style.width = 'auto';
        uploadBtn.style.whiteSpace = 'nowrap';
        
        // 숨겨진 파일 입력
        const fileInput = document.createElement('input');
        fileInput.id = 'upload-file-input';
        fileInput.type = 'file';
        fileInput.accept = '.csv,.xlsx,.xls';
        fileInput.style.display = 'none';
        this.addEventListenerSafe(fileInput, 'change', (e) => this.csvFileHandler.handleFileUpload(e));
        
        this.addEventListenerSafe(uploadBtn, 'click', () => {
            fileInput.click();
        });
        
        buttonContainer.appendChild(uploadBtn);
        buttonContainer.appendChild(fileInput);
        
        // 우리 반 이름 불러오기 버튼
        const loadClassBtn3 = document.createElement('button');
        loadClassBtn3.id = 'load-class-names-3';
        loadClassBtn3.className = 'secondary-btn';
        loadClassBtn3.textContent = '우리 반 이름 불러오기';
        loadClassBtn3.style.flex = 'none';
        loadClassBtn3.style.width = 'auto';
        loadClassBtn3.style.whiteSpace = 'nowrap';
        this.addEventListenerSafe(loadClassBtn3, 'click', () => this.handleLoadClassNames());
        buttonContainer.appendChild(loadClassBtn3);
        
        // 자리 배치하기 버튼과 체크박스 추가
        const arrangeBtn = document.createElement('button');
        arrangeBtn.id = 'arrange-seats';
        arrangeBtn.className = 'arrange-seats-btn';
        arrangeBtn.textContent = '자리 배치 실행하기';
        arrangeBtn.style.width = 'auto';
        arrangeBtn.style.flex = 'none';
        arrangeBtn.style.whiteSpace = 'nowrap';
        buttonContainer.appendChild(arrangeBtn);
        
        // 이전 좌석 안 앉기 체크박스
        const avoidPrevSeatLabel = document.createElement('label');
        avoidPrevSeatLabel.style.cssText = 'display:flex; align-items:center; gap:4px; margin:0; white-space:nowrap;';
        const avoidPrevSeatInput = document.createElement('input');
        avoidPrevSeatInput.type = 'checkbox';
        avoidPrevSeatInput.id = 'avoid-prev-seat';
        const avoidPrevSeatSpan = document.createElement('span');
        avoidPrevSeatSpan.textContent = '이전 좌석 안 앉기';
        avoidPrevSeatLabel.appendChild(avoidPrevSeatInput);
        avoidPrevSeatLabel.appendChild(avoidPrevSeatSpan);
        buttonContainer.appendChild(avoidPrevSeatLabel);
        
        // 이전 짝 금지 체크박스
        const avoidPrevPartnerLabel = document.createElement('label');
        avoidPrevPartnerLabel.style.cssText = 'display:flex; align-items:center; gap:4px; margin:0; white-space:nowrap;';
        const avoidPrevPartnerInput = document.createElement('input');
        avoidPrevPartnerInput.type = 'checkbox';
        avoidPrevPartnerInput.id = 'avoid-prev-partner';
        const avoidPrevPartnerSpan = document.createElement('span');
        avoidPrevPartnerSpan.textContent = '이전 짝 금지';
        avoidPrevPartnerLabel.appendChild(avoidPrevPartnerInput);
        avoidPrevPartnerLabel.appendChild(avoidPrevPartnerSpan);
        buttonContainer.appendChild(avoidPrevPartnerLabel);
        
        studentTableContainer.appendChild(buttonContainer);

        const count = students.length;
        const fixedRandomMode = document.querySelector('input[name="custom-mode-2"][value="fixed-random"]:checked') as HTMLInputElement;
        
        // 학생 수에 따라 테이블 개수 결정 (10명씩 그룹화)
        const studentsPerTable = 10;
        const numberOfTables = Math.ceil(count / studentsPerTable);
        
        // 각 테이블 생성 (10명씩)
        for (let tableIndex = 0; tableIndex < numberOfTables; tableIndex++) {
            const startIndex = tableIndex * studentsPerTable;
            const endIndex = Math.min(startIndex + studentsPerTable, count);
            const studentsInThisTable = endIndex - startIndex;
            
            // 개별 테이블 래퍼 생성
            const tableWrapper = document.createElement('div');
            tableWrapper.style.cssText = `
                border: 1px solid #dee2e6;
                border-radius: 8px;
                padding: 15px;
                background: white;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                min-width: 0; /* 그리드 아이템이 축소될 수 있도록 */
                overflow-x: auto; /* 테이블이 너무 넓으면 가로 스크롤 */
            `;
            
            // 테이블 제목 추가 (2개 이상일 때만)
            if (numberOfTables > 1) {
                const tableTitle = document.createElement('div');
                tableTitle.style.cssText = `
                    font-weight: bold;
                    margin-bottom: 10px;
                    color: #495057;
                    font-size: 1.1em;
                    padding-bottom: 8px;
                    border-bottom: 2px solid #dee2e6;
                `;
                tableTitle.textContent = `${startIndex + 1}번 ~ ${endIndex}번`;
                tableWrapper.appendChild(tableTitle);
            }

            // 테이블 생성
            const table = document.createElement('table');
            table.className = 'student-input-table';
            table.style.cssText = `
                width: 100%;
                border-collapse: collapse;
            `;
            
            // 헤더 생성
            const thead = document.createElement('thead');
            const headerRow = document.createElement('tr');
            
            if (fixedRandomMode) {
                headerRow.innerHTML = `
                    <th>번호</th>
                    <th>이름</th>
                    <th>성별</th>
                    <th title="미리보기 화면의 좌석 카드에 표시된 번호(#1, #2...)를 선택하세요. 고정 좌석을 지정하지 않으려면 '없음'을 선택하세요.">고정 좌석</th>
                    <th>작업</th>
                `;
            } else {
                headerRow.innerHTML = `
                    <th>번호</th>
                    <th>이름</th>
                    <th>성별</th>
                    <th>작업</th>
                `;
            }
            thead.appendChild(headerRow);
            table.appendChild(thead);

            // 본문 생성
            const tbody = document.createElement('tbody');
            for (let i = startIndex; i < endIndex; i++) {
                const student = students[i];
                const globalIndex = i + 1; // 전체 학생 중 인덱스 (1부터 시작)
                const localIndex = i - startIndex + 1; // 현재 테이블 내에서의 인덱스 (1부터 시작)
                
                const row = document.createElement('tr');
                row.dataset.studentIndex = i.toString();
                
                // 번호 열
                const numCell = document.createElement('td');
                numCell.textContent = globalIndex.toString();
                numCell.style.textAlign = 'center';
                numCell.style.padding = '10px';
                numCell.style.background = '#f8f9fa';
                
                // 이름 입력 열
                const nameCell = document.createElement('td');
                const nameInput = document.createElement('input');
                nameInput.type = 'text';
                nameInput.placeholder = '학생 이름';
                nameInput.className = 'student-name-input';
                nameInput.value = student.name;
                nameInput.id = `student-name-${globalIndex}`;
                nameInput.tabIndex = globalIndex;
                nameCell.appendChild(nameInput);
                
                // 성별 선택 열
                const genderCell = document.createElement('td');
                const genderSelect = document.createElement('select');
                genderSelect.className = 'student-gender-select';
                genderSelect.id = `student-gender-${globalIndex}`;
                genderSelect.innerHTML = `
                    <option value="">선택</option>
                    <option value="M">남</option>
                    <option value="F">여</option>
                `;
                genderSelect.value = student.gender;
                genderSelect.tabIndex = count + globalIndex;
                genderCell.appendChild(genderSelect);
                
                // 고정 좌석 선택 열 (고정 좌석 모드일 때만)
                let fixedSeatCell: HTMLTableCellElement | null = null;
                if (fixedRandomMode) {
                    fixedSeatCell = document.createElement('td');
                    const fixedSeatSelect = document.createElement('select');
                    fixedSeatSelect.className = 'fixed-seat-select';
                    fixedSeatSelect.id = `fixed-seat-${globalIndex}`;
                    fixedSeatSelect.innerHTML = '<option value="">없음</option>';
                    fixedSeatSelect.tabIndex = count * 2 + globalIndex;
                    
                    // 고정된 좌석이 있으면 옵션 추가
                    if (this.fixedSeatIds.size > 0) {
                        this.fixedSeatIds.forEach(seatId => {
                            const option = document.createElement('option');
                            option.value = seatId.toString();
                            option.textContent = `좌석 #${seatId}`;
                            fixedSeatSelect.appendChild(option);
                        });
                    }
                    
                    // 학생 데이터에 저장된 고정 좌석이 있으면 선택
                    const studentIndex = parseInt(row.dataset.studentIndex || '0', 10);
                    if (this.students[studentIndex] && this.students[studentIndex].fixedSeatId) {
                        fixedSeatSelect.value = this.students[studentIndex].fixedSeatId.toString();
                        // 번호 셀 배경색 설정 (초기 상태)
                        if (numCell) {
                            numCell.style.background = '#667eea';
                            numCell.style.color = 'white';
                            numCell.style.fontWeight = 'bold';
                        }
                    }
                    
                    // 고정 좌석 선택 변경 이벤트
                    this.addEventListenerSafe(fixedSeatSelect, 'change', () => {
                        const selectedSeatId = fixedSeatSelect.value;
                        const studentIndex = parseInt(row.dataset.studentIndex || '0', 10);
                        
                        // 학생 데이터에 고정 좌석 ID 저장
                        if (this.students[studentIndex]) {
                            if (selectedSeatId) {
                                this.students[studentIndex].fixedSeatId = parseInt(selectedSeatId, 10);
                            } else {
                                delete this.students[studentIndex].fixedSeatId;
                            }
                        }
                        
                        // 번호 셀 배경색 변경
                        const numCell = row.querySelector('td:first-child') as HTMLElement;
                        if (numCell) {
                            if (selectedSeatId) {
                                // 고정 좌석이 선택된 경우 파란색 배경
                                numCell.style.background = '#667eea';
                                numCell.style.color = 'white';
                                numCell.style.fontWeight = 'bold';
                            } else {
                                // 선택이 해제된 경우 원래 배경색으로 복원
                                numCell.style.background = '#f8f9fa';
                                numCell.style.color = '';
                                numCell.style.fontWeight = '';
                            }
                        }
                        
                        
                    });
                    
                    fixedSeatCell.appendChild(fixedSeatSelect);
                }
                
                // 작업 열 (삭제 버튼)
                const actionCell = document.createElement('td');
                actionCell.style.textAlign = 'center';
                actionCell.style.padding = '8px';
                const deleteBtn = document.createElement('button');
                deleteBtn.textContent = '🗑️'; // 삭제 아이콘
                deleteBtn.type = 'button';
                deleteBtn.className = 'delete-row-btn';
                deleteBtn.title = '삭제';
                deleteBtn.onclick = () => this.handleDeleteStudentRow(row);
                actionCell.appendChild(deleteBtn);

                // 키보드 이벤트 추가 (이름 입력 필드)
                this.addEventListenerSafe(nameInput, 'keydown', (e: Event) => {
                    const ke = e as KeyboardEvent;
                    if (ke.key === 'Enter') {
                        genderSelect.focus();
                    } else if (ke.key === 'ArrowDown') {
                        this.moveToCell(tbody, localIndex, 'name', 'down');
                    } else if (ke.key === 'ArrowUp') {
                        this.moveToCell(tbody, localIndex, 'name', 'up');
                    }
                });

                // 키보드 이벤트 추가 (성별 선택 필드)
                this.addEventListenerSafe(genderSelect, 'keydown', (e: Event) => {
                    const ke = e as KeyboardEvent;
                    if (ke.key === 'Enter' || ke.key === 'Tab') {
                        const nextRow = tbody.querySelector(`tr:nth-child(${Math.min(localIndex + 1, studentsInThisTable)})`);
                        const nextNameInput = nextRow?.querySelector('.student-name-input') as HTMLInputElement;
                        if (nextNameInput) {
                            nextNameInput.focus();
                            nextNameInput.select();
                        }
                    } else if (ke.key === 'ArrowDown') {
                        this.moveToCell(tbody, localIndex, 'gender', 'down');
                    } else if (ke.key === 'ArrowUp') {
                        this.moveToCell(tbody, localIndex, 'gender', 'up');
                    }
                });
                
                row.appendChild(numCell);
                row.appendChild(nameCell);
                row.appendChild(genderCell);
                if (fixedSeatCell) {
                    row.appendChild(fixedSeatCell);
                }
                row.appendChild(actionCell);
                
                tbody.appendChild(row);
            }
            
            table.appendChild(tbody);
            tableWrapper.appendChild(table);
            studentTableContainer.appendChild(tableWrapper);
        }
        
        // 통계와 버튼을 하나의 컨테이너로 묶기
        const statsAndButtonsWrapper = document.createElement('div');
        statsAndButtonsWrapper.style.cssText = `
            grid-column: 1 / -1;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 10px;
            margin-top: 10px;
            flex-wrap: wrap;
        `;
        
        // 통계 표시를 위한 컨테이너 추가 (모든 테이블 아래에 하나만)
        const statsContainer = document.createElement('div');
        statsContainer.style.cssText = `
            padding: 12px;
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            font-size: 0.95em;
            flex: 0 0 auto;
            width: fit-content;
        `;
        statsContainer.id = 'student-table-stats';
        const statsCell = document.createElement('div');
        statsCell.id = 'student-table-stats-cell';
        statsContainer.appendChild(statsCell);
        statsAndButtonsWrapper.appendChild(statsContainer);
        
        // 작업 버튼 추가
        const actionButtons = document.createElement('div');
        actionButtons.className = 'table-action-buttons';
        actionButtons.style.cssText = `
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            align-items: center;
            flex: 0 0 auto;
        `;
        actionButtons.innerHTML = `
            <button id="add-student-row-btn" style="width: auto; flex: 0 0 auto; min-width: 0;">행 추가</button>
            <button id="save-student-table-btn" class="save-btn" style="width: auto; flex: 0 0 auto; min-width: 0; background: #28a745; color: white; padding: 8px 16px; border: none; border-radius: 4px; cursor: pointer; font-weight: 500; white-space: nowrap;">✅ 우리반 학생으로 등록하기</button>
        `;
        statsAndButtonsWrapper.appendChild(actionButtons);
        studentTableContainer.appendChild(statsAndButtonsWrapper);
        
        outputSection.appendChild(studentTableContainer);
        
        // 초기 통계 업데이트
        this.updateStudentTableStats();
        
        // 통계 업데이트를 위한 이벤트 리스너 추가 (이벤트 위임으로 모든 변경사항 감지)
        // 모든 테이블의 tbody에 이벤트 리스너 추가
        const allTbodies = studentTableContainer.querySelectorAll('tbody');
        allTbodies.forEach(tbody => {
            this.addEventListenerSafe(tbody, 'input', () => {
                this.updateStudentTableStats();
            });
            this.addEventListenerSafe(tbody, 'change', () => {
                this.updateStudentTableStats();
            });
            
            // 테이블이 동적으로 변경될 때를 대비한 MutationObserver 추가
            const observer = new MutationObserver(() => {
                this.updateStudentTableStats();
            });
            observer.observe(tbody, {
                childList: true,
                subtree: true,
                attributes: false
            });
        });
    }

    /**
     * 칠판 그리기
     */
    private drawTeacherDeskAndBoard(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement): void {
        const width = canvas.width;
        
        // 칠판 그리기
        ctx.fillStyle = '#2c3e50';
        const boardY = 10;
        const boardHeight = 60;
        ctx.fillRect(width * 0.2, boardY, width * 0.6, boardHeight);
        
        // 칠판 테두리
        ctx.strokeStyle = '#1a252f';
        ctx.lineWidth = 2;
        ctx.strokeRect(width * 0.2, boardY, width * 0.6, boardHeight);
        
        // 칠판 텍스트
        ctx.fillStyle = '#ecf0f1';
        ctx.font = 'bold 18px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('칠판', width * 0.5, boardY + 40);
    }


    /**
     * 1명씩 한 줄로 배치 서브 메뉴 토글
     */
    private toggleSingleSubmenu(show: boolean): void {
        const singleSubmenu = document.getElementById('single-submenu');
        if (!singleSubmenu) return;
        
        if (show) {
            singleSubmenu.style.display = 'block';
        } else {
            singleSubmenu.style.display = 'none';
        }
    }

    private togglePairSubmenu(show: boolean): void {
        const pairSubmenu = document.getElementById('pair-submenu');
        if (!pairSubmenu) return;
        
        if (show) {
            pairSubmenu.style.display = 'block';
        } else {
            pairSubmenu.style.display = 'none';
        }
    }

    /**
     * 모둠 배치 서브 메뉴 토글
     */
    /**
     * '이전 짝 금지' 체크박스 활성화/비활성화
     */
    private toggleAvoidPrevPartnerOption(enable: boolean): void {
        const avoidPrevPartner = document.getElementById('avoid-prev-partner') as HTMLInputElement;
        if (avoidPrevPartner) {
            if (enable) {
                // 활성화
                avoidPrevPartner.disabled = false;
                avoidPrevPartner.style.opacity = '1';
                avoidPrevPartner.style.cursor = '';
            } else {
                // 비활성화 및 체크 해제
                avoidPrevPartner.disabled = true;
                avoidPrevPartner.checked = false;
                avoidPrevPartner.style.opacity = '0.5';
                avoidPrevPartner.style.cursor = 'not-allowed';
            }
        }
    }

    private togglePartitionSection(enable: boolean): void {
        const section = document.getElementById('partition-section');
        const input = document.getElementById('number-of-partitions') as HTMLInputElement | null;

        if (input) {
            input.disabled = !enable;
        }

        if (section) {
            section.classList.toggle('option-disabled', !enable);
            // 읽기 전용 안내를 스크린리더에도 전달한다
            section.setAttribute('aria-disabled', enable ? 'false' : 'true');
        }

        // 사용자 구성 배치에서는 분단이라는 개념이 없다는 것을 알려준다
        const note = document.getElementById('partition-disabled-note');
        if (note) {
            note.style.display = enable ? 'none' : 'block';
        }
    }

    private toggleGroupSubmenu(show: boolean): void {
        const groupSubmenu = document.getElementById('group-submenu');
        if (!groupSubmenu) return;
        
        if (show) {
            groupSubmenu.style.display = 'block';
        } else {
            groupSubmenu.style.display = 'none';
        }
    }

    /**
     * 모둠 배치 남녀 섞기 옵션 토글
     */
    private toggleGroupGenderMixOption(show: boolean): void {
        const genderMixOption = document.getElementById('group-gender-mix-option');
        if (!genderMixOption) return;
        
        if (show) {
            genderMixOption.style.display = 'block';
        } else {
            genderMixOption.style.display = 'none';
        }
    }

    /**
     * 모둠 배치 선택 시 분단 개수 제한 적용
     */
    private updatePartitionLimitForGroup(groupSize: string): void {
        const partitionInput = document.getElementById('number-of-partitions') as HTMLInputElement;
        if (!partitionInput) return;

        // 3명 모둠 배치 선택 시 3, 4, 5개 분단만 허용
        if (groupSize === 'group-3') {
            partitionInput.min = '3';
            partitionInput.max = '5';
            
            // 현재 값이 허용 범위를 벗어나면 조정
            const currentValue = parseInt(partitionInput.value, 10);
            if (currentValue < 3) {
                partitionInput.value = '3';
            } else if (currentValue > 5) {
                partitionInput.value = '5';
            }
        }
        // 4명 모둠 배치 선택 시 3, 4개 분단만 허용
        else if (groupSize === 'group-4') {
            partitionInput.min = '3';
            partitionInput.max = '4';
            
            // 현재 값이 허용 범위를 벗어나면 조정
            const currentValue = parseInt(partitionInput.value, 10);
            if (currentValue < 3) {
                partitionInput.value = '3';
            } else if (currentValue > 4) {
                partitionInput.value = '4';
            }
        }
        // 5명 모둠 배치 선택 시 3, 4, 5개 분단만 허용
        else if (groupSize === 'group-5') {
            partitionInput.min = '3';
            partitionInput.max = '5';
            
            // 현재 값이 허용 범위를 벗어나면 조정
            const currentValue = parseInt(partitionInput.value, 10);
            if (currentValue < 3) {
                partitionInput.value = '3';
            } else if (currentValue > 5) {
                partitionInput.value = '5';
            }
        }
        // 6명 모둠 배치 선택 시 2, 3, 4개 분단만 허용
        else if (groupSize === 'group-6') {
            partitionInput.min = '2';
            partitionInput.max = '4';
            
            // 현재 값이 허용 범위를 벗어나면 조정
            const currentValue = parseInt(partitionInput.value, 10);
            if (currentValue < 2) {
                partitionInput.value = '2';
            } else if (currentValue > 4) {
                partitionInput.value = '4';
            }
        } else {
            // 다른 모둠 배치 옵션이면 제한 해제
            this.resetPartitionLimit();
        }
    }

    /**
     * 1명씩 한줄로 배치 선택 시 분단 개수 제한 적용 (3, 4, 5, 6만 허용)
     */
    private updatePartitionLimitForSingleUniform(): void {
        const partitionInput = document.getElementById('number-of-partitions') as HTMLInputElement;
        if (!partitionInput) return;

        partitionInput.min = '3';
        partitionInput.max = '6';
        
        // 현재 값이 허용 범위를 벗어나면 조정
        const currentValue = parseInt(partitionInput.value, 10);
        if (currentValue < 3) {
            partitionInput.value = '3';
        } else if (currentValue > 6) {
            partitionInput.value = '6';
        }
    }

    /**
     * 짝꿍 배치 선택 시 분단 개수 제한 적용 (3, 4, 5만 허용)
     */
    private updatePartitionLimitForPair(): void {
        const partitionInput = document.getElementById('number-of-partitions') as HTMLInputElement;
        if (!partitionInput) return;

        partitionInput.min = '3';
        partitionInput.max = '5';
        
        // 현재 값이 허용 범위를 벗어나면 조정
        const currentValue = parseInt(partitionInput.value, 10);
        if (currentValue < 3) {
            partitionInput.value = '3';
        } else if (currentValue > 5) {
            partitionInput.value = '5';
        }
    }

    /**
     * 분단 개수 제한 해제 (기본값으로 복원)
     */
    private resetPartitionLimit(): void {
        const partitionInput = document.getElementById('number-of-partitions') as HTMLInputElement;
        if (!partitionInput) return;

        partitionInput.min = '1';
        partitionInput.max = '10';
    }

    /**
     * 프로그램 실행
     */
    public run(): void {
        if (!this.isInitialized) {
            // 개발 모드에서만 에러 로깅
            logger.error('컨트롤러가 초기화되지 않았습니다.');
            return;
        }
    }
    
    /**
     * 개발 모드 확인 (로컬호스트 또는 개발 환경)
     */
    private isDevelopmentMode(): boolean {
        return window.location.hostname === 'localhost' || 
               window.location.hostname === '127.0.0.1' ||
               window.location.hostname.includes('dev');
    }
    
    /**
     * 안전한 클립보드 복사 (브라우저 호환성 개선)
     */
    private async copyToClipboard(text: string): Promise<boolean> {
        try {
            // 최신 Clipboard API 시도
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
                return true;
            }
        } catch (err) {
            // Clipboard API 실패 시 폴백 사용
        }
        
        // 폴백: document.execCommand 사용
        try {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.left = '-999999px';
            textarea.style.top = '-999999px';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            
            const successful = document.execCommand('copy');
            document.body.removeChild(textarea);
            return successful;
        } catch (err) {
            logger.error('클립보드 복사 실패:', err);
            return false;
        }
    }
            
    /**
     * 안전한 이벤트 리스너 추가 (메모리 누수 방지)
     * 향후 사용 예정
     */
    private addEventListenerSafe(element: EventTarget, event: string, handler: EventListener | ((e: Event) => void), options?: boolean | AddEventListenerOptions): void {
        element.addEventListener(event, handler as EventListener, options);
        this.eventListeners.push({ element, event, handler: handler as EventListener });
    }
    
    /**
     * 안전한 setTimeout (메모리 누수 방지)
     */
    private setTimeoutSafe(callback: () => void, delay: number): number {
        const timerId = window.setTimeout(() => {
            this.timers.delete(timerId);
            callback();
        }, delay);
        this.timers.add(timerId);
        return timerId;
    }
    
    /**
     * 모든 타이머 정리
     */
    private clearAllTimers(): void {
        this.timers.forEach(timerId => {
            clearTimeout(timerId);
        });
        this.timers.clear();
    }
    
    /**
     * 모든 이벤트 리스너 정리
     */
    private removeAllEventListeners(): void {
        this.eventListeners.forEach(({ element, event, handler }) => {
            element.removeEventListener(event, handler);
        });
        this.eventListeners = [];
    }
    
    /**
     * 정리 메서드 (컨트롤러 종료 시 호출)
     */
    public cleanup(): void {
        this.clearAllTimers();
        this.removeAllEventListeners();
    }

    /**
     * 모든 반과 명단을 파일 하나로 내보낸다.
     *
     * localStorage는 영구 저장소가 아니다. 브라우저 데이터 삭제, 학교 PC 초기화,
     * Safari의 자동 정리로 언제든 사라질 수 있다. 이 파일이 유일한 백업 수단이므로
     * 실패했을 때 조용히 넘어가서는 안 된다.
     */
    private handleExportBackup(): void {
        try {
            const backup = BackupService.buildBackup(
                {
                    read: (key) => this.storageManager.safeGetItem(key),
                    write: (key, value) => this.storageManager.safeSetItem(key, value)
                },
                new Date().toISOString()
            );

            if (backup.classes.length === 0 && !backup.studentRoster) {
                this.outputModule.showWarning('내보낼 자료가 없습니다. 반을 만들고 학생 명단을 입력한 뒤 다시 시도해주세요.');
                return;
            }

            const fileName = BackupService.buildFileName(backup.exportedAt);
            const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json;charset=utf-8' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // 브라우저가 내려받기를 시작할 시간을 준 뒤 해제한다
            this.setTimeoutSafe(() => URL.revokeObjectURL(url), 1000);

            const classCount = backup.classes.length;
            this.outputModule.showSuccess(
                `${classCount}개 반의 자료를 "${fileName}" 파일로 내보냈습니다. ` +
                'USB나 학교 드라이브에 보관해두시면 다른 기기에서도 불러올 수 있습니다.'
            );
            logger.info('백업 내보내기 완료:', { fileName, classCount });
        } catch (error) {
            logger.error('백업 내보내기 실패:', error);
            this.outputModule.showError('내보내기에 실패했습니다. 다시 시도해주세요.');
        }
    }

    /**
     * 백업 파일에서 반과 명단을 복원한다.
     *
     * 같은 반은 덮어쓰고, 백업에 없는 기존 반은 그대로 둔다.
     * 실수로 예전 백업을 불러왔을 때 최근 작업이 사라지지 않도록 하기 위해서다.
     */
    private handleImportBackup(event: Event): void {
        const input = event.target as HTMLInputElement | null;
        const file = input?.files?.[0];
        if (!file) return;

        // 같은 파일을 다시 선택해도 change 이벤트가 발생하도록 값을 비운다
        if (input) input.value = '';

        if (file.size > 20 * 1024 * 1024) {
            this.outputModule.showError('파일이 너무 큽니다. 20MB 이하의 백업 파일만 불러올 수 있습니다.');
            return;
        }

        const reader = new FileReader();

        reader.onerror = () => {
            this.outputModule.showError('파일을 읽는 중 오류가 발생했습니다.');
        };

        reader.onload = () => {
            try {
                const parsed = BackupService.parseBackup(String(reader.result ?? ''));
                if (!parsed.ok) {
                    this.outputModule.showError(parsed.errorMessage);
                    return;
                }

                const classCount = parsed.backup.classes.length;
                const exportedAt = parsed.backup.exportedAt
                    ? parsed.backup.exportedAt.slice(0, 10)
                    : '알 수 없음';

                const proceed = confirm(
                    `백업 파일에 ${classCount}개 반이 들어 있습니다. (내보낸 날짜: ${exportedAt})\n\n` +
                    '같은 이름의 반은 파일 내용으로 바뀌고, 파일에 없는 반은 그대로 남습니다.\n\n' +
                    '불러올까요?'
                );
                if (!proceed) return;

                const result = BackupService.applyBackup(
                    {
                        read: (key) => this.storageManager.safeGetItem(key),
                        write: (key, value) => this.storageManager.safeSetItem(key, value)
                    },
                    parsed.backup
                );

                if (!result.ok) {
                    this.outputModule.showError(result.errorMessage || '불러오기에 실패했습니다.');
                    return;
                }

                // 반 목록을 다시 그린다
                this.updateClassSelect();
                this.updateHistoryDropdown();

                const parts: string[] = [];
                if (result.added > 0) parts.push(`${result.added}개 반 추가`);
                if (result.replaced > 0) parts.push(`${result.replaced}개 반 갱신`);
                if (result.kept > 0) parts.push(`기존 ${result.kept}개 반 유지`);

                this.outputModule.showSuccess(
                    `불러오기를 마쳤습니다. (${parts.join(', ')}) 상단에서 반을 선택하면 자리 배치가 나타납니다.`
                );
                logger.info('백업 불러오기 완료:', result);
            } catch (error) {
                logger.error('백업 불러오기 실패:', error);
                this.outputModule.showError('불러오기에 실패했습니다. 파일을 확인해주세요.');
            }
        };

        reader.readAsText(file, 'UTF-8');
    }

    /**
     * 명단에서 중복된 이름 찾기
     */
    private findDuplicateStudentNames(students: Array<{name: string}>): string[] {
        const seen = new Set<string>();
        const duplicates = new Set<string>();

        students.forEach(student => {
            const key = (student.name || '').trim().toLowerCase();
            if (!key) return;
            if (seen.has(key)) {
                duplicates.add(student.name.trim());
            }
            seen.add(key);
        });

        return Array.from(duplicates);
    }

    /**
     * 자리 배치 진행 상태 설정
     * 진행 중에는 버튼을 비활성화해 중복 실행을 막는다.
     */
    private setArrangingSeats(arranging: boolean): void {
        this.arrangingSeats = arranging;

        const arrangeBtn = document.getElementById('arrange-seats') as HTMLButtonElement | null;
        if (arrangeBtn) {
            arrangeBtn.disabled = arranging;
            arrangeBtn.setAttribute('aria-busy', arranging ? 'true' : 'false');
        }
    }

    /**
     * 좌석 배치하기 처리
     */
    private handleArrangeSeats(): void {
            // 배치 연출이 끝나기 전에 다시 실행하면 두 배치가 뒤엉킨다.
            // 앞선 실행의 커튼 타이머가 뒤이은 실행의 연출을 도중에 뜯어내고,
            // 되돌리기 이력에도 중복 항목이 쌓인다.
            if (this.arrangingSeats) {
                return;
            }
            this.setArrangingSeats(true);

            // 읽기 전용 모드 해제
            this.disableReadOnlyMode();
            
            // 테이블에서 학생 데이터 가져오기
            const studentData = this.inputModule.getStudentData();
            
            if (studentData.length === 0) {
                this.outputModule.showError('학생 정보를 먼저 입력해주세요.');
                this.setArrangingSeats(false);
                return;
            }

            // 동명이인 확인
            // '이전 자리 피하기' / '이전 짝꿍 피하기'는 학생을 이름으로 구분하므로
            // 같은 이름이 둘 있으면 엉뚱한 학생에게 제약이 적용된다.
            // 배치를 막지는 않되 교사가 알 수 있게 알린다.
            const duplicateNames = this.findDuplicateStudentNames(studentData);
            if (duplicateNames.length > 0) {
                this.outputModule.showWarning(
                    `같은 이름의 학생이 있습니다: ${duplicateNames.join(', ')}. ` +
                    '이전 자리·짝꿍 피하기 옵션은 같은 이름끼리 구분하지 못합니다.'
                );
            }

        // 대용량 데이터 처리 시 프로그레스 바 사용 (100명 이상)
        const useProgress = studentData.length >= 100;
        let updateProgress: ((progress: number, statusMessage?: string) => void) | null = null;
        
        if (useProgress) {
            updateProgress = this.outputModule.showProgress('자리 배치를 생성하는 중입니다...', 0);
        } else {
            // 로딩 상태 표시
            this.outputModule.showLoading('자리 배치를 생성하는 중...');
        }
        
        // 3초 동안 지속하는 음향 효과 재생
        this.animationManager.playArrangementSound();
        
        try {
            // 커튼 애니메이션 시작
            this.animationManager.startCurtainAnimation();

            // 대용량 데이터 처리 시 지연 렌더링
            if (studentData.length > 50) {
                // 비동기 처리로 UI 블로킹 방지
                this.setTimeoutSafe(() => {
                    this.processArrangeSeats(studentData, updateProgress);
                }, 50);
            } else {
                this.processArrangeSeats(studentData, updateProgress);
            }
        } catch (error) {
            logger.error('좌석 배치 중 오류:', error);
            if (updateProgress) {
                this.outputModule.hideProgress();
            }
            this.outputModule.showError('좌석 배치 중 오류가 발생했습니다.');
            this.animationManager.stopCurtainAnimation();
            this.setArrangingSeats(false);
        }
    }
    
    /**
     * 좌석 배치 처리 (내부 메서드)
     */
    private processArrangeSeats(
        studentData: Array<{name: string, gender: 'M' | 'F'}>,
        updateProgress?: ((progress: number, statusMessage?: string) => void) | null
    ): void {
        try {
            if (updateProgress) {
                updateProgress(10, '학생 데이터 준비 중...');
            }

            

            // 학생 데이터를 Student 객체로 변환
            this.students = StudentModel.createMultiple(studentData);
            
            if (updateProgress) {
                updateProgress(30, '학생 데이터 변환 완료');
            }
            
            // 고정 좌석 모드인지 확인
            const fixedRandomMode = document.querySelector('input[name="custom-mode-2"][value="fixed-random"]:checked') as HTMLInputElement;
            
            // 고정 좌석 정보를 테이블에서 읽어오기
            if (fixedRandomMode) {
                const fixedSeatSelects = document.querySelectorAll('.fixed-seat-select') as NodeListOf<HTMLSelectElement>;
                fixedSeatSelects.forEach((select, index) => {
                    const seatIdStr = select.value;
                    if (seatIdStr && index < this.students.length && this.students[index]) {
                        const seatId = parseInt(seatIdStr, 10);
                        if (!isNaN(seatId)) {
                            this.students[index].fixedSeatId = seatId;
                            
                        }
                    }
                });
            }
            
            // 남학생과 여학생 분리
            const maleStudents = this.students.filter(s => s.gender === 'M');
            const femaleStudents = this.students.filter(s => s.gender === 'F');
            
            
            
            // card-layout-container가 숨겨져 있으면 표시
            const cardContainer = document.getElementById('card-layout-container');
            if (cardContainer) {
                cardContainer.style.display = 'block';
            }
            
            // 기존 카드들에서 이름만 변경 (카드 위치는 고정)
            const seatsArea = document.getElementById('seats-area');
            if (!seatsArea) {
                this.animationManager.stopCurtainAnimation();
                this.setArrangingSeats(false);
                this.outputModule.showError('좌석 배치 영역을 찾을 수 없습니다.');
                return;
            }
            
            // 기존 카드들 가져오기 (분단 레이블 제외)
            let existingCards = seatsArea.querySelectorAll('.student-seat-card');
            
            // 옵션 체크박스 값 읽기
            const avoidPrevSeat = (document.getElementById('avoid-prev-seat') as HTMLInputElement | null)?.checked === true;
            const avoidPrevPartner = (document.getElementById('avoid-prev-partner') as HTMLInputElement | null)?.checked === true;
            
            // 좌석 카드가 없으면 자동으로 생성
            if (existingCards.length === 0) {
                // card-layout-container가 숨겨져 있으면 표시
                if (cardContainer) {
                    cardContainer.style.display = 'block';
                }
                
                this.renderExampleCards();
                
                // renderExampleCards() 후 다시 카드 가져오기
                existingCards = seatsArea.querySelectorAll('.student-seat-card');
                if (existingCards.length === 0) {
                    this.animationManager.stopCurtainAnimation();
                    this.setArrangingSeats(false);
                    const loadingElement = document.querySelector('.loading');
                    if (loadingElement) {
                        loadingElement.remove();
                    }
                    this.outputModule.showError('좌석 배치 형태를 설정하고 학생 수를 입력해주세요.');
                    return;
                }
            }
            
            // 학생 수 > 좌석 수 경고
            if (this.students.length > existingCards.length) {
                const unassignedCount = this.students.length - existingCards.length;
                this.outputModule.showWarning(`학생 수(${this.students.length}명)가 좌석 수(${existingCards.length}개)보다 많습니다. ${unassignedCount}명의 학생이 배정되지 않습니다.`);
            }

            if (updateProgress) {
                updateProgress(50, '좌석 배치 준비 중...');
            }

            // 확정된 자리 이력에서 이전 좌석 및 짝꿍 정보 추출
            const { lastSeatByStudent, lastPartnerByStudent } = this.extractHistoryConstraints(avoidPrevSeat, avoidPrevPartner);
            
            // 고정 좌석 모드인 경우
            if (fixedRandomMode && this.fixedSeatIds.size > 0) {
                // 1단계: 모든 카드의 이름 초기화
                existingCards.forEach((card) => {
                    const cardElement = card as HTMLElement;
                    const nameDiv = cardElement.querySelector('.student-name') as HTMLElement;
                    if (nameDiv) {
                        nameDiv.textContent = '';
                    }
                });
                
                // 2단계: 고정 좌석에 지정된 학생 배치
                const fixedStudents = this.students.filter(s => s.fixedSeatId !== undefined);
                existingCards.forEach((card) => {
                    const cardElement = card as HTMLElement;
                    const seatIdStr = cardElement.getAttribute('data-seat-id');
                    if (!seatIdStr) return;
                    
                    const seatId = parseInt(seatIdStr, 10);
                    
                    // 고정 좌석인 경우
                    if (this.fixedSeatIds.has(seatId)) {
                        const fixedStudent = fixedStudents.find(s => s.fixedSeatId === seatId);
                        if (fixedStudent) {
                            const nameDiv = cardElement.querySelector('.student-name') as HTMLElement;
                            if (nameDiv) {
                                nameDiv.textContent = fixedStudent.name;
                                
                            }
                        }
                    }
                });
                
                // 3단계: 나머지 좌석에 랜덤 배치
                // 고정 좌석에 배치된 학생들을 제외한 나머지 학생들
                const allRemainingMales = maleStudents.filter(s => !s.fixedSeatId);
                const allRemainingFemales = femaleStudents.filter(s => !s.fixedSeatId);
                let shuffledMales = RandomService.shuffle(allRemainingMales);
                let shuffledFemales = RandomService.shuffle(allRemainingFemales);
                
                // 고정 좌석이 아닌 좌석만 필터링
                const nonFixedCards = Array.from(existingCards).filter(card => {
                    const seatIdStr = (card as HTMLElement).getAttribute('data-seat-id');
                    if (!seatIdStr) return false;
                    const seatId = parseInt(seatIdStr, 10);
                    return !this.fixedSeatIds.has(seatId);
                }) as HTMLElement[];
                
                
                
                
                // 페어 컨테이너 우선 처리 (짝 제약 고려)
                const seatsAreaEl = document.getElementById('seats-area')!;
                const pairContainers: HTMLElement[] = [];
                Array.from(seatsAreaEl.querySelectorAll('.student-seat-card')).forEach(card => {
                    const parent = (card as HTMLElement).parentElement as HTMLElement;
                    const siblings = parent ? parent.querySelectorAll('.student-seat-card') : null;
                    if (siblings && siblings.length === 2 && pairContainers.indexOf(parent) === -1) {
                        pairContainers.push(parent);
                    }
                });

                pairContainers.forEach(container => {
                    const cards = Array.from(container.querySelectorAll('.student-seat-card')) as HTMLElement[];
                    if (cards.length !== 2) return;
                    const [cardA, cardB] = cards;
                    const seatIdA = parseInt(cardA.getAttribute('data-seat-id') || '0', 10);
                    const seatIdB = parseInt(cardB.getAttribute('data-seat-id') || '0', 10);
                    const isMaleA = cardA.classList.contains('gender-m');
                    const isMaleB = cardB.classList.contains('gender-m');
                    const nameDivA = cardA.querySelector('.student-name') as HTMLElement;
                    const nameDivB = cardB.querySelector('.student-name') as HTMLElement;

                    // 각 카드의 이름 존재 여부 확인 (고정 좌석 모드)
                    const hasNameA = nameDivA && nameDivA.textContent && nameDivA.textContent.trim() !== '';
                    const hasNameB = nameDivB && nameDivB.textContent && nameDivB.textContent.trim() !== '';
                    
                    // 둘 다 이름이 있으면 이미 모두 배치된 것이므로 스킵
                    if (hasNameA && hasNameB) {
                        return;
                    }

                    const poolA = isMaleA ? shuffledMales : shuffledFemales;
                    const poolB = isMaleB ? shuffledMales : shuffledFemales;

                    // A 카드 처리 (이름이 없는 경우만)
                    let chosenA: Student | undefined = undefined;
                    if (!hasNameA) {
                        if (poolA.length === 0) {
                            // poolA가 비어있으면 다른 성별에서 시도
                            const alternativePoolA = isMaleA ? shuffledFemales : shuffledMales;
                            if (alternativePoolA.length > 0) {
                                let idxA = 0;
                                if (avoidPrevSeat) {
                                    for (let i = 0; i < alternativePoolA.length; i++) {
                                        const cand = alternativePoolA[i];
                                        if (lastSeatByStudent[cand.name] !== seatIdA) { idxA = i; break; }
                                    }
                                }
                                chosenA = alternativePoolA.splice(idxA, 1)[0];
                                if (nameDivA && chosenA) nameDivA.textContent = chosenA.name || '';
                                if (isMaleA) shuffledMales = alternativePoolA; else shuffledFemales = alternativePoolA;
                            }
                        } else {
                            let idxA = 0;
                            if (avoidPrevSeat) {
                                for (let i = 0; i < poolA.length; i++) {
                                    const cand = poolA[i];
                                    if (lastSeatByStudent[cand.name] !== seatIdA) { idxA = i; break; }
                                }
                            }
                            chosenA = poolA.splice(idxA, 1)[0];
                            if (nameDivA && chosenA) nameDivA.textContent = chosenA.name || '';
                            if (isMaleA) shuffledMales = poolA; else shuffledFemales = poolA;
                        }
                    } else {
                        // A에 이미 이름이 있으면 해당 학생 정보 가져오기 (partner 제약 확인용)
                        const existingName = nameDivA.textContent?.trim() || '';
                        chosenA = this.students.find(s => s.name === existingName);
                    }

                    // B 카드 처리 (이름이 없는 경우만)
                    if (!hasNameB) {
                        if (poolB.length === 0) {
                            // poolB가 비어있으면 다른 성별에서 시도
                            const alternativePoolB = isMaleB ? shuffledFemales : shuffledMales;
                            if (alternativePoolB.length > 0) {
                                let idxB = 0;
                                // partner 제약 고려
                                if (chosenA) {
                                    for (let i = 0; i < alternativePoolB.length; i++) {
                                        const cand = alternativePoolB[i];
                                        const seatOk = !avoidPrevSeat || lastSeatByStudent[cand.name] !== seatIdB;
                                        const partnerOk = !avoidPrevPartner || (
                                            lastPartnerByStudent[chosenA.name] !== cand.name && 
                                            lastPartnerByStudent[cand.name] !== chosenA.name
                                        );
                                        if (seatOk && partnerOk) { idxB = i; break; }
                                    }
                                } else if (avoidPrevSeat) {
                                    for (let i = 0; i < alternativePoolB.length; i++) {
                                        const cand = alternativePoolB[i];
                                        if (lastSeatByStudent[cand.name] !== seatIdB) { idxB = i; break; }
                                    }
                                }
                                const chosenB = alternativePoolB.splice(idxB, 1)[0];
                                if (nameDivB && chosenB) nameDivB.textContent = chosenB.name || '';
                                if (isMaleB) shuffledMales = alternativePoolB; else shuffledFemales = alternativePoolB;
                            }
                        } else {
                            let idxB = -1;
                            // partner 제약 고려
                            for (let i = 0; i < poolB.length; i++) {
                                const cand = poolB[i];
                                const seatOk = !avoidPrevSeat || lastSeatByStudent[cand.name] !== seatIdB;
                                const partnerOk = !avoidPrevPartner || !chosenA || (
                                    lastPartnerByStudent[chosenA.name] !== cand.name && 
                                    lastPartnerByStudent[cand.name] !== chosenA.name
                                );
                                if (seatOk && partnerOk) { idxB = i; break; }
                            }
                            
                            // 조건을 만족하는 학생이 없으면 첫 번째 학생을 선택 (강제 배치)
                            if (idxB === -1) {
                                idxB = 0;
                            }
                            
                            const chosenB = poolB.splice(idxB, 1)[0];
                            if (nameDivB && chosenB) nameDivB.textContent = chosenB.name || '';
                            if (isMaleB) shuffledMales = poolB; else shuffledFemales = poolB;
                        }
                    }
                });

                // 나머지 단일 카드 처리 (고정 좌석 제외)
                const singleCards: HTMLElement[] = [];
                Array.from(seatsAreaEl.querySelectorAll('.student-seat-card')).forEach(card => {
                    const parent = (card as HTMLElement).parentElement as HTMLElement;
                    const siblings = parent ? parent.querySelectorAll('.student-seat-card') : null;
                    if (!siblings || siblings.length !== 2) {
                        const seatIdStr = (card as HTMLElement).getAttribute('data-seat-id');
                        if (seatIdStr) {
                            const seatId = parseInt(seatIdStr, 10);
                            // 고정 좌석이 아닌 경우만 추가
                            if (!this.fixedSeatIds.has(seatId)) {
                                singleCards.push(card as HTMLElement);
                            }
                        }
                    }
                });
                singleCards.forEach(cardElement => {
                    const seatIdStr = cardElement.getAttribute('data-seat-id');
                    if (!seatIdStr) return;
                    const seatId = parseInt(seatIdStr, 10);
                    const nameDiv = cardElement.querySelector('.student-name') as HTMLElement;
                    
                    // 이미 이름이 있는 카드는 스킵 (이미 페어 컨테이너에서 배치된 경우)
                    const hasName = nameDiv && nameDiv.textContent && nameDiv.textContent.trim() !== '';
                    if (hasName) {
                        return;
                    }
                    
                    const isMaleCard = cardElement.classList.contains('gender-m');
                    // 남은 학생 중에서 성별에 맞는 학생 찾기, 없으면 다른 성별도 허용
                    let pool = isMaleCard ? shuffledMales : shuffledFemales;
                    if (pool.length === 0) {
                        // 성별에 맞는 학생이 없으면 다른 성별에서 가져오기
                        pool = isMaleCard ? shuffledFemales : shuffledMales;
                    }
                    if (pool.length === 0) { 
                        if (nameDiv) nameDiv.textContent = ''; 
                        return; 
                    }
                    let pickIdx = 0;
                    if (avoidPrevSeat) {
                        for (let i = 0; i < pool.length; i++) {
                            const cand = pool[i];
                            if (lastSeatByStudent[cand.name] !== seatId) { pickIdx = i; break; }
                        }
                    }
                    const chosen = pool.splice(pickIdx, 1)[0];
                    if (nameDiv && chosen) nameDiv.textContent = chosen.name || '';
                    if (isMaleCard) shuffledMales = pool; else shuffledFemales = pool;
                });
            } else {
                // 일반 랜덤 배치 모드
                let shuffledMales = RandomService.shuffle(maleStudents);
                let shuffledFemales = RandomService.shuffle(femaleStudents);
                
                // 페어 컨테이너 우선 처리
                const seatsAreaEl = document.getElementById('seats-area')!;
                const pairContainers: HTMLElement[] = [];
                Array.from(seatsAreaEl.querySelectorAll('.student-seat-card')).forEach(card => {
                    const parent = (card as HTMLElement).parentElement as HTMLElement;
                    const siblings = parent ? parent.querySelectorAll('.student-seat-card') : null;
                    if (siblings && siblings.length === 2 && pairContainers.indexOf(parent) === -1) {
                        pairContainers.push(parent);
                    }
                });

                // 성별에 맞는 학생이 남아 있지 않으면 다른 성별 풀에서 채운다.
                // (splice로 원본 배열을 직접 줄이므로 별도의 되돌려쓰기가 필요 없다)
                const poolFor = (preferMale: boolean): Student[] =>
                    PairingService.selectPool(preferMale, shuffledMales, shuffledFemales);

                pairContainers.forEach(container => {
                    const cards = Array.from(container.querySelectorAll('.student-seat-card')) as HTMLElement[];
                    if (cards.length !== 2) return;
                    const [cardA, cardB] = cards;
                    const seatIdA = parseInt(cardA.getAttribute('data-seat-id') || '0', 10);
                    const seatIdB = parseInt(cardB.getAttribute('data-seat-id') || '0', 10);
                    const isMaleA = cardA.classList.contains('gender-m');
                    const isMaleB = cardB.classList.contains('gender-m');
                    const nameDivA = cardA.querySelector('.student-name') as HTMLElement;
                    const nameDivB = cardB.querySelector('.student-name') as HTMLElement;

                    // A 자리 배정
                    // A의 성별 풀이 비었다고 해서 이 컨테이너 전체를 건너뛰면 안 된다.
                    // 그렇게 하면 아직 학생이 남아 있는 B 자리까지 함께 비고,
                    // 짝꿍 컨테이너는 아래 '단일 카드' 처리 대상에도 들어가지 않아
                    // 끝까지 빈 자리로 남는다 (학생 수와 좌석 수가 같아 경고도 뜨지 않는다).
                    const poolA = poolFor(isMaleA);
                    let chosenA: Student | undefined;
                    if (poolA.length === 0) {
                        if (nameDivA) nameDivA.textContent = '';
                    } else {
                        let idxA = 0;
                        if (avoidPrevSeat) {
                            for (let i = 0; i < poolA.length; i++) {
                                const cand = poolA[i];
                                if (lastSeatByStudent[cand.name] !== seatIdA) { idxA = i; break; }
                            }
                        }
                        chosenA = poolA.splice(idxA, 1)[0];
                        if (nameDivA) nameDivA.textContent = chosenA?.name || '';
                    }

                    // B 자리 배정 (A가 소모한 결과를 반영해 풀을 다시 고른다)
                    const poolB = poolFor(isMaleB);
                    if (poolB.length === 0) {
                        if (nameDivB) nameDivB.textContent = '';
                        return;
                    }

                    let idxB = -1;
                    for (let i = 0; i < poolB.length; i++) {
                        const cand = poolB[i];
                        const seatOk = !avoidPrevSeat || lastSeatByStudent[cand.name] !== seatIdB;
                        // 짝꿍 회피는 A가 실제로 배정된 경우에만 판단할 수 있다.
                        // chosenA가 없을 때 조건을 거짓으로 두면 모든 후보가 탈락해
                        // 항상 강제 배치로 떨어진다.
                        const partnerOk = !avoidPrevPartner || !chosenA || (
                            lastPartnerByStudent[chosenA.name] !== cand.name &&
                            lastPartnerByStudent[cand.name] !== chosenA.name
                        );
                        if (seatOk && partnerOk) { idxB = i; break; }
                    }
                    
                    // 조건을 만족하는 학생이 없으면 첫 번째 학생을 선택 (강제 배치)
                    if (idxB === -1) {
                        idxB = 0;
                    }
                    
                    const chosenB = poolB.splice(idxB, 1)[0];
                    if (nameDivB) nameDivB.textContent = chosenB?.name || '';
                });

                // 나머지 단일 카드 처리
                const singleCards: HTMLElement[] = [];
                Array.from(seatsAreaEl.querySelectorAll('.student-seat-card')).forEach(card => {
                    const parent = (card as HTMLElement).parentElement as HTMLElement;
                    const siblings = parent ? parent.querySelectorAll('.student-seat-card') : null;
                    if (!siblings || siblings.length !== 2) {
                        singleCards.push(card as HTMLElement);
                    }
                });
                singleCards.forEach(cardElement => {
                    const seatIdStr = cardElement.getAttribute('data-seat-id');
                    if (!seatIdStr) return;
                    const seatId = parseInt(seatIdStr, 10);
                    const nameDiv = cardElement.querySelector('.student-name') as HTMLElement;
                    const isMaleCard = cardElement.classList.contains('gender-m');
                    // 남은 학생 중에서 성별에 맞는 학생 찾기, 없으면 다른 성별도 허용
                    let pool = isMaleCard ? shuffledMales : shuffledFemales;
                    if (pool.length === 0) {
                        // 성별에 맞는 학생이 없으면 다른 성별에서 가져오기
                        pool = isMaleCard ? shuffledFemales : shuffledMales;
                    }
                    if (pool.length === 0) { 
                        if (nameDiv) nameDiv.textContent = ''; 
                        return; 
                    }
                    let pickIdx = 0;
                    if (avoidPrevSeat) {
                        for (let i = 0; i < pool.length; i++) {
                            const cand = pool[i];
                            if (lastSeatByStudent[cand.name] !== seatId) { pickIdx = i; break; }
                        }
                    }
                    const chosen = pool.splice(pickIdx, 1)[0];
                    if (nameDiv && chosen) nameDiv.textContent = chosen.name || '';
                    if (isMaleCard) shuffledMales = pool; else shuffledFemales = pool;
                });
            }
            
            // 현재 배치 결과 저장 (이전 좌석/짝 정보)
            const newLastSeatByStudent: Record<string, number> = {};
            const newLastPartnerByStudent: Record<string, string> = {};
            const allCards = Array.from(document.querySelectorAll('#seats-area .student-seat-card')) as HTMLElement[];
            allCards.forEach(card => {
                const name = (card.querySelector('.student-name') as HTMLElement)?.textContent?.trim() || '';
                const seatId = parseInt(card.getAttribute('data-seat-id') || '0', 10);
                if (name) newLastSeatByStudent[name] = seatId;
                const parent = card.parentElement as HTMLElement | null;
                if (parent) {
                    const siblings = Array.from(parent.querySelectorAll('.student-seat-card')) as HTMLElement[];
                    if (siblings.length === 2) {
                        const other = siblings[0] === card ? siblings[1] : siblings[0];
                        const otherName = (other.querySelector('.student-name') as HTMLElement)?.textContent?.trim() || '';
                        if (name && otherName) {
                            newLastPartnerByStudent[name] = otherName;
                        }
                    }
                }
            });
            try {
                this.storageManager.safeSetItem('lastSeatByStudent', JSON.stringify(newLastSeatByStudent));
                this.storageManager.safeSetItem('lastPartnerByStudent', JSON.stringify(newLastPartnerByStudent));
            } catch {}
            
            if (updateProgress) {
                updateProgress(90, '배치 결과 저장 중...');
            }
            
            // 로딩 제거 (showSuccess가 자동으로 로딩을 제거하지만 확실히 하기 위해)
            const loadingElement = document.querySelector('.loading');
            if (loadingElement) {
                loadingElement.remove();
            }
            
            if (updateProgress) {
                updateProgress(100, '배치 완료!');
                // 프로그레스 바를 잠시 표시한 후 숨김
                this.setTimeoutSafe(() => {
                    this.outputModule.hideProgress();
            this.outputModule.showSuccess('좌석 배치가 완료되었습니다!');
                }, 500);
            } else {
                this.outputModule.showSuccess('좌석 배치가 완료되었습니다!');
            }
            
            // 읽기 전용 모드 해제 (새로운 배치 생성 시)
            this.disableReadOnlyMode();
            
            // 자리 배치도 액션 버튼들 표시
            const actionButtons = document.getElementById('layout-action-buttons');
            if (actionButtons) {
                actionButtons.style.display = 'block';
            }
            
            // 확정된 자리 이력 드롭다운 업데이트 (항상 표시되므로 업데이트만)
            this.updateHistoryDropdown();
            
            // 고정 좌석 모드 도움말 숨기기
            const fixedSeatHelp = document.getElementById('fixed-seat-help');
            if (fixedSeatHelp) {
                fixedSeatHelp.style.display = 'none';
            }
            
            // 자리가 배정되었으므로 이제 카드를 끄는 것은 '학생 교환'이다.
            // 책상 옮기기는 배정 전에만 하도록 해서 두 동작이 섞이지 않게 한다.
            this.customLayoutManager?.setDeskEditing(false);

            // 1초 후 폭죽 애니메이션 시작
            this.setTimeoutSafe(() => {
                this.animationManager.startFireworks();
            }, 1000);
            
            // 3초 후 커튼 열기
            this.setTimeoutSafe(() => {
                this.animationManager.openCurtain();
            }, 3000);
            
            // 자리 배치 완료 후 히스토리 저장
            this.setTimeoutSafe(() => {
                this.saveLayoutToHistory();
                // 연출이 모두 끝난 뒤에 다시 배치할 수 있게 한다
                this.setArrangingSeats(false);
            }, 3100);
            
            // 배치 완료 후 화면을 맨 위로 스크롤 (스크롤 컨테이너와 윈도우 모두 시도)
            try {
                const resultContainer = document.querySelector('.result-container') as HTMLElement | null;
                const mainContent = document.querySelector('.main-content') as HTMLElement | null;
                
                // 윈도우 스크롤
                try {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                } catch {}
                
                // DOM 요소 스크롤
                [document.documentElement, document.body, resultContainer, mainContent].forEach((target) => {
                    if (target && target instanceof HTMLElement) {
                        try {
                            if ('scrollTo' in target && typeof target.scrollTo === 'function') {
                                target.scrollTo({ top: 0, behavior: 'smooth' });
                            } else if ('scrollTop' in target && typeof target.scrollTop === 'number') {
                                target.scrollTop = 0;
                        }
                    } catch {}
                    }
                });
            } catch {}
            
        } catch (error) {
            // 로딩 제거
            const loadingElement = document.querySelector('.loading');
            if (loadingElement) {
                loadingElement.remove();
            }
            
            this.animationManager.stopCurtainAnimation();
            this.setArrangingSeats(false);
            
            logger.error('좌석 배치 중 오류:', error);
            this.outputModule.showError('좌석 배치 중 오류가 발생했습니다. 콘솔을 확인해주세요.');
        }
    }

    /**
     * 자리 확정 처리
     */
    private async handleConfirmSeats(): Promise<void> {
        try {
            // 현재 좌석 배치 데이터 수집
            const seatsArea = document.getElementById('seats-area');
            if (!seatsArea) {
                this.outputModule.showError('좌석 배치 데이터를 찾을 수 없습니다.');
                return;
            }

            // 현재 배치 상태 저장
            const currentLayout: Array<{seatId: number, studentName: string, gender: 'M' | 'F'}> = [];
            const pairInfo: Array<{student1: string, student2: string}> = []; // 짝꿍 정보
            
            // 현재 배치 유형 확인 및 배치 형태 정보 수집
            const layoutTypeInput = document.querySelector('input[name="layout-type"]:checked') as HTMLInputElement;
            const layoutType = layoutTypeInput?.value || 'single-uniform';
            const isPairLayout = layoutType === 'pair-uniform';
            
            // 배치 형태 정보 수집 (복원을 위해 필요)
            const singleModeInput = document.querySelector('input[name="single-mode"]:checked') as HTMLInputElement;
            const pairModeInput = document.querySelector('input[name="pair-mode"]:checked') as HTMLInputElement;
            const groupSizeInput = document.querySelector('input[name="group-size"]:checked') as HTMLInputElement;
            const partitionInput = document.getElementById('number-of-partitions') as HTMLInputElement;
            
            const singleMode = singleModeInput?.value || 'basic-row';
            const pairMode = pairModeInput?.value || 'gender-pair';
            const groupSize = groupSizeInput?.value || '';
            const partitionCount = partitionInput ? parseInt(partitionInput.value || '1', 10) : 1;
            
            const allCards = Array.from(seatsArea.querySelectorAll('.student-seat-card')) as HTMLElement[];
            
            // 짝꿍 배치인 경우 짝꿍 정보 추출
            if (isPairLayout) {
                const pairContainers: HTMLElement[] = [];
                allCards.forEach(card => {
                    const parent = (card as HTMLElement).parentElement as HTMLElement;
                    const siblings = parent ? parent.querySelectorAll('.student-seat-card') : null;
                    if (siblings && siblings.length === 2 && pairContainers.indexOf(parent) === -1) {
                        pairContainers.push(parent);
                    }
                });
                
                pairContainers.forEach(container => {
                    const cards = Array.from(container.querySelectorAll('.student-seat-card')) as HTMLElement[];
                    if (cards.length === 2) {
                        const nameDiv1 = cards[0].querySelector('.student-name') as HTMLElement;
                        const nameDiv2 = cards[1].querySelector('.student-name') as HTMLElement;
                        const student1 = nameDiv1?.textContent?.trim() || '';
                        const student2 = nameDiv2?.textContent?.trim() || '';
                        
                        if (student1 && student2) {
                            pairInfo.push({ student1, student2 });
                        }
                    }
                });
            }
            
            // 화면의 카드에서 현재 배치를 읽는다 (되돌리기 복원과 같은 경로를 사용한다)
            currentLayout.push(...this.collectLayoutFromDom());

            if (currentLayout.length === 0) {
                this.outputModule.showError('확정할 자리 배치가 없습니다.');
                return;
            }

            // 현재 선택된 반 ID 가져오기 (반별로 따로 저장하기 위해 필수)
            const currentClassId = this.classManager?.getCurrentClassId();
            if (!currentClassId) {
                this.outputModule.showWarning('반이 선택되지 않아 이력이 저장되지 않습니다. 먼저 반을 선택하세요.');
                logger.warn('자리 확정 실패: 반이 선택되지 않음');
                return;
            }

            // 날짜 생성 (yy-mm-dd 형식)
            const now = new Date();
            const year = now.getFullYear().toString().slice(-2);
            const month = (now.getMonth() + 1).toString().padStart(2, '0');
            const day = now.getDate().toString().padStart(2, '0');
            const dateString = `${year}-${month}-${day}`;

            // 이력 데이터 생성
            const historyId = `history_${Date.now()}`;
            const historyItem: SeatHistoryItem = {
                id: historyId,
                date: dateString,
                layout: currentLayout,
                timestamp: now.getTime(),
                // 배치 형태 정보 저장 (복원을 위해 필요)
                layoutType: layoutType,
                singleMode: layoutType === 'single-uniform' ? singleMode : undefined,
                pairMode: layoutType === 'pair-uniform' ? pairMode : undefined,
                partitionCount: partitionCount,
                groupSize: layoutType === 'group' ? groupSize : undefined,
                // 반 ID 저장 (검증을 위해 필수)
                classId: currentClassId
            };
            
            // 짝꿍 정보가 있으면 추가
            if (pairInfo.length > 0) {
                historyItem.pairInfo = pairInfo;
            }

            // 사용자 구성 배치는 책상 위치까지 함께 남겨야 같은 모양으로 복원된다
            if (layoutType === 'custom') {
                historyItem.customPositions = this.customLayoutManager.readPositions();
            }
            
            // 반별 이력 키: seatHistory_${classId} (각 반마다 독립적으로 저장)
            const historyKey = `seatHistory_${currentClassId}`;
            logger.info(`반별 이력 저장 시작: 반ID=${currentClassId}, 키=${historyKey}`);
            const existingHistory = this.readSeatHistory(currentClassId);
            if (existingHistory === null) {
                // 기존 이력을 읽지 못한 상태에서 저장하면 이력 전체가 새 항목 하나로 교체된다.
                this.outputModule.showError('기존 이력을 읽을 수 없어 자리를 확정하지 않았습니다. 브라우저 저장소 설정을 확인한 뒤 다시 시도해주세요.');
                logger.error('자리 확정 중단: 기존 이력 읽기 실패', { currentClassId });
                return;
            }
            existingHistory.unshift(historyItem); // 최신 항목을 맨 앞에 추가
            // 최대 50개까지만 저장
            if (existingHistory.length > 50) {
                existingHistory.splice(50);
            }
            
            // 저장 실행 (localStorage)
            const saved = this.storageManager.safeSetItem(historyKey, JSON.stringify(existingHistory));
            if (!saved) {
                logger.error('이력 저장 실패:', { historyKey, historyLength: existingHistory.length });
                this.outputModule.showError('이력 저장에 실패했습니다. 브라우저 저장소를 확인해주세요.');
                return;
            }
            
            // 클라우드 저장은 아래에서 한 번만, 결과를 기다려 수행한다.
            // (같은 문서에 두 번 쓰면 나중 쓰기가 오래된 데이터로 앞선 쓰기를 덮을 수 있다.)
            
            // 저장 확인: 저장 직후 읽어서 검증
            const verifyHistory = this.readSeatHistory(currentClassId);
            if (!verifyHistory || verifyHistory.length === 0 || verifyHistory[0].id !== historyItem.id) {
                logger.error('이력 저장 검증 실패:', { 
                    saved: saved, 
                    verifyLength: verifyHistory?.length ?? null,
                    expectedId: historyItem.id,
                    actualId: verifyHistory?.[0]?.id 
                });
                this.outputModule.showError('이력 저장 후 검증에 실패했습니다. 다시 시도해주세요.');
                return;
            }
            
            logger.info('✅ 이력 저장 성공:', { 
                historyKey, 
                historyLength: verifyHistory.length,
                historyId: historyItem.id,
                date: historyItem.date 
            });

            // 드롭다운 메뉴 업데이트
            this.updateHistoryDropdown();

            // 화면 데이터를 모델에 반영한 뒤 저장한다.
            this.updateSeatsAndStudentsFromLayout(currentLayout);

            // 저장 결과를 기다린 뒤에 안내한다.
            // 기다리지 않고 성공 메시지를 띄우면, 저장이 실패해도 교사는 저장된 것으로 믿는다.
            let layoutSaved = true;
            if (this.classManager) {
                layoutSaved = await this.classManager.saveCurrentLayout({ silent: true });
                if (!layoutSaved) {
                    logger.error('자리 확정 시 자리 배치도 저장 실패');
                }
            }

            // 실제 저장 결과에 따른 안내 문구
            const saveOk = layoutSaved;
            const saveLine = layoutSaved
                ? '💾 이 기기에 저장되었습니다.'
                : '⚠️ 자리 배치도 저장에 실패했습니다. 브라우저 저장소를 확인해주세요.';

            // XSS 방지: DOM API를 사용하여 메시지 생성
            const container = (this.outputModule as any).container;
            if (container) {
                // 기존 메시지 제거
                const existingMessage = container.querySelector('.output-message');
                if (existingMessage) {
                    existingMessage.remove();
                }

                // 새 메시지 생성 (textContent 사용으로 XSS 방지)
                const messageElement = document.createElement('div');
                messageElement.className = saveOk ? 'output-message success' : 'output-message warning';
                const lines = [
                    '✅ 자리가 확정되었습니다.',
                    '',
                    '📋 확정된 자리 이력에 기록하였습니다.',
                    '',
                    saveLine,
                    '',
                    `📅 날짜: ${dateString}`
                ];
                lines.forEach((line, i) => {
                    if (i > 0) messageElement.appendChild(document.createElement('br'));
                    if (line) {
                        const span = document.createElement('span');
                        span.textContent = line;
                        if (i === 0 || i === 4 || i === 6) span.style.fontWeight = 'bold';
                        messageElement.appendChild(span);
                    }
                });
                messageElement.style.cssText = `
                    padding: 18px;
                    margin: 20px 0;
                    border-radius: 8px;
                    font-weight: 500;
                    background: ${saveOk ? '#d4edda' : '#fff3cd'};
                    color: ${saveOk ? '#155724' : '#856404'};
                    border: 1px solid ${saveOk ? '#c3e6cb' : '#ffeeba'};
                    line-height: 1.8;
                    font-size: 1.05em;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                `;
                messageElement.setAttribute('role', 'status');
                messageElement.setAttribute('aria-live', 'polite');
                messageElement.setAttribute('aria-atomic', 'true');
                messageElement.setAttribute('aria-label', `자리가 확정되었습니다. 확정된 자리 이력에 기록되었습니다. ${saveLine}`);
                
                container.appendChild(messageElement);
                
                // 7초 후 자동 제거 (메시지가 길어서 조금 더 길게)
                setTimeout(() => {
                    if (messageElement.parentNode) {
                        messageElement.remove();
                    }
                }, 7000);
            } else {
                // 폴백: 기본 showSuccess 사용
                const fallbackMessage = `✅ 자리가 확정되었습니다. 📋 확정된 자리 이력에 기록하였습니다. ${saveLine} 📅 날짜: ${dateString}`;
                if (saveOk) {
                    this.outputModule.showSuccess(fallbackMessage);
                } else {
                    this.outputModule.showWarning(fallbackMessage);
                }
            }
        } catch (error) {
            logger.error('자리 확정 중 오류:', error);
            this.outputModule.showError('자리 확정 중 오류가 발생했습니다.');
        }
    }

    /**
     * 자리 확정 시 수집한 데이터로 현재 seats와 students 업데이트
     */
    private collectLayoutFromDom(): Array<{seatId: number, studentName: string, gender: 'M' | 'F'}> {
        const seatsArea = document.getElementById('seats-area');
        if (!seatsArea) return [];

        const layout: Array<{seatId: number, studentName: string, gender: 'M' | 'F'}> = [];
        const cards = Array.from(seatsArea.querySelectorAll('.student-seat-card')) as HTMLElement[];

        cards.forEach(card => {
            const seatIdStr = card.getAttribute('data-seat-id');
            if (!seatIdStr) return;

            const seatId = parseInt(seatIdStr, 10);
            if (isNaN(seatId)) return;

            const nameDiv = card.querySelector('.student-name') as HTMLElement | null;
            const studentName = nameDiv?.textContent?.trim() || '';
            if (!studentName) return;

            const gender: 'M' | 'F' = card.classList.contains('gender-m') ? 'M' : 'F';
            layout.push({ seatId, studentName, gender });
        });

        return layout;
    }

    /**
     * 자리 확정 시 수집한 데이터로 현재 seats와 students 업데이트
     */
    private updateSeatsAndStudentsFromLayout(
        currentLayout: Array<{seatId: number, studentName: string, gender: 'M' | 'F'}>
    ): void {
        try {
            if (!currentLayout || currentLayout.length === 0) {
                logger.warn('자리 확정 데이터가 비어있습니다.');
                return;
            }

            // 기존 명단을 최대한 그대로 유지한다 (규칙은 SeatOccupancyService에 있고 테스트로 고정되어 있다)
            const merged = SeatOccupancyService.mergeRosterFromLayout(
                this.students,
                currentLayout,
                (name, gender) => StudentModel.create(name, gender)
            );
            const studentBySeatId = merged.studentBySeatId;
            this.students = merged.students;
            
            // 좌석 목록 업데이트 (기존 seats의 position 정보 유지)
            const seatIds = currentLayout.map(l => l.seatId).filter(id => id > 0);
            const maxSeatId = seatIds.length > 0 ? Math.max(...seatIds) : 0;
            const updatedSeats: Seat[] = [];
            
            // 기존 seats에서 position 정보 가져오기
            const existingSeatMap = new Map<number, Seat>();
            if (this.seats && Array.isArray(this.seats)) {
                this.seats.forEach(seat => {
                    if (seat && seat.id) {
                        existingSeatMap.set(seat.id, seat);
                    }
                });
            }
            
            // maxSeatId가 0이면 빈 배열 반환
            if (maxSeatId === 0) {
                logger.warn('유효한 좌석 ID가 없어 seats를 초기화했습니다.');
                this.seats = [];
                return;
            }
            
            for (let i = 1; i <= maxSeatId; i++) {
                const layoutItem = currentLayout.find(l => l.seatId === i);
                const existingSeat = existingSeatMap.get(i);
                
                if (layoutItem && layoutItem.studentName) {
                    // 학생이 배치된 좌석
                    const student = studentBySeatId.get(layoutItem.seatId);
                    if (student) {
                        const seat: Seat = {
                            id: i,
                            position: existingSeat?.position || { x: 0, y: 0 },
                            studentId: student.id,
                            studentName: student.name,
                            isFixed: existingSeat?.isFixed || false,
                            isActive: existingSeat?.isActive !== false
                        };
                        updatedSeats.push(seat);
                    }
                } else {
                    // 빈 좌석
                    const seat: Seat = {
                        id: i,
                        position: existingSeat?.position || { x: 0, y: 0 },
                        isFixed: existingSeat?.isFixed || false,
                        isActive: existingSeat?.isActive !== false
                    };
                    updatedSeats.push(seat);
                }
            }
            
            this.seats = updatedSeats;
            
            logger.info('자리 확정 데이터로 seats와 students 업데이트 완료:', {
                seatsCount: this.seats.length,
                studentsCount: this.students.length
            });
        } catch (error) {
            logger.error('자리 확정 데이터 업데이트 중 오류:', error);
            // 오류 발생 시에도 프로그램이 계속 실행되도록 함
        }
    }

    /**
     * 좌석 이력 가져오기 (반별로 관리, 최신순으로 정렬)
     * @param classId 반 ID (없으면 현재 선택된 반의 ID 사용)
     */
    private getSeatHistory(classId?: string): SeatHistoryItem[] {
        return this.readSeatHistory(classId) ?? [];
    }

    /**
     * 좌석 이력 읽기 (엄격)
     *
     * 읽기/파싱에 실패하면 null을 반환한다.
     * '이력이 없음'과 '읽지 못함'을 구분하지 않으면,
     * 읽기가 한 번 실패했을 때 빈 배열에 새 항목 하나만 붙여 되쓰는 결과가 되어
     * 저장돼 있던 이력 전체가 로컬과 클라우드 양쪽에서 사라진다.
     *
     * 이력을 덮어쓰는 경로에서는 반드시 이 메서드를 사용하고,
     * null이면 쓰기를 중단해야 한다.
     *
     * 이 메서드는 저장소에 쓰지 않는다. 조회가 데이터를 바꾸면 안 된다.
     */
    private readSeatHistory(classId?: string): SeatHistoryItem[] | null {
        try {
            // 반 ID가 없으면 현재 선택된 반 ID 사용
            const targetClassId = classId || this.classManager?.getCurrentClassId();
            
            // 반이 선택되지 않았으면 빈 배열 반환
            if (!targetClassId) {
                return [];
            }
            
            // 반별 이력 키: seatHistory_${classId}
            const historyKey = `seatHistory_${targetClassId}`;
            const read = this.storageManager.readItem(historyKey);
            if (!read.ok) {
                // 저장소 접근 실패 — '이력 없음'과 구분해야 한다
                logger.error('좌석 이력 읽기 실패: 저장소에 접근할 수 없습니다.', { historyKey });
                return null;
            }
            if (!read.value) return [];
            
            // JSON 파싱 시도 (데이터 손상 처리)
            let history: SeatHistoryItem[];
            try {
                history = JSON.parse(read.value) as SeatHistoryItem[];
                if (!Array.isArray(history)) {
                    return null;
                }
            } catch (parseError) {
                // 데이터 손상 — 손상된 값을 덮어쓰지 않도록 실패로 알린다
                logger.error('좌석 이력 파싱 실패:', { historyKey, parseError });
                return null;
            }
            
            // 반 ID로 필터링 (classId가 저장된 경우 검증)
            const filteredHistory = history.filter(item => {
                // classId가 없으면 포함 (이전 버전 호환성)
                if (!item.classId) {
                    return true;
                }
                // classId가 있으면 현재 반 ID와 일치하는 것만 포함
                return item.classId === targetClassId;
            });
            
            // 다른 반의 항목이 섞여 있으면 기록만 남긴다.
            // 조회 함수가 저장소를 정리(삭제)하면 읽기만 했는데 데이터가 사라진다.
            if (filteredHistory.length !== history.length) {
                logger.error('잘못된 반 ID를 가진 이력 항목이 섞여 있습니다(표시에서만 제외):', {
                    targetClassId,
                    originalCount: history.length,
                    filteredCount: filteredHistory.length
                });
            }
            
            // 최신 항목이 앞에 오도록 timestamp 기준 내림차순 정렬
            return filteredHistory.sort((a, b) => {
                return (b.timestamp || 0) - (a.timestamp || 0);
            });
        } catch (error) {
            logger.error('좌석 이력 읽기 중 오류:', error);
            return null;
        }
    }

    /**
     * 확정된 자리 이력에서 이전 좌석 및 짝꿍 제약 조건 추출
     */
    private extractHistoryConstraints(avoidPrevSeat: boolean, avoidPrevPartner: boolean): {
        lastSeatByStudent: Record<string, number>,
        lastPartnerByStudent: Record<string, string>
    } {
        const lastSeatByStudent: Record<string, number> = {};
        const lastPartnerByStudent: Record<string, string> = {};

        if (!avoidPrevSeat && !avoidPrevPartner) {
            return { lastSeatByStudent, lastPartnerByStudent };
        }

        // 확정된 자리 이력 가져오기 (현재 선택된 반의 이력)
        const currentClassId = this.classManager?.getCurrentClassId();
        if (!currentClassId) {
            return { lastSeatByStudent, lastPartnerByStudent };
        }
        const history = this.getSeatHistory(currentClassId);
        if (history.length === 0) {
            return { lastSeatByStudent, lastPartnerByStudent };
        }

        // 현재 배치 유형 확인
        const layoutTypeInput = document.querySelector('input[name="layout-type"]:checked') as HTMLInputElement;
        const isPairLayout = layoutTypeInput && layoutTypeInput.value === 'pair-uniform';

        // 모든 이력에서 정보 추출 (최신 이력 우선)
        history.forEach(historyItem => {
            const layout = historyItem.layout;

            // 이전 좌석 정보 추출
            if (avoidPrevSeat) {
                layout.forEach(item => {
                    if (item.studentName && !lastSeatByStudent[item.studentName]) {
                        lastSeatByStudent[item.studentName] = item.seatId;
                    }
                });
            }

            // 이전 짝꿍 정보 추출 (이력의 layoutType이 'pair-uniform'인 경우만)
            if (avoidPrevPartner && isPairLayout) {
                // 이력의 layoutType이 'pair-uniform'인 경우만 짝꿍 정보 추출
                const historyLayoutType = historyItem.layoutType;
                if (historyLayoutType === 'pair-uniform') {
                    // pairInfo가 있으면 사용 (더 정확함)
                    if (historyItem.pairInfo && historyItem.pairInfo.length > 0) {
                        historyItem.pairInfo.forEach(pair => {
                            if (pair.student1 && pair.student2) {
                                if (!lastPartnerByStudent[pair.student1]) {
                                    lastPartnerByStudent[pair.student1] = pair.student2;
                                }
                                if (!lastPartnerByStudent[pair.student2]) {
                                    lastPartnerByStudent[pair.student2] = pair.student1;
                                }
                            }
                        });
                    } else {
                        // pairInfo가 없으면 좌석 번호 기반으로 추론 (하위 호환성)
                        const seatGroups: {[seatId: number]: Array<{studentName: string, seatId: number}>} = {};
                        
                        layout.forEach(item => {
                            if (item.studentName) {
                                if (!seatGroups[item.seatId]) {
                                    seatGroups[item.seatId] = [];
                                }
                                seatGroups[item.seatId].push(item);
                            }
                        });

                        // 같은 좌석에 2명이 앉은 경우 (짝꿍 배치)
                        Object.values(seatGroups).forEach(group => {
                            if (group.length === 2) {
                                const [student1, student2] = group;
                                if (student1.studentName && student2.studentName) {
                                    if (!lastPartnerByStudent[student1.studentName]) {
                                        lastPartnerByStudent[student1.studentName] = student2.studentName;
                                    }
                                    if (!lastPartnerByStudent[student2.studentName]) {
                                        lastPartnerByStudent[student2.studentName] = student1.studentName;
                                    }
                                }
                            }
                        });

                        // 인접한 좌석 번호를 가진 학생들도 짝꿍으로 간주 (같은 행에 있는 경우)
                        const sortedLayout = [...layout].sort((a, b) => a.seatId - b.seatId);
                        for (let i = 0; i < sortedLayout.length - 1; i++) {
                            const current = sortedLayout[i];
                            const next = sortedLayout[i + 1];
                            
                            // 인접한 좌석이고 (차이가 1 또는 2), 같은 행에 있을 가능성이 높은 경우
                            if (current.studentName && next.studentName && 
                                (next.seatId - current.seatId <= 2)) {
                                // 이미 다른 짝꿍이 없으면 인접 학생을 짝꿍으로 기록
                                if (!lastPartnerByStudent[current.studentName] && !lastPartnerByStudent[next.studentName]) {
                                    lastPartnerByStudent[current.studentName] = next.studentName;
                                    lastPartnerByStudent[next.studentName] = current.studentName;
                                }
                            }
                        }
                    }
                }
            }
        });

        return { lastSeatByStudent, lastPartnerByStudent };
    }

    /**
     * 이력 드롭다운 메뉴 업데이트
     */
    private updateHistoryDropdown(): void {
        this.uiManager.updateHistoryDropdown();
    }

    /**
     * 이력 항목 삭제 (반별로 관리)
     */
    private deleteHistoryItem(historyId: string): void {
        if (!confirm('이 자리 이력을 삭제하시겠습니까?')) {
            return;
        }

        try {
            const currentClassId = this.classManager?.getCurrentClassId();
            if (!currentClassId) {
                this.outputModule.showError('반이 선택되지 않았습니다.');
                return;
            }
            
            const historyKey = `seatHistory_${currentClassId}`;
            const history = this.getSeatHistory(currentClassId);
            const filteredHistory = history.filter(item => item.id !== historyId);
            this.storageManager.safeSetItem(historyKey, JSON.stringify(filteredHistory));
            
            // 드롭다운 메뉴 업데이트
            this.updateHistoryDropdown();
            
            // 드롭다운이 열려있으면 닫기
            const historyContent = document.getElementById('history-dropdown-content');
            if (historyContent) {
                historyContent.style.display = 'none';
            }
        } catch (error) {
            logger.error('이력 삭제 중 오류:', error);
            this.outputModule.showError('이력 삭제 중 오류가 발생했습니다.');
        }
    }

    /**
     * 이력 항목 불러오기 (반별로 관리)
     */
    private async loadHistoryItem(historyId: string): Promise<void> {
        try {
            const currentClassId = this.classManager?.getCurrentClassId();
            if (!currentClassId) {
                this.outputModule.showError('반이 선택되지 않았습니다.');
                return;
            }
            
            // 현재 반의 이력만 가져오기 (명시적으로 반 ID 전달)
            const history = this.getSeatHistory(currentClassId);
            
            // 디버깅: 현재 반 ID와 이력 개수 로그
            logger.info('이력 불러오기 시도:', {
                currentClassId,
                historyId,
                historyCount: history.length,
                historyIds: history.map(item => item.id)
            });
            
            const historyItem = history.find(item => item.id === historyId);

            if (!historyItem) {
                logger.error('이력을 찾을 수 없음:', {
                    currentClassId,
                    historyId,
                    availableIds: history.map(item => item.id)
                });
                this.outputModule.showError(`현재 선택된 반('${currentClassId}')의 이력을 찾을 수 없습니다. 반이 변경되었을 수 있습니다.`);
                // 드롭다운을 다시 업데이트하여 현재 반의 이력만 표시
                this.updateHistoryDropdown();
                return;
            }
            
            // 추가 검증: 이력 항목이 현재 반에 속하는지 확인
            // classId가 저장되어 있으면 검증
            if (historyItem.classId && historyItem.classId !== currentClassId) {
                logger.error('이력 항목의 반 ID가 일치하지 않음:', {
                    currentClassId,
                    historyItemClassId: historyItem.classId,
                    historyId,
                    layoutType: historyItem.layoutType,
                    date: historyItem.date
                });
                this.outputModule.showError(`이 이력은 '${historyItem.classId}'반의 이력입니다. 현재 선택된 반('${currentClassId}')과 일치하지 않습니다.`);
                // 드롭다운을 다시 업데이트하여 현재 반의 이력만 표시
                this.updateHistoryDropdown();
                return;
            }
            
            logger.info('이력 항목 찾음:', {
                currentClassId,
                historyId,
                historyItemClassId: historyItem.classId,
                layoutType: historyItem.layoutType,
                date: historyItem.date
            });

            // 중요: 옵션 복원 전에 읽기 전용 모드 활성화
            // 이렇게 하면 옵션 복원 중 이벤트 핸들러가 실행되어 옵션이 변경되는 것을 방지
            this.isReadOnlyMode = true;
            // 확정된 이력을 보는 중에는 책상도 옮길 수 없어야 한다
            this.customLayoutManager?.setDeskEditing(false);

            // card-layout-container가 숨겨져 있으면 표시
            const cardContainer = document.getElementById('card-layout-container');
            if (cardContainer) {
                cardContainer.style.display = 'block';
            }

            // 좌석 배치 복원
            const seatsArea = document.getElementById('seats-area');
            if (!seatsArea) {
                this.outputModule.showError('좌석 배치 영역을 찾을 수 없습니다.');
                return;
            }

            // 이력에서 필요한 좌석 수 계산
            const maxSeatId = Math.max(...historyItem.layout.map(item => item.seatId), 0);
            const totalSeats = maxSeatId;
            
            // 학생 수 계산 (남학생 + 여학생)
            const maleCount = historyItem.layout.filter(item => item.gender === 'M').length;
            const femaleCount = historyItem.layout.filter(item => item.gender === 'F').length;
            const totalStudents = maleCount + femaleCount;
            
            // 남학생/여학생 수 입력 필드 설정
            const maleInput = document.getElementById('male-students') as HTMLInputElement;
            const femaleInput = document.getElementById('female-students') as HTMLInputElement;
            if (maleInput) maleInput.value = maleCount.toString();
            if (femaleInput) femaleInput.value = femaleCount.toString();
            
            // 중요: renderExampleCards()가 호출되기 전에 this.students 배열을 설정해야 함
            // 이력 데이터에서 학생 정보를 추출하여 this.students 배열 구성
            this.students = [];
            historyItem.layout.forEach(({ studentName, gender }, index) => {
                this.students.push({
                    id: index + 1,
                    name: studentName,
                    gender: gender
                });
            });
            
            logger.info('학생 데이터 설정 완료:', {
                totalStudents: this.students.length,
                maleCount: this.students.filter(s => s.gender === 'M').length,
                femaleCount: this.students.filter(s => s.gender === 'F').length
            });
            
            // 저장된 배치 형태 정보 복원 (이력에서 불러온 배치 형태로 복원)
            if (historyItem.layoutType) {
                logger.info('배치 형태 복원 시작:', {
                    layoutType: historyItem.layoutType,
                    singleMode: historyItem.singleMode,
                    pairMode: historyItem.pairMode,
                    partitionCount: historyItem.partitionCount
                });
                
                // 먼저 모든 라디오 버튼의 체크 해제
                document.querySelectorAll('input[name="layout-type"]').forEach((input: Element) => {
                    (input as HTMLInputElement).checked = false;
                });
                
                // layout-type 라디오 버튼 설정 (이벤트 발생 없이)
                const layoutTypeRadio = document.querySelector(`input[name="layout-type"][value="${historyItem.layoutType}"]`) as HTMLInputElement;
                if (layoutTypeRadio) {
                    layoutTypeRadio.checked = true;
                }
                
                // 라디오를 코드로 바꾸면 change 이벤트가 발생하지 않으므로
                // 배치 형태에 따라 달라지는 옵션들을 여기서 직접 맞춰준다
                this.updateCustomLayoutHint();
                this.togglePartitionSection(historyItem.layoutType !== 'custom');
                
                // layout-type에 따른 서브 옵션 설정
                if (historyItem.layoutType === 'single-uniform') {
                    // single-submenu 표시
                    const singleSubmenu = document.getElementById('single-submenu');
                    if (singleSubmenu) {
                        singleSubmenu.style.display = 'block';
                    }
                    const pairSubmenu = document.getElementById('pair-submenu');
                    if (pairSubmenu) {
                        pairSubmenu.style.display = 'none';
                    }
                    
                    // single-mode 설정
                    if (historyItem.singleMode) {
                        document.querySelectorAll('input[name="single-mode"]').forEach((input: Element) => {
                            (input as HTMLInputElement).checked = false;
                        });
                        const singleModeRadio = document.querySelector(`input[name="single-mode"][value="${historyItem.singleMode}"]`) as HTMLInputElement;
                        if (singleModeRadio) {
                            singleModeRadio.checked = true;
                        }
                    }
                } else if (historyItem.layoutType === 'pair-uniform') {
                    // pair-submenu 표시
                    const pairSubmenu = document.getElementById('pair-submenu');
                    if (pairSubmenu) {
                        pairSubmenu.style.display = 'block';
                    }
                    const singleSubmenu = document.getElementById('single-submenu');
                    if (singleSubmenu) {
                        singleSubmenu.style.display = 'none';
                    }
                    // group-submenu 숨김
                    const groupSubmenu = document.getElementById('group-submenu');
                    if (groupSubmenu) {
                        groupSubmenu.style.display = 'none';
                    }
                    
                    // pair-mode 설정
                    if (historyItem.pairMode) {
                        document.querySelectorAll('input[name="pair-mode"]').forEach((input: Element) => {
                            (input as HTMLInputElement).checked = false;
                        });
                        const pairModeRadio = document.querySelector(`input[name="pair-mode"][value="${historyItem.pairMode}"]`) as HTMLInputElement;
                        if (pairModeRadio) {
                            pairModeRadio.checked = true;
                        } else {
                            logger.warn(`pair-mode 라디오 버튼을 찾을 수 없습니다: ${historyItem.pairMode}`);
                        }
                    } else {
                        logger.warn('historyItem에 pairMode가 없습니다.');
                    }
                } else if (historyItem.layoutType === 'group') {
                    // group-submenu 표시
                    const groupSubmenu = document.getElementById('group-submenu');
                    if (groupSubmenu) {
                        groupSubmenu.style.display = 'block';
                    }
                    // single-submenu와 pair-submenu 숨김
                    const singleSubmenu = document.getElementById('single-submenu');
                    if (singleSubmenu) {
                        singleSubmenu.style.display = 'none';
                    }
                    const pairSubmenu = document.getElementById('pair-submenu');
                    if (pairSubmenu) {
                        pairSubmenu.style.display = 'none';
                    }
                    // group-size 설정
                    if (historyItem.groupSize) {
                        document.querySelectorAll('input[name="group-size"]').forEach((input: Element) => {
                            (input as HTMLInputElement).checked = false;
                        });
                        const groupSizeRadio = document.querySelector(`input[name="group-size"][value="${historyItem.groupSize}"]`) as HTMLInputElement;
                        if (groupSizeRadio) {
                            groupSizeRadio.checked = true;
                        }
                    }
                }
                
                // 분단 수 설정
                if (historyItem.partitionCount !== undefined) {
                    const partitionInput = document.getElementById('number-of-partitions') as HTMLInputElement;
                    if (partitionInput) {
                        partitionInput.value = historyItem.partitionCount.toString();
                    }
                }
                
                // 옵션 복원 완료 대기 (UI 업데이트를 위해)
                await new Promise<void>(resolve => setTimeout(resolve, 300));
                
                // 복원된 옵션 확인 및 검증
                const restoredLayoutType = document.querySelector('input[name="layout-type"]:checked') as HTMLInputElement;
                const actualLayoutType = restoredLayoutType?.value;
                
                // pair-uniform인 경우 pair-mode도 확인
                let actualPairMode: string | null = null;
                if (historyItem.layoutType === 'pair-uniform') {
                    const restoredPairMode = document.querySelector('input[name="pair-mode"]:checked') as HTMLInputElement;
                    actualPairMode = restoredPairMode?.value || null;
                }
                
                logger.info('배치 형태 복원 완료:', {
                    expected: {
                        layoutType: historyItem.layoutType,
                        pairMode: historyItem.pairMode,
                        singleMode: historyItem.singleMode,
                        groupSize: historyItem.groupSize
                    },
                    actual: {
                        layoutType: actualLayoutType,
                        pairMode: actualPairMode,
                        singleMode: (document.querySelector('input[name="single-mode"]:checked') as HTMLInputElement | null)?.value || null,
                        groupSize: (document.querySelector('input[name="group-size"]:checked') as HTMLInputElement | null)?.value || null
                    }
                });
                
                // 옵션 복원 검증 실패 시 경고
                if (actualLayoutType !== historyItem.layoutType) {
                    logger.error('배치 형태 복원 실패:', {
                        expected: historyItem.layoutType,
                        actual: actualLayoutType
                    });
                    this.outputModule.showWarning(`배치 형태 복원에 실패했습니다. 예상: ${historyItem.layoutType}, 실제: ${actualLayoutType}`);
                }
                
                if (historyItem.layoutType === 'pair-uniform' && actualPairMode !== historyItem.pairMode) {
                    logger.error('pair-mode 복원 실패:', {
                        expected: historyItem.pairMode,
                        actual: actualPairMode
                    });
                    this.outputModule.showWarning(`짝꿍 모드 복원에 실패했습니다. 예상: ${historyItem.pairMode}, 실제: ${actualPairMode}`);
                }
            }

            // 모든 카드 초기화
            let allCards = Array.from(seatsArea.querySelectorAll('.student-seat-card')) as HTMLElement[];
            
            // 좌석 카드가 없거나 개수가 맞지 않으면 카드 생성
            if (allCards.length === 0 || allCards.length < totalSeats) {
                // 옵션이 제대로 복원되었는지 다시 한 번 확인
                const verifyLayoutType = document.querySelector('input[name="layout-type"]:checked') as HTMLInputElement;
                const verifyLayoutTypeValue = verifyLayoutType?.value;
                
                if (verifyLayoutTypeValue !== historyItem.layoutType) {
                    logger.error('옵션 복원 검증 실패 - 카드 생성 전:', {
                        expected: historyItem.layoutType,
                        actual: verifyLayoutTypeValue
                    });
                    // 강제로 옵션 재설정
                    document.querySelectorAll('input[name="layout-type"]').forEach((input: Element) => {
                        (input as HTMLInputElement).checked = false;
                    });
                    const layoutTypeRadio = document.querySelector(`input[name="layout-type"][value="${historyItem.layoutType}"]`) as HTMLInputElement;
                    if (layoutTypeRadio) {
                        layoutTypeRadio.checked = true;
                    }
                    
                    // pair-uniform인 경우 pair-mode도 재설정
                    if (historyItem.layoutType === 'pair-uniform' && historyItem.pairMode) {
                        document.querySelectorAll('input[name="pair-mode"]').forEach((input: Element) => {
                            (input as HTMLInputElement).checked = false;
                        });
                        const pairModeRadio = document.querySelector(`input[name="pair-mode"][value="${historyItem.pairMode}"]`) as HTMLInputElement;
                        if (pairModeRadio) {
                            pairModeRadio.checked = true;
                        }
                    }
                    
                    // 재설정 후 대기
                    await new Promise<void>(resolve => setTimeout(resolve, 200));
                }
                
                // pair-uniform인 경우 직접 이력 데이터를 기반으로 카드 생성
                if (historyItem.layoutType === 'pair-uniform') {
                    // seatsArea 초기화
                    seatsArea.innerHTML = '';
                    
                    // seatsArea의 그리드 설정
                    const partitionCount = historyItem.partitionCount || 5;
                    seatsArea.style.gridTemplateColumns = `repeat(${partitionCount}, 1fr)`;
                    seatsArea.style.gap = '10px 40px';
                    
                    // 분단 레이블 추가
                    for (let i = 1; i <= partitionCount; i++) {
                        const label = document.createElement('div');
                        label.textContent = `${i}분단`;
                        label.style.textAlign = 'center';
                        label.style.fontWeight = 'bold';
                        label.style.color = '#667eea';
                        label.style.fontSize = '0.9em';
                        label.style.marginBottom = '5px';
                        seatsArea.appendChild(label);
                    }
                    
                    // 이력 데이터를 seatId 순으로 정렬
                    const sortedLayout = [...historyItem.layout].sort((a, b) => a.seatId - b.seatId);
                    
                    // seatId를 기준으로 짝꿍 그룹 생성 (연속된 seatId가 짝꿍)
                    const pairGroups: Array<Array<{seatId: number, studentName: string, gender: 'M' | 'F'}>> = [];
                    for (let i = 0; i < sortedLayout.length; i += 2) {
                        const pair: Array<{seatId: number, studentName: string, gender: 'M' | 'F'}> = [];
                        if (i < sortedLayout.length) {
                            pair.push(sortedLayout[i]);
                        }
                        if (i + 1 < sortedLayout.length) {
                            pair.push(sortedLayout[i + 1]);
                        }
                        if (pair.length > 0) {
                            pairGroups.push(pair);
                        }
                    }
                    
                    // 짝꿍 그룹을 분단별로 배치
                    const rowsPerPartition = Math.ceil(pairGroups.length / partitionCount);
                    let pairIndex = 0;
                    
                    for (let row = 0; row < rowsPerPartition; row++) {
                        for (let partition = 0; partition < partitionCount; partition++) {
                            if (pairIndex >= pairGroups.length) break;
                            
                            const pair = pairGroups[pairIndex++];
                            const pairContainer = document.createElement('div');
                            pairContainer.style.display = 'flex';
                            pairContainer.style.gap = '0px';
                            pairContainer.style.width = '100%';
                            pairContainer.style.justifyContent = 'center';
                            
                            // 짝꿍 카드 생성
                            pair.forEach(({ seatId, studentName, gender }) => {
                                const student = this.students.find(s => s.name === studentName);
                                if (student) {
                                    const card = this.createStudentCard(student, this.students.indexOf(student));
                                    card.setAttribute('data-seat-id', seatId.toString());
                                    const seatNumberDiv = card.querySelector('.seat-number-label') as HTMLElement;
                                    if (seatNumberDiv) {
                                        seatNumberDiv.textContent = `#${seatId}`;
                                    }
                                    const nameDiv = card.querySelector('.student-name') as HTMLElement;
                                    if (nameDiv) {
                                        nameDiv.textContent = studentName;
                                    }
                                    card.classList.remove('gender-m', 'gender-f');
                                    card.classList.add(`gender-${gender.toLowerCase()}`);
                                    pairContainer.appendChild(card);
                                }
                            });
                            
                            seatsArea.appendChild(pairContainer);
                        }
                    }
                    
                    // 카드 다시 가져오기
                    allCards = Array.from(seatsArea.querySelectorAll('.student-seat-card')) as HTMLElement[];
                    this.nextSeatId = maxSeatId + 1;
                    
                    // pair-uniform인 경우 이미 카드가 생성되고 데이터가 설정되었으므로 복원 완료 처리
                    const restoredCount = allCards.length;
                    logger.info(`이력 복원 완료 (pair-uniform): ${restoredCount}/${historyItem.layout.length}개 좌석 복원됨`, {
                        totalCards: allCards.length,
                        totalSeats: totalSeats,
                        historyLayoutCount: historyItem.layout.length
                    });
                } else {
                    // 사용자 구성 배치는 확정 당시의 책상 위치로 복원한다.
                    // renderExampleCards가 이 값을 한 번 쓰고 비운다.
                    if (historyItem.layoutType === 'custom' && historyItem.customPositions) {
                        this.pendingCustomPositions =
                            CustomLayoutService.parsePositions(historyItem.customPositions);
                    }
                    
                    // pair-uniform이 아닌 경우 renderExampleCards 호출
                    // 미리보기 카드 생성 (renderExampleCards 호출 - 복원된 배치 형태로 렌더링됨)
                    // renderExampleCards는 nextSeatId를 1로 초기화하므로, 호출 후에 seatId를 재설정해야 함
                    this.renderExampleCards();
                    
                    // 카드가 완전히 렌더링될 때까지 대기
                    await new Promise<void>(resolve => setTimeout(resolve, 300));
                    
                    // 카드 다시 가져오기
                    allCards = Array.from(seatsArea.querySelectorAll('.student-seat-card')) as HTMLElement[];
                }
            }
            
            // pair-uniform이 아닌 경우에만 data-seat-id 재설정 및 복원 로직 실행
            let restoredCount = 0;
            if (historyItem.layoutType !== 'pair-uniform') {
                // 이력 데이터의 seatId에 맞춰 카드의 data-seat-id 재설정
                // 이력 데이터를 seatId 순으로 정렬
                const sortedLayout = [...historyItem.layout].sort((a, b) => a.seatId - b.seatId);
                
                // 카드가 충분하지 않으면 추가 생성
                while (allCards.length < totalSeats) {
                    // 빈 학생 데이터로 카드 생성
                    const emptyStudent: Student = {
                        id: allCards.length + 1,
                        name: '',
                        gender: 'M'
                    };
                    const newCard = this.createStudentCard(emptyStudent, allCards.length);
                    seatsArea.appendChild(newCard);
                    allCards.push(newCard);
                }
                
                // 이력 데이터의 seatId에 맞춰 각 카드의 data-seat-id 설정
                // 중요: 카드 순서와 seatId 순서가 다를 수 있으므로, 각 seatId에 해당하는 카드를 찾아서 설정
                sortedLayout.forEach(({ seatId }) => {
                    // 먼저 이미 올바른 seatId를 가진 카드가 있는지 확인
                    let card = allCards.find(c => c.getAttribute('data-seat-id') === seatId.toString());
                    
                    // 없으면 순서대로 할당 (아직 seatId가 설정되지 않은 카드 찾기)
                    if (!card) {
                        // seatId가 1부터 시작하는 카드 찾기 (renderExampleCards가 1부터 생성)
                        const cardIndex = seatId - 1;
                        if (cardIndex >= 0 && cardIndex < allCards.length) {
                            card = allCards[cardIndex];
                        } else {
                            // 범위를 벗어나면 빈 카드 찾기
                            card = allCards.find(c => !c.getAttribute('data-seat-id') || c.getAttribute('data-seat-id') === '0');
                        }
                    }
                    
                    if (card) {
                        card.setAttribute('data-seat-id', seatId.toString());
                        // 좌석 번호 레이블도 업데이트
                        const seatNumberDiv = card.querySelector('.seat-number-label') as HTMLElement;
                        if (seatNumberDiv) {
                            seatNumberDiv.textContent = `#${seatId}`;
                        }
                    } else {
                        logger.warn(`좌석 카드를 찾을 수 없습니다: seatId=${seatId}`);
                    }
                });
                
                // nextSeatId 업데이트 (다음 카드 생성을 위해)
                this.nextSeatId = maxSeatId + 1;
                
                // 모든 카드 초기화
                allCards.forEach(card => {
                    const nameDiv = card.querySelector('.student-name') as HTMLElement;
                    if (nameDiv) {
                        nameDiv.textContent = '';
                    }
                });
                
                // 이력 데이터로 복원
                // 먼저 모든 카드를 다시 가져와서 최신 상태 확인
                allCards = Array.from(seatsArea.querySelectorAll('.student-seat-card')) as HTMLElement[];
                
                let restoredCount = 0;
                const missingSeats: Array<{seatId: number, studentName: string}> = [];
                
                historyItem.layout.forEach(({ seatId, studentName, gender }) => {
                    const card = seatsArea.querySelector(`[data-seat-id="${seatId}"]`) as HTMLElement;
                    if (card) {
                        const nameDiv = card.querySelector('.student-name') as HTMLElement;
                        if (nameDiv) {
                            nameDiv.textContent = studentName;
                            // 성별 클래스 설정
                            card.classList.remove('gender-m', 'gender-f');
                            card.classList.add(`gender-${gender.toLowerCase()}`);
                            restoredCount++;
                        } else {
                            logger.warn(`학생 이름 요소를 찾을 수 없습니다: seatId=${seatId}`);
                            missingSeats.push({ seatId, studentName });
                        }
                    } else {
                        logger.warn(`좌석 카드를 찾을 수 없습니다: seatId=${seatId}, studentName=${studentName}`);
                        missingSeats.push({ seatId, studentName });
                    }
                });
                
                // 누락된 좌석이 있으면 추가 시도
                if (missingSeats.length > 0) {
                    logger.warn(`누락된 좌석 ${missingSeats.length}개 발견, 추가 시도 중...`);
                    missingSeats.forEach(({ seatId, studentName }) => {
                        // 빈 카드 찾기 또는 새로 생성
                        let emptyCard = allCards.find(c => {
                            const nameDiv = c.querySelector('.student-name') as HTMLElement;
                            return nameDiv && !nameDiv.textContent?.trim();
                        });
                        
                        if (emptyCard) {
                            emptyCard.setAttribute('data-seat-id', seatId.toString());
                            const seatNumberDiv = emptyCard.querySelector('.seat-number-label') as HTMLElement;
                            if (seatNumberDiv) {
                                seatNumberDiv.textContent = `#${seatId}`;
                            }
                            const nameDiv = emptyCard.querySelector('.student-name') as HTMLElement;
                            if (nameDiv) {
                                nameDiv.textContent = studentName;
                                restoredCount++;
                            }
                        }
                    });
                }
                
                if (restoredCount === 0) {
                    logger.error('이력 복원 실패: 복원된 좌석이 없습니다.', {
                        totalCards: allCards.length,
                        totalSeats: totalSeats,
                        historyLayoutCount: historyItem.layout.length,
                        cardSeatIds: allCards.map(c => c.getAttribute('data-seat-id'))
                    });
                    this.outputModule.showError('이력 복원에 실패했습니다. 좌석 카드를 찾을 수 없습니다.');
                    return;
                }
                
                logger.info(`이력 복원 완료: ${restoredCount}/${historyItem.layout.length}개 좌석 복원됨`, {
                    totalCards: allCards.length,
                    totalSeats: totalSeats,
                    historyLayoutCount: historyItem.layout.length
                });
            } else {
                // pair-uniform인 경우 이미 카드가 생성되고 데이터가 설정되었으므로 복원 완료 처리
                restoredCount = allCards.length;
                logger.info(`이력 복원 완료 (pair-uniform): ${restoredCount}/${historyItem.layout.length}개 좌석 복원됨`, {
                    totalCards: allCards.length,
                    totalSeats: totalSeats,
                    historyLayoutCount: historyItem.layout.length
                });
            }

            // 읽기 전용 모드 활성화
            this.isReadOnlyMode = true;
            // 확정된 이력을 보는 중에는 책상도 옮길 수 없어야 한다
            this.customLayoutManager?.setDeskEditing(false);
            
            // 모든 좌석 카드의 드래그 비활성화
            allCards.forEach(card => {
                card.setAttribute('draggable', 'false');
                card.style.cursor = 'default';
                card.style.opacity = '0.8';
                card.classList.add('read-only-seat');
            });
            
            // "자리 배치하기" 버튼 비활성화
            const arrangeBtn = document.getElementById('arrange-seats');
            if (arrangeBtn) {
                (arrangeBtn as HTMLButtonElement).disabled = true;
                arrangeBtn.style.opacity = '0.5';
                arrangeBtn.style.cursor = 'not-allowed';
                arrangeBtn.title = '확정된 자리 이력은 수정할 수 없습니다.';
            }
            
            // 사이드바 옵션들 비활성화
            this.disableSidebarOptions();
            
            // 읽기 전용 모드 표시 배지 추가
            const readOnlyBadge = document.createElement('div');
            readOnlyBadge.id = 'read-only-badge';
            readOnlyBadge.textContent = '📋 읽기 전용 (확정된 자리 이력)';
            
            // 자리 배치도 화면의 왼쪽 상단 모서리에 배치
            const classroomLayout = document.getElementById('classroom-layout');
            let badgeTop = 20;
            let badgeLeft = 20;
            
            if (classroomLayout) {
                const rect = classroomLayout.getBoundingClientRect();
                // 자리 배치도 영역의 왼쪽 상단 모서리에 배치
                badgeTop = rect.top + 10; // 상단에서 10px 아래
                badgeLeft = rect.left + 10; // 왼쪽에서 10px 오른쪽
            }
            
            readOnlyBadge.style.cssText = `
                position: fixed;
                top: ${badgeTop}px;
                left: ${badgeLeft}px;
                background: #ff9800;
                color: white;
                padding: 10px 20px;
                border-radius: 8px;
                font-size: 0.9em;
                font-weight: bold;
                z-index: 1000;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
                animation: slideIn 0.3s ease;
                max-width: 300px;
            `;
            
            // 기존 배지 제거
            const existingBadge = document.getElementById('read-only-badge');
            if (existingBadge) {
                existingBadge.remove();
            }
            
            document.body.appendChild(readOnlyBadge);

            // 드롭다운 닫기
            const dropdown = document.getElementById('history-dropdown-content');
            if (dropdown) {
                dropdown.style.display = 'none';
            }

            // 자리 배치도 액션 버튼들 표시
            const actionButtons = document.getElementById('layout-action-buttons');
            if (actionButtons) {
                actionButtons.style.display = 'block';
            }

            this.outputModule.showSuccess(`${historyItem.date}의 자리 배치를 불러왔습니다. (읽기 전용)`);
        } catch (error) {
            logger.error('이력 불러오기 중 오류:', error);
            this.outputModule.showError('이력을 불러오는 중 오류가 발생했습니다.');
        }
    }

    /**
     * 사이드바 옵션들 비활성화 (읽기 전용 모드)
     */
    private disableSidebarOptions(): void {
        // 옵션1: 좌석 배치 형태
        const layoutTypeInputs = document.querySelectorAll('input[name="layout-type"]') as NodeListOf<HTMLInputElement>;
        layoutTypeInputs.forEach(input => {
            input.disabled = true;
            input.style.opacity = '0.5';
            input.style.cursor = 'not-allowed';
        });
        
        // 서브 옵션들
        const singleModeInputs = document.querySelectorAll('input[name="single-mode"]') as NodeListOf<HTMLInputElement>;
        singleModeInputs.forEach(input => {
            input.disabled = true;
            input.style.opacity = '0.5';
            input.style.cursor = 'not-allowed';
        });
        
        const pairModeInputs = document.querySelectorAll('input[name="pair-mode"]') as NodeListOf<HTMLInputElement>;
        pairModeInputs.forEach(input => {
            input.disabled = true;
            input.style.opacity = '0.5';
            input.style.cursor = 'not-allowed';
        });
        
        const groupSizeInputs = document.querySelectorAll('input[name="group-size"]') as NodeListOf<HTMLInputElement>;
        groupSizeInputs.forEach(input => {
            input.disabled = true;
            input.style.opacity = '0.5';
            input.style.cursor = 'not-allowed';
        });
        
        const reverseGenderOrder = document.getElementById('reverse-gender-order') as HTMLInputElement;
        if (reverseGenderOrder) {
            reverseGenderOrder.disabled = true;
            reverseGenderOrder.style.opacity = '0.5';
            reverseGenderOrder.style.cursor = 'not-allowed';
        }
        
        const groupGenderMix = document.getElementById('group-gender-mix') as HTMLInputElement;
        if (groupGenderMix) {
            groupGenderMix.disabled = true;
            groupGenderMix.style.opacity = '0.5';
            groupGenderMix.style.cursor = 'not-allowed';
        }
        
        const genderPairing = document.getElementById('gender-pairing') as HTMLInputElement;
        if (genderPairing) {
            genderPairing.disabled = true;
            genderPairing.style.opacity = '0.5';
            genderPairing.style.cursor = 'not-allowed';
        }
        
        // 옵션2: 학생 자리 수
        const maleStudents = document.getElementById('male-students') as HTMLInputElement;
        if (maleStudents) {
            maleStudents.disabled = true;
            maleStudents.style.opacity = '0.5';
            maleStudents.style.cursor = 'not-allowed';
        }
        
        const femaleStudents = document.getElementById('female-students') as HTMLInputElement;
        if (femaleStudents) {
            femaleStudents.disabled = true;
            femaleStudents.style.opacity = '0.5';
            femaleStudents.style.cursor = 'not-allowed';
        }
        
        // 옵션3: 분단 개수
        const numberOfPartitions = document.getElementById('number-of-partitions') as HTMLInputElement;
        if (numberOfPartitions) {
            numberOfPartitions.disabled = true;
            numberOfPartitions.style.opacity = '0.5';
            numberOfPartitions.style.cursor = 'not-allowed';
        }
        
        // 옵션4: 맞춤 구성
        const customModeInputs = document.querySelectorAll('input[name="custom-mode-2"]') as NodeListOf<HTMLInputElement>;
        customModeInputs.forEach(input => {
            input.disabled = true;
            input.style.opacity = '0.5';
            input.style.cursor = 'not-allowed';
        });
        
        // 학생 이름 입력하기 버튼
        const createStudentTable = document.getElementById('create-student-table') as HTMLButtonElement;
        if (createStudentTable) {
            createStudentTable.disabled = true;
            createStudentTable.style.opacity = '0.5';
            createStudentTable.style.cursor = 'not-allowed';
            createStudentTable.title = '확정된 자리 이력은 수정할 수 없습니다.';
        }
        
        // 옵션 설정 기억하기 버튼
        const saveOptions = document.getElementById('save-options') as HTMLButtonElement;
        if (saveOptions) {
            saveOptions.disabled = true;
            saveOptions.style.opacity = '0.5';
            saveOptions.style.cursor = 'not-allowed';
        }
        
        // 초기화 버튼 (읽기 전용 모드에서는 비활성화하지 않음 - 사용자가 초기화할 수 있도록)
        
        // 자리 배치하기 섹션의 체크박스들 (출력 영역)
        const avoidPrevSeat = document.getElementById('avoid-prev-seat') as HTMLInputElement;
        if (avoidPrevSeat) {
            avoidPrevSeat.disabled = true;
            avoidPrevSeat.style.opacity = '0.5';
            avoidPrevSeat.style.cursor = 'not-allowed';
        }
        
        const avoidPrevPartner = document.getElementById('avoid-prev-partner') as HTMLInputElement;
        if (avoidPrevPartner) {
            avoidPrevPartner.disabled = true;
            avoidPrevPartner.style.opacity = '0.5';
            avoidPrevPartner.style.cursor = 'not-allowed';
        }
    }

    /**
     * 사이드바 옵션들 활성화 (읽기 전용 모드 해제)
     */
    private enableSidebarOptions(): void {
        // 옵션1: 좌석 배치 형태
        const layoutTypeInputs = document.querySelectorAll('input[name="layout-type"]') as NodeListOf<HTMLInputElement>;
        layoutTypeInputs.forEach(input => {
            input.disabled = false;
            input.style.opacity = '1';
            input.style.cursor = '';
        });
        
        // 서브 옵션들
        const singleModeInputs = document.querySelectorAll('input[name="single-mode"]') as NodeListOf<HTMLInputElement>;
        singleModeInputs.forEach(input => {
            input.disabled = false;
            input.style.opacity = '1';
            input.style.cursor = '';
        });
        
        const pairModeInputs = document.querySelectorAll('input[name="pair-mode"]') as NodeListOf<HTMLInputElement>;
        pairModeInputs.forEach(input => {
            input.disabled = false;
            input.style.opacity = '1';
            input.style.cursor = '';
        });
        
        const groupSizeInputs = document.querySelectorAll('input[name="group-size"]') as NodeListOf<HTMLInputElement>;
        groupSizeInputs.forEach(input => {
            input.disabled = false;
            input.style.opacity = '1';
            input.style.cursor = '';
        });
        
        const reverseGenderOrder = document.getElementById('reverse-gender-order') as HTMLInputElement;
        if (reverseGenderOrder) {
            reverseGenderOrder.disabled = false;
            reverseGenderOrder.style.opacity = '1';
            reverseGenderOrder.style.cursor = '';
        }
        
        const groupGenderMix = document.getElementById('group-gender-mix') as HTMLInputElement;
        if (groupGenderMix) {
            groupGenderMix.disabled = false;
            groupGenderMix.style.opacity = '1';
            groupGenderMix.style.cursor = '';
        }
        
        const genderPairing = document.getElementById('gender-pairing') as HTMLInputElement;
        if (genderPairing) {
            genderPairing.disabled = false;
            genderPairing.style.opacity = '1';
            genderPairing.style.cursor = '';
        }
        
        // 옵션2: 학생 자리 수
        const maleStudents = document.getElementById('male-students') as HTMLInputElement;
        if (maleStudents) {
            maleStudents.disabled = false;
            maleStudents.style.opacity = '1';
            maleStudents.style.cursor = '';
        }
        
        const femaleStudents = document.getElementById('female-students') as HTMLInputElement;
        if (femaleStudents) {
            femaleStudents.disabled = false;
            femaleStudents.style.opacity = '1';
            femaleStudents.style.cursor = '';
        }
        
        // 옵션3: 분단 개수
        const numberOfPartitions = document.getElementById('number-of-partitions') as HTMLInputElement;
        if (numberOfPartitions) {
            numberOfPartitions.disabled = false;
            numberOfPartitions.style.opacity = '1';
            numberOfPartitions.style.cursor = '';
        }
        
        // 옵션4: 맞춤 구성
        const customModeInputs = document.querySelectorAll('input[name="custom-mode-2"]') as NodeListOf<HTMLInputElement>;
        customModeInputs.forEach(input => {
            input.disabled = false;
            input.style.opacity = '1';
            input.style.cursor = '';
        });
        
        // 학생 이름 입력하기 버튼
        const createStudentTable = document.getElementById('create-student-table') as HTMLButtonElement;
        if (createStudentTable) {
            createStudentTable.disabled = false;
            createStudentTable.style.opacity = '1';
            createStudentTable.style.cursor = 'pointer';
            createStudentTable.title = '';
        }
        
        // 옵션 설정 기억하기 버튼
        const saveOptions = document.getElementById('save-options') as HTMLButtonElement;
        if (saveOptions) {
            saveOptions.disabled = false;
            saveOptions.style.opacity = '1';
            saveOptions.style.cursor = 'pointer';
        }
        
        // 자리 배치하기 섹션의 체크박스들 (출력 영역)
        const avoidPrevSeat = document.getElementById('avoid-prev-seat') as HTMLInputElement;
        if (avoidPrevSeat) {
            avoidPrevSeat.disabled = false;
            avoidPrevSeat.style.opacity = '1';
            avoidPrevSeat.style.cursor = '';
        }
        
        const avoidPrevPartner = document.getElementById('avoid-prev-partner') as HTMLInputElement;
        if (avoidPrevPartner) {
            avoidPrevPartner.disabled = false;
            avoidPrevPartner.style.opacity = '1';
            avoidPrevPartner.style.cursor = '';
        }
    }

    /**
     * 읽기 전용 모드 해제
     */
    private disableReadOnlyMode(): void {
        if (!this.isReadOnlyMode) return;
        
        this.isReadOnlyMode = false;
        
        // 모든 좌석 카드의 드래그 활성화
        const seatsArea = document.getElementById('seats-area');
        if (seatsArea) {
            const allCards = Array.from(seatsArea.querySelectorAll('.student-seat-card')) as HTMLElement[];
            allCards.forEach(card => {
                card.setAttribute('draggable', 'true');
                card.style.cursor = 'grab';
                card.style.opacity = '1';
                card.classList.remove('read-only-seat');
            });
        }
        
        // "자리 배치하기" 버튼 활성화
        const arrangeBtn = document.getElementById('arrange-seats');
        if (arrangeBtn) {
            (arrangeBtn as HTMLButtonElement).disabled = false;
            arrangeBtn.style.opacity = '1';
            arrangeBtn.style.cursor = 'pointer';
            arrangeBtn.title = '';
        }
        
        // 사이드바 옵션들 활성화
        this.enableSidebarOptions();
        
        // 읽기 전용 모드 표시 배지 제거
        const readOnlyBadge = document.getElementById('read-only-badge');
        if (readOnlyBadge) {
            readOnlyBadge.remove();
        }
    }


    /**
     * 남녀 짝꿍 배치 렌더링
     */
    private renderGenderPairs(maleStudents: Student[], femaleStudents: Student[], partitionCount: number): void {
        const seatsArea = document.getElementById('seats-area');
        if (!seatsArea) return;

        // 남학생과 여학생을 무작위로 섞기
        const shuffledMales = RandomService.shuffle(maleStudents);
        const shuffledFemales = RandomService.shuffle(femaleStudents);

        const totalPairs = Math.min(shuffledMales.length, shuffledFemales.length);
        const rowsPerPartition = Math.ceil(totalPairs / partitionCount);

        let maleIndex = 0;
        let femaleIndex = 0;

        for (let row = 0; row < rowsPerPartition; row++) {
            for (let partition = 0; partition < partitionCount; partition++) {
                if (maleIndex >= shuffledMales.length || femaleIndex >= shuffledFemales.length) break;

                const pairContainer = document.createElement('div');
                pairContainer.style.display = 'flex';
                pairContainer.style.gap = '0px';

                // 남학생 카드
                const maleCard = this.createStudentNameCard(shuffledMales[maleIndex]);
                pairContainer.appendChild(maleCard);
                maleIndex++;

                // 여학생 카드
                const femaleCard = this.createStudentNameCard(shuffledFemales[femaleIndex]);
                pairContainer.appendChild(femaleCard);
                femaleIndex++;

                seatsArea.appendChild(pairContainer);
            }
        }
    }

    /**
     * 같은 성끼리 짝꿍 배치 렌더링
     */
    private renderSameGenderPairs(maleStudents: Student[], femaleStudents: Student[], partitionCount: number): void {
        const seatsArea = document.getElementById('seats-area');
        if (!seatsArea) return;

        // 남학생과 여학생을 무작위로 섞기
        const shuffledMales = RandomService.shuffle(maleStudents);
        const shuffledFemales = RandomService.shuffle(femaleStudents);

        const allPairs: Array<{student1: Student, student2: Student | null}> = [];

        // 남학생 짝꿍
        for (let i = 0; i < shuffledMales.length; i += 2) {
            allPairs.push({
                student1: shuffledMales[i],
                student2: (i + 1 < shuffledMales.length) ? shuffledMales[i + 1] : null
            });
        }

        // 여학생 짝꿍
        for (let i = 0; i < shuffledFemales.length; i += 2) {
            allPairs.push({
                student1: shuffledFemales[i],
                student2: (i + 1 < shuffledFemales.length) ? shuffledFemales[i + 1] : null
            });
        }

        const totalPairs = allPairs.length;
        const rowsPerPartition = Math.ceil(totalPairs / partitionCount);

        for (let row = 0; row < rowsPerPartition; row++) {
            for (let partition = 0; partition < partitionCount; partition++) {
                const pairIndex = row * partitionCount + partition;

                if (pairIndex < allPairs.length) {
                    const pair = allPairs[pairIndex];
                    const pairContainer = document.createElement('div');
                    pairContainer.style.display = 'flex';
                    pairContainer.style.gap = '0px';

                    // 첫 번째 카드
                    const card1 = this.createStudentNameCard(pair.student1);
                    pairContainer.appendChild(card1);

                    // 두 번째 카드 (있으면)
                    if (pair.student2) {
                        const card2 = this.createStudentNameCard(pair.student2);
                        pairContainer.appendChild(card2);
                    }

                    seatsArea.appendChild(pairContainer);
                }
            }
        }
    }

    /**
     * 학생 이름만 표시하는 카드 생성
     */
    private createStudentNameCard(student: Student): HTMLDivElement {
        const card = document.createElement('div');
        card.className = 'student-seat-card';
        
        // 성별에 따라 클래스 추가
        if (student.gender === 'M') {
            card.classList.add('gender-m');
        } else {
            card.classList.add('gender-f');
        }
        
        // 이름만 표시 (가운데 정렬)
        const nameDiv = document.createElement('div');
        nameDiv.className = 'student-name';
        nameDiv.textContent = student.name;
        nameDiv.style.textAlign = 'center';
        nameDiv.style.fontSize = '1.1em';
        nameDiv.style.fontWeight = 'bold';
        
        card.appendChild(nameDiv);
        
        return card;
    }


    /**
     * 자리 배치도 저장 처리
     */
    private handleSaveLayout(): void {
        this.printExportManager.saveLayoutAsHtml();
    }


    /**
     * 뷰어 모드 활성화 (자리 배치도만 표시)
     */
    private enableViewerMode(viewData: string): void {
        try {
            // 입력 데이터 길이 검증 (보안)
            if (!viewData || viewData.length > 10000) {
                throw new Error('공유 데이터가 유효하지 않습니다.');
            }
            
            // URL-safe Base64 디코딩
            const base64Data = viewData
                .replace(/-/g, '+')
                .replace(/_/g, '/');
            
            // 패딩 추가 (필요한 경우)
            const padding = base64Data.length % 4;
            const paddedData = padding ? base64Data + '='.repeat(4 - padding) : base64Data;
            
            // Base64 디코딩
            let decodedData: string;
            try {
                decodedData = decodeURIComponent(escape(atob(paddedData)));
            } catch (e) {
                try {
                    decodedData = decodeURIComponent(escape(atob(viewData)));
                } catch (e2) {
                    throw new Error('공유 데이터 디코딩에 실패했습니다.');
                }
            }
            
            // JSON 파싱
            let shareInfo: ShareInfo;
            try {
                shareInfo = JSON.parse(decodedData) as ShareInfo;
            } catch (e) {
                throw new Error('공유 데이터 형식이 올바르지 않습니다.');
            }
            
            // 데이터 검증
            if (!this.validateSharedData(shareInfo)) {
                throw new Error('공유 데이터 검증에 실패했습니다.');
            }
            
            // 만료 시간 확인 (36진수로 저장된 경우 파싱)
            if (shareInfo.e) {
                let expiresAt: number;
                if (typeof shareInfo.e === 'string') {
                    // 36진수로 저장된 경우
                    expiresAt = parseInt(shareInfo.e, 36);
                } else {
                    // 숫자로 저장된 경우 (이전 형식 호환)
                    expiresAt = shareInfo.e as number;
                }
                if (Date.now() > expiresAt) {
                    throw new Error('이 공유 링크는 만료되었습니다.');
                }
            }
            
            // 비밀번호 확인
            if (shareInfo.p) {
                const passwordHash = shareInfo.p as string;
                const userPassword = prompt('이 공유 링크는 비밀번호가 필요합니다. 비밀번호를 입력하세요:');
                if (!userPassword) {
                    throw new Error('비밀번호가 필요합니다.');
                }
                
                // 비밀번호 해시 계산
                let hash = 0;
                for (let i = 0; i < userPassword.length; i++) {
                    const char = userPassword.charCodeAt(i);
                    hash = ((hash << 5) - hash) + char;
                    hash = hash & hash;
                }
                const userPasswordHash = Math.abs(hash).toString(36);
                
                if (userPasswordHash !== passwordHash) {
                    throw new Error('비밀번호가 올바르지 않습니다.');
                }
            }
            
            // 학생 정보 추출 (새로운 압축 형식 지원)
            const studentDataList = shareInfo.s || shareInfo.students || [];
            const nameArray = shareInfo.n || shareInfo.names || [];
            const gridColumns = shareInfo.l || shareInfo.layout || '';
            
            // 만료 시간 처리 (상대 시간을 절대 시간으로 변환)
            if (shareInfo.e && typeof shareInfo.e === 'number') {
                // 상대 시간(시간 단위)인 경우 절대 시간으로 변환
                if (shareInfo.e < 1000000000000) { // 밀리초가 아닌 경우 (상대 시간)
                    const expiresAt = Date.now() + (shareInfo.e * 60 * 60 * 1000);
                    shareInfo.e = expiresAt;
                }
            }
            
            // 학생 데이터 생성 (새로운 압축 형식: [이름ID, 성별])
            this.students = studentDataList.map((student: SharedStudentData, index: number) => {
                if (Array.isArray(student)) {
                    // 새로운 형식: [이름ID, 성별]
                    if (nameArray.length > 0 && typeof student[0] === 'number') {
                        const nameId = student[0] as number;
                        const name = nameArray[nameId] || `학생${index + 1}`;
                        const gender = (student[1] === 'F' ? 'F' : 'M') as 'M' | 'F';
                        return {
                            id: index + 1,
                            name: String(name).trim() || `학생${index + 1}`,
                            gender: gender
                        };
                    } else {
                        // 이전 형식: [이름, 성별]
                    const name = String(student[0] || '').trim();
                    const gender = (student[1] === 'F' ? 'F' : 'M') as 'M' | 'F';
                    return {
                        id: index + 1,
                        name: name || `학생${index + 1}`,
                        gender: gender
                    };
                    }
                } else {
                    // 객체 형식: {name: string, gender: 'M' | 'F'}
                    const name = String(student.name || '').trim();
                    const gender = (student.gender === 'F' ? 'F' : 'M') as 'M' | 'F';
                    return {
                        id: index + 1,
                        name: name || `학생${index + 1}`,
                        gender: gender
                    };
                }
            });
            
            // 성별별 학생 수 계산
            let maleCount = 0;
            let femaleCount = 0;
            this.students.forEach(student => {
                if (student.gender === 'M') {
                    maleCount++;
                } else {
                    femaleCount++;
                }
            });
            
            // 사이드바 입력 업데이트 (숨겨져 있지만 데이터는 설정)
            const maleCountInput = document.getElementById('male-students') as HTMLInputElement;
            const femaleCountInput = document.getElementById('female-students') as HTMLInputElement;
            if (maleCountInput) maleCountInput.value = maleCount.toString();
            if (femaleCountInput) femaleCountInput.value = femaleCount.toString();
            
            // 미리보기 업데이트 (좌석 카드 생성)
            this.updatePreviewForGenderCounts();
            
            // 자리 배치 렌더링 (학생 테이블 생성 없이 직접 렌더링)
            this.setTimeoutSafe(() => {
                // 좌석 영역 가져오기
                const seatsArea = document.getElementById('seats-area');
                if (!seatsArea) {
                    throw new Error('좌석 영역을 찾을 수 없습니다.');
                }
                
                // 그리드 컬럼 설정
                if (gridColumns) {
                    seatsArea.style.gridTemplateColumns = gridColumns;
                }
                
                // 좌석 카드가 없으면 예시 카드 렌더링
                const existingCards = seatsArea.querySelectorAll('.student-seat-card');
                if (existingCards.length === 0) {
                    this.renderExampleCards();
                }
                
                // 학생들을 좌석에 배치 (더 긴 대기 시간)
                this.setTimeoutSafe(() => {
                    const cards = seatsArea.querySelectorAll('.student-seat-card');
                    
                    // 카드가 없으면 다시 렌더링 시도
                    if (cards.length === 0) {
                        logger.warn('좌석 카드가 없습니다. 다시 렌더링 시도...');
                        this.renderExampleCards();
                        this.setTimeoutSafe(() => {
                            this.updateSeatsFromCards();
                            // 인쇄용 화면 표시
                            this.setTimeoutSafe(() => {
                                this.showPrintView();
                            }, 500);
                        }, 500);
                        return;
                    }
                    
                    this.updateSeatsFromCards();
                    // 인쇄용 화면 표시 (이미지 변환 대신)
                    this.setTimeoutSafe(() => {
                        this.showPrintView();
                    }, 500);
                }, 500);
            }, 500);
            
        } catch (error) {
            logger.error('뷰어 모드 로드 실패:', error);
            // 안전한 에러 메시지 표시 (innerHTML 대신 textContent 사용)
            const errorDiv = document.createElement('div');
            errorDiv.style.cssText = 'padding: 20px; text-align: center;';
            const h2 = document.createElement('h2');
            h2.textContent = '자리 배치도를 불러올 수 없습니다.';
            const p = document.createElement('p');
            p.textContent = '공유 링크가 유효하지 않거나 만료되었을 수 있습니다.';
            errorDiv.appendChild(h2);
            errorDiv.appendChild(p);
            document.body.innerHTML = '';
            document.body.appendChild(errorDiv);
        }
    }
    
    /**
     * 좌석 카드에서 학생 정보 업데이트
     */
    private updateSeatsFromCards(): void {
        const seatsArea = document.getElementById('seats-area');
        if (!seatsArea) return;
        
                    const cards = seatsArea.querySelectorAll('.student-seat-card');
                    let cardIndex = 0;
                    
                    this.students.forEach((student) => {
                        if (cardIndex < cards.length) {
                            const card = cards[cardIndex] as HTMLElement;
                            const nameDiv = card.querySelector('.student-name') as HTMLElement;
                            if (nameDiv) {
                                nameDiv.textContent = student.name;
                                // 성별 클래스 설정
                                card.classList.remove('gender-m', 'gender-f');
                                card.classList.add(`gender-${student.gender.toLowerCase()}`);
                            }
                            cardIndex++;
                        }
                    });
                    
                    // 빈 좌석 초기화
                    for (let i = cardIndex; i < cards.length; i++) {
                        const card = cards[i] as HTMLElement;
                        const nameDiv = card.querySelector('.student-name') as HTMLElement;
                        if (nameDiv) {
                            nameDiv.textContent = '';
                        }
                    }
    }
    
    /**
     * 인쇄용 화면 표시 (QR 스캔 시 사용)
     */
    private showPrintView(): void {
        try {
            // UI 숨기기
            this.setupViewerModeUI();
            
            // 잠시 대기 (렌더링 완료 보장)
            setTimeout(() => {
                // classroom-layout 가져오기
                const classroomLayout = document.getElementById('classroom-layout');
                if (!classroomLayout) {
                    throw new Error('교실 레이아웃을 찾을 수 없습니다.');
                }
                
                // 모든 UI 숨기고 인쇄용 화면만 표시
                document.body.innerHTML = '';
                document.body.style.cssText = 'margin: 0; padding: 0; background: #f5f5f5; display: flex; flex-direction: column; justify-content: flex-start; align-items: center; min-height: 100vh; overflow-x: hidden;';
                
                // 상단 컨트롤 바 (인쇄 버튼)
                const controlBar = document.createElement('div');
                controlBar.style.cssText = 'width: 100%; background: #fff; padding: 15px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); display: flex; justify-content: center; gap: 10px; position: sticky; top: 0; z-index: 1000;';
                
                const printBtn = document.createElement('button');
                printBtn.textContent = '🖨️ 인쇄하기';
                printBtn.style.cssText = 'padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 5px; font-size: 16px; cursor: pointer; font-weight: bold;';
                printBtn.onclick = () => window.print();
                
                const closeBtn = document.createElement('button');
                closeBtn.textContent = '✕ 닫기';
                closeBtn.style.cssText = 'padding: 10px 20px; background: #6c757d; color: white; border: none; border-radius: 5px; font-size: 16px; cursor: pointer;';
                closeBtn.onclick = () => window.location.href = window.location.pathname;
                
                controlBar.appendChild(printBtn);
                controlBar.appendChild(closeBtn);
                
                // 컨테이너 생성
                const container = document.createElement('div');
                container.style.cssText = 'width: 100%; max-width: 100%; padding: 20px; box-sizing: border-box; flex: 1;';
                
                // classroom-layout 복사 및 스타일 조정
                const printLayout = classroomLayout.cloneNode(true) as HTMLElement;
                printLayout.style.cssText = `
                    min-height: auto;
                    background: #ffffff;
                    border: 2px solid #ddd;
                    border-radius: 10px;
                    padding: 20px;
                    position: relative;
                    width: 100%;
                    max-width: 100%;
                    box-sizing: border-box;
                    margin: 0 auto;
                `;
                
                // 스마트폰 화면에 맞게 조절
                const viewportWidth = window.innerWidth || 375;
                const maxContentWidth = Math.min(viewportWidth - 40, 800);
                printLayout.style.maxWidth = `${maxContentWidth}px`;
                
                // 인쇄용 화면에서 좌석 카드 크기를 반응형으로 조절
                const style = document.createElement('style');
                style.id = 'print-view-responsive-style';
                
                // 기존 그리드 레이아웃 가져오기
                const seatsArea = printLayout.querySelector('#seats-area') as HTMLElement;
                const originalGridTemplate = seatsArea ? window.getComputedStyle(seatsArea).gridTemplateColumns : '';
                
                // 화면 크기에 따라 카드 크기 계산
                const calcViewportWidth = window.innerWidth || 375;
                const containerPadding = 40; // 좌우 패딩
                const availableWidth = Math.min(calcViewportWidth - containerPadding, maxContentWidth - containerPadding);
                
                // 카드 개수 추정 (기존 그리드 컬럼 수 유지)
                const cardCount = seatsArea ? seatsArea.querySelectorAll('.student-seat-card').length : 0;
                let estimatedColumns = 6; // 기본값
                
                if (originalGridTemplate && originalGridTemplate !== 'none') {
                    // 기존 그리드 컬럼 수 추출
                    const match = originalGridTemplate.match(/repeat\((\d+)/);
                    if (match) {
                        estimatedColumns = parseInt(match[1], 10);
                    }
                }
                
                // 카드 크기 계산 (그리드 컬럼 수 유지하면서 화면에 맞게)
                const gap = 8;
                const cardSize = Math.floor((availableWidth - (gap * (estimatedColumns - 1))) / estimatedColumns);
                const minCardSize = Math.max(50, cardSize); // 최소 50px
                const maxCardSize = 120; // 최대 120px
                const finalCardSize = Math.min(maxCardSize, Math.max(minCardSize, cardSize));
                
                style.textContent = `
                    /* 인쇄용 화면에서 좌석 카드 반응형 크기 조절 */
                    .student-seat-card {
                        width: ${finalCardSize}px !important;
                        height: ${finalCardSize}px !important;
                        min-width: ${finalCardSize}px !important;
                        max-width: ${finalCardSize}px !important;
                        flex-shrink: 0 !important;
                    }
                    
                    /* 좌석 영역 그리드 간격 조절 */
                    #seats-area {
                        gap: ${gap}px !important;
                    }
                    
                    /* 작은 화면에서는 더 작은 카드 */
                    @media (max-width: 480px) {
                        .student-seat-card {
                            width: 50px !important;
                            height: 50px !important;
                            min-width: 50px !important;
                            max-width: 50px !important;
                            padding: 3px !important;
                        }
                        
                        .student-name {
                            font-size: 0.75em !important;
                        }
                        
                        #seats-area {
                            gap: 5px !important;
                        }
                    }
                    
                    /* 중간 화면 */
                    @media (min-width: 481px) and (max-width: 768px) {
                        .student-seat-card {
                            width: 70px !important;
                            height: 70px !important;
                            min-width: 70px !important;
                            max-width: 70px !important;
                        }
                        
                        #seats-area {
                            gap: 6px !important;
                        }
                    }
                `;
                document.head.appendChild(style);
                
                container.appendChild(printLayout);
                document.body.appendChild(controlBar);
                document.body.appendChild(container);
                
                logger.info('인쇄용 화면 표시 완료');
            }, 500);
        } catch (error) {
            logger.error('인쇄용 화면 표시 실패:', error);
            // 실패 시 에러 메시지 표시
            const errorDiv = document.createElement('div');
            errorDiv.style.cssText = 'padding: 20px; text-align: center; color: #dc3545;';
            errorDiv.innerHTML = '<h2>화면 표시 실패</h2><p>자리 배치도를 표시할 수 없습니다.</p>';
            document.body.innerHTML = '';
            document.body.appendChild(errorDiv);
        }
    }
    
    /**
     * 자리 배치도를 이미지로 변환하여 이미지만 표시
     */
    private async convertLayoutToImage(): Promise<void> {
        try {
            // 먼저 UI 숨기기 (이미지 변환 전)
            this.setupViewerModeUI();
            
            // 렌더링 완료를 기다림 (충분한 시간 확보)
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // classroom-layout 전체를 캡처 (칠판, 교탁, 좌석 모두 포함)
            const classroomLayout = document.getElementById('classroom-layout');
            if (!classroomLayout) {
                throw new Error('교실 레이아웃을 찾을 수 없습니다.');
            }
            
            // 전체 크기 계산 (실제 콘텐츠 크기 - 칠판, 교탁, 좌석 모두 포함)
            // 요소의 실제 크기를 정확히 계산
            const rect = classroomLayout.getBoundingClientRect();
            
            // 모든 자식 요소의 실제 크기를 계산하여 전체 영역 파악
            const seatsArea = document.getElementById('seats-area');
            const blackboardArea = document.getElementById('blackboard-area');
            
            // 좌석 영역의 실제 크기 계산 (모든 좌석 카드 포함)
            let seatsAreaHeight = 0;
            let seatsAreaWidth = 0;
            let maxBottom = 0;
            
            if (seatsArea) {
                // 모든 좌석 카드의 실제 위치 계산
                const seatCards = seatsArea.querySelectorAll('.student-seat-card');
                if (seatCards.length > 0) {
                    // 각 카드의 bottom 위치를 찾아서 가장 아래쪽 위치 계산
                    seatCards.forEach((card) => {
                        const cardRect = card.getBoundingClientRect();
                        const seatsRect = seatsArea.getBoundingClientRect();
                        // 상대 위치 계산 (seatsArea 기준)
                        const relativeBottom = (cardRect.bottom - seatsRect.top) + (cardRect.height || 0);
                        maxBottom = Math.max(maxBottom, relativeBottom);
                    });
                    
                    // 좌석 영역의 실제 높이 = 가장 아래쪽 카드의 bottom + 여유 공간
                    seatsAreaHeight = Math.max(
                        maxBottom + 20, // 여유 공간 추가
                        seatsArea.scrollHeight,
                        seatsArea.offsetHeight,
                        seatsArea.getBoundingClientRect().height
                    );
                } else {
                    // 카드가 없으면 기본 높이 사용
                    seatsAreaHeight = seatsArea.scrollHeight || seatsArea.offsetHeight || seatsArea.getBoundingClientRect().height;
                }
                
                seatsAreaWidth = Math.max(
                    seatsArea.scrollWidth,
                    seatsArea.offsetWidth,
                    seatsArea.getBoundingClientRect().width
                );
            }
            
            // 칠판 영역 고려 (절대 위치이므로 포함)
            const blackboardHeight = blackboardArea ? (blackboardArea.getBoundingClientRect().height || 40) : 40;
            
            // 전체 높이 계산: 상단 패딩(20px) + 칠판(20px top + 높이) + 좌석 영역(margin-top 80px + 실제 높이) + 하단 패딩(20px)
            // 좌석 영역은 margin-top 80px부터 시작하므로, 전체 높이는 80 + seatsAreaHeight
            const calculatedHeight = 20 + // 상단 패딩
                (20 + blackboardHeight) + // 칠판 영역
                seatsAreaHeight + // 좌석 영역 높이
                20; // 하단 패딩
            
            const totalHeight = Math.max(
                calculatedHeight,
                classroomLayout.scrollHeight,
                classroomLayout.offsetHeight,
                80 + seatsAreaHeight, // 좌석 영역 시작 위치 + 높이
                rect.height
            );
            
            // 전체 너비 계산
            const totalWidth = Math.max(
                classroomLayout.scrollWidth,
                classroomLayout.offsetWidth,
                seatsAreaWidth + 40, // 좌석 영역 너비 + 좌우 패딩
                rect.width
            );
            
            // 스마트폰 화면 크기에 맞게 최대 너비 설정 (viewport width 고려)
            const viewportWidth = window.innerWidth || 375; // 기본값: iPhone SE 크기
            const maxWidth = Math.min(viewportWidth - 20, 800); // 패딩 고려
            
            // 전체 영역을 캡처하기 위해 스크롤 위치 저장 및 리셋
            const originalScrollX = window.scrollX;
            const originalScrollY = window.scrollY;
            window.scrollTo(0, 0);
            
            // 요소의 원래 스타일 저장
            const originalOverflow = classroomLayout.style.overflow;
            const originalPosition = classroomLayout.style.position;
            const originalMaxHeight = classroomLayout.style.maxHeight;
            const originalMinHeight = classroomLayout.style.minHeight;
            
            // 전체 영역이 보이도록 스타일 조정
            classroomLayout.style.overflow = 'visible';
            classroomLayout.style.position = 'relative';
            if (classroomLayout.style.maxHeight) {
                classroomLayout.style.maxHeight = 'none';
            }
            // 최소 높이를 실제 높이로 설정하여 전체 영역 확보
            classroomLayout.style.minHeight = `${totalHeight}px`;
            
            // html2canvas로 이미지 변환 (전체 캡처, 스마트폰 최적화)
            // width와 height를 명시하지 않으면 자동으로 전체 영역을 캡처
            // html2canvas v1.x 옵션 (타입 정의가 v0.5 기준이므로 any 캐스트)
            // 이미지 저장을 누를 때만 불러온다 (약 200KB, 첫 화면에는 필요 없다)
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await (html2canvas as any)(classroomLayout, {
                backgroundColor: '#ffffff',
                scale: 2, // 고해상도 (스마트폰에서도 선명하게)
                logging: false,
                useCORS: true,
                allowTaint: false,
                scrollX: 0,
                scrollY: 0,
                ignoreElements: (element: Element) => {
                    // 숨겨진 요소는 제외
                    const style = window.getComputedStyle(element);
                    return style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0';
                }
            });
            
            logger.info('이미지 캡처 완료', {
                canvasWidth: canvas.width,
                canvasHeight: canvas.height,
                expectedWidth: totalWidth,
                expectedHeight: totalHeight,
                seatsAreaHeight
            });
            
            // 스타일 복원
            classroomLayout.style.overflow = originalOverflow;
            classroomLayout.style.position = originalPosition;
            if (originalMaxHeight) {
                classroomLayout.style.maxHeight = originalMaxHeight;
            }
            if (originalMinHeight) {
                classroomLayout.style.minHeight = originalMinHeight;
            }
            
            // 스크롤 위치 복원
            window.scrollTo(originalScrollX, originalScrollY);
            
            // 캔버스 크기를 스마트폰에 맞게 조정
            let finalCanvas = canvas;
            if (canvas.width > maxWidth) {
                const newWidth = maxWidth;
                const newHeight = (canvas.height * maxWidth) / canvas.width;
                const resizedCanvas = document.createElement('canvas');
                resizedCanvas.width = newWidth;
                resizedCanvas.height = newHeight;
                const ctx = resizedCanvas.getContext('2d');
                if (ctx) {
                    // 고품질 리사이징
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctx.drawImage(canvas, 0, 0, newWidth, newHeight);
                    finalCanvas = resizedCanvas;
                }
            }
            
            // 이미지 데이터 URL 생성 (품질 조정)
            const imageDataUrl = finalCanvas.toDataURL('image/png', 0.95);
            
            // 모든 UI 숨기고 이미지만 표시 (스마트폰 화면에 맞게)
            document.body.innerHTML = '';
            document.body.style.cssText = 'margin: 0; padding: 10px; background: #f5f5f5; display: flex; justify-content: center; align-items: flex-start; min-height: 100vh; overflow-x: hidden;';
            
            const imageContainer = document.createElement('div');
            imageContainer.style.cssText = 'text-align: center; max-width: 100%; width: 100%; display: flex; flex-direction: column; align-items: center;';
            
            const img = document.createElement('img');
            img.src = imageDataUrl;
            // 스마트폰 화면 크기에 딱 맞게 조절
            img.style.cssText = 'max-width: 100%; width: 100%; height: auto; border: 2px solid #ddd; border-radius: 8px; box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1); background: white; display: block; object-fit: contain;';
            img.alt = '자리 배치도';
            
            imageContainer.appendChild(img);
            document.body.appendChild(imageContainer);
            
            logger.info('자리 배치도 이미지 변환 완료', { width: finalCanvas.width, height: finalCanvas.height });
        } catch (error) {
            logger.error('이미지 변환 실패:', error);
            // 이미지 변환 실패 시 원래 뷰어 모드 유지
            const errorDiv = document.createElement('div');
            errorDiv.style.cssText = 'padding: 20px; text-align: center; color: #dc3545;';
            errorDiv.innerHTML = '<h2>이미지 변환 실패</h2><p>자리 배치도를 이미지로 변환할 수 없습니다.</p>';
            document.body.innerHTML = '';
            document.body.appendChild(errorDiv);
        }
    }
    
    /**
     * 뷰어 모드 UI 설정 (사이드바, 헤더 버튼 숨기기)
     */
    private setupViewerModeUI(): void {
        // 사이드바 숨기기
        const sidebar = document.getElementById('sidebar');
        if (sidebar) {
            sidebar.style.display = 'none';
        }
        
        // 사이드바 토글 버튼 숨기기
        const sidebarToggle = document.getElementById('sidebar-toggle-btn');
        if (sidebarToggle) {
            sidebarToggle.style.display = 'none';
        }
        
        // 헤더 숨기기
        const header = document.querySelector('.top-header');
        if (header) {
            (header as HTMLElement).style.display = 'none';
        }
        
        // 메인 컨테이너를 전체 화면으로
        const mainContainer = document.querySelector('.main-container');
        if (mainContainer) {
            (mainContainer as HTMLElement).style.margin = '0';
            (mainContainer as HTMLElement).style.padding = '0';
        }
        
        // 메인 콘텐츠 영역 스타일 조정
        const mainContent = document.querySelector('.main-content');
        if (mainContent) {
            (mainContent as HTMLElement).style.width = '100%';
            (mainContent as HTMLElement).style.margin = '0';
            (mainContent as HTMLElement).style.padding = '10px';
        }
        
        // 메인 헤더 숨기기 (자리 배치도 제목과 버튼들)
        const mainHeader = document.querySelector('.main-header');
        if (mainHeader) {
            (mainHeader as HTMLElement).style.display = 'none';
        }
        
        // 결과 컨테이너 스타일 조정 (전체 화면)
        const resultContainer = document.getElementById('output-section');
        if (resultContainer) {
            resultContainer.style.margin = '0';
            resultContainer.style.padding = '0';
        }
        
        // 카드 레이아웃 컨테이너 스타일 조정
        const cardLayoutContainer = document.getElementById('card-layout-container');
        if (cardLayoutContainer) {
            cardLayoutContainer.style.margin = '0';
            cardLayoutContainer.style.padding = '10px';
        }
        
        // 교실 레이아웃 스타일 조정 (전체 화면)
        const classroomLayout = document.getElementById('classroom-layout');
        if (classroomLayout) {
            classroomLayout.style.minHeight = 'calc(100vh - 20px)';
            classroomLayout.style.padding = '10px';
        }
        
        // body 스타일 조정 (여백 제거)
        document.body.style.margin = '0';
        document.body.style.padding = '0';
        
        // 뷰포트 메타 태그 확인 및 추가 (모바일 최적화)
        let viewportMeta = document.querySelector('meta[name="viewport"]');
        if (!viewportMeta) {
            viewportMeta = document.createElement('meta');
            viewportMeta.setAttribute('name', 'viewport');
            document.head.appendChild(viewportMeta);
        }
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    }

    /**
     * 공유된 배치 데이터 검증
     */
    private validateSharedData(shareInfo: ShareInfo): boolean {
        // 타입 검증
        const type = shareInfo.t || shareInfo.type;
        if (type !== 'sa' && type !== 'seating-arrangement') {
            return false;
        }
        
        // 학생 데이터 검증
        const studentDataList = shareInfo.s || shareInfo.students || [];
        if (!Array.isArray(studentDataList)) {
            return false;
        }
        
        // 최대 학생 수 제한 (보안 및 성능)
        if (studentDataList.length > 200) {
            return false;
        }
        
        // 이름 배열 검증 (새로운 압축 형식)
        const nameArray = shareInfo.n || shareInfo.names || [];
        if (nameArray.length > 0) {
            if (!Array.isArray(nameArray)) {
                return false;
            }
            for (const name of nameArray) {
                if (typeof name !== 'string' || name.length > 50) {
                    return false;
                }
            }
        }
        
        // 각 학생 데이터 검증
        for (const student of studentDataList) {
            if (Array.isArray(student)) {
                // 새로운 압축 형식: [이름ID, 성별] 또는 이전 형식: [이름, 성별]
                if (student.length < 2) {
                    return false;
                }
                // 이름ID가 숫자인 경우 (새로운 형식)
                if (typeof student[0] === 'number') {
                    const nameId = student[0] as number;
                    if (nameId < 0 || (nameArray.length > 0 && nameId >= nameArray.length)) {
                        return false;
                    }
                } else if (typeof student[0] === 'string') {
                    // 이전 형식: [이름, 성별]
                    if (student[0].length > 50) {
                        return false;
                    }
                } else {
                    return false;
                }
                if (student[1] !== 'M' && student[1] !== 'F') {
                    return false;
                }
            } else if (typeof student === 'object' && student !== null) {
                // 객체 형식: {name: string, gender: 'M' | 'F'}
                if (typeof student.name !== 'string' || student.name.length > 50) {
                    return false;
                }
                if (student.gender !== 'M' && student.gender !== 'F') {
                    return false;
                }
            } else {
                return false;
            }
        }
        
        // 레이아웃 검증 (선택적)
        const gridColumns = shareInfo.l || shareInfo.layout;
        if (gridColumns && (typeof gridColumns !== 'string' || gridColumns.length > 500)) {
            return false;
        }
        
        return true;
    }

    /**
     * 공유된 배치 데이터 로드
     */
    private loadSharedLayout(shareData: string): void {
        try {
            // 입력 데이터 길이 검증 (보안)
            if (!shareData || shareData.length > 10000) {
                throw new Error('공유 데이터가 유효하지 않습니다.');
            }
            
            // URL-safe Base64 디코딩 (+, /, = 문자 복원)
            const base64Data = shareData
                .replace(/-/g, '+')
                .replace(/_/g, '/');
            
            // 패딩 추가 (필요한 경우)
            const padding = base64Data.length % 4;
            const paddedData = padding ? base64Data + '='.repeat(4 - padding) : base64Data;
            
            // Base64 디코딩
            let decodedData: string;
            try {
                decodedData = decodeURIComponent(escape(atob(paddedData)));
            } catch (e) {
                // 이전 형식 호환성: 일반 Base64 디코딩 시도
                try {
                decodedData = decodeURIComponent(escape(atob(shareData)));
                } catch (e2) {
                    throw new Error('공유 데이터 디코딩에 실패했습니다.');
                }
            }
            
            // JSON 파싱
            let shareInfo: ShareInfo;
            try {
                shareInfo = JSON.parse(decodedData) as ShareInfo;
            } catch (e) {
                throw new Error('공유 데이터 형식이 올바르지 않습니다.');
            }
            
            // 데이터 검증
            if (!this.validateSharedData(shareInfo)) {
                throw new Error('공유 데이터 검증에 실패했습니다.');
            }
            
            // 학생 정보로부터 배치 복원 (압축된 형식과 이전 형식 모두 지원)
            const studentDataList = shareInfo.s || shareInfo.students || [];
            const nameArray = shareInfo.n || shareInfo.names || [];
            const gridColumns = shareInfo.l || shareInfo.layout || '';
            
            // 학생 데이터 생성 (새로운 압축 형식: [이름ID, 성별] 또는 이전 형식 지원)
            this.students = studentDataList.map((student: SharedStudentData, index: number) => {
                if (Array.isArray(student)) {
                    // 새로운 형식: [이름ID, 성별]
                    if (nameArray.length > 0 && typeof student[0] === 'number') {
                        const nameId = student[0] as number;
                        const name = nameArray[nameId] || `학생${index + 1}`;
                        const gender = (student[1] === 'F' ? 'F' : 'M') as 'M' | 'F';
                        return {
                            id: index + 1,
                            name: String(name).trim() || `학생${index + 1}`,
                            gender: gender
                        };
                    } else {
                        // 이전 형식: [이름, 성별]
                    const name = String(student[0] || '').trim();
                    const gender = (student[1] === 'F' ? 'F' : 'M') as 'M' | 'F';
                    return {
                        id: index + 1,
                        name: name || `학생${index + 1}`,
                        gender: gender
                    };
                    }
                } else {
                    // 객체 형식: {name: string, gender: 'M' | 'F'}
                    const name = String(student.name || '').trim();
                    const gender = (student.gender === 'F' ? 'F' : 'M') as 'M' | 'F';
                    return {
                        id: index + 1,
                        name: name || `학생${index + 1}`,
                        gender: gender
                    };
                }
            });
            
            // 성별별 학생 수 계산
            let maleCount = 0;
            let femaleCount = 0;
            this.students.forEach(student => {
                if (student.gender === 'M') {
                    maleCount++;
                } else {
                    femaleCount++;
                }
            });
            
            // 사이드바 입력 업데이트
            const maleCountInput = document.getElementById('male-students') as HTMLInputElement;
            const femaleCountInput = document.getElementById('female-students') as HTMLInputElement;
            if (maleCountInput) maleCountInput.value = maleCount.toString();
            if (femaleCountInput) femaleCountInput.value = femaleCount.toString();
            
            // 미리보기 업데이트
            this.updatePreviewForGenderCounts();
            
            // 학생 테이블 생성
            this.setTimeoutSafe(() => {
                const totalStudents = this.students.length;
                this.studentTableManager.createStudentTable(totalStudents);
                
                // 학생 정보 입력 (이름과 성별)
                this.setTimeoutSafe(() => {
                    this.students.forEach((student, index) => {
                        const nameInput = document.getElementById(`student-name-${index + 1}`) as HTMLInputElement;
                        const genderSelect = document.getElementById(`student-gender-${index + 1}`) as HTMLSelectElement;
                        if (nameInput) {
                            nameInput.value = student.name;
                        }
                        if (genderSelect) {
                            genderSelect.value = student.gender;
                        }
                    });
                    
                    // 자리 배치 실행
                    this.setTimeoutSafe(() => {
                        const arrangeBtn = document.getElementById('arrange-seats') as HTMLButtonElement;
                        if (arrangeBtn) {
                            arrangeBtn.click();
                        }
                        
                        // 그리드 컬럼 설정 (레이아웃 복원)
                        this.setTimeoutSafe(() => {
                            const seatsArea = document.getElementById('seats-area');
                            if (seatsArea && gridColumns) {
                                seatsArea.style.gridTemplateColumns = gridColumns;
                            }
                            
                            this.outputModule.showSuccess('공유된 자리 배치도가 로드되었습니다.');
                        }, 500);
                    }, 500);
                }, 500);
            }, 300);
            
        } catch (error) {
            logger.error('공유 데이터 로드 실패:', error);
            this.outputModule.showError('공유된 자리 배치도를 로드할 수 없습니다.');
            
            // 실패 시 기본 레이아웃 표시
            this.renderInitialExampleLayout();
            this.setTimeoutSafe(() => {
                this.updatePreviewForGenderCounts();
            }, 100);
        }
    }

    /**
     * 간단한 공유 주소(URL) 생성 (압축된 형식, 뷰어 모드)
     * @param seatsHtml 좌석 HTML
     * @param gridColumns 그리드 컬럼 설정
     * @param dateString 날짜 문자열
     * @param expiresIn 만료 시간 (시간 단위, 선택사항)
     * @param password 비밀번호 (선택사항)
     * @deprecated 공유 기능이 제거되었습니다. 이 메서드는 더 이상 사용되지 않습니다.
     */
    private generateShareUrl(_seatsHtml: string, _gridColumns: string, _dateString: string, _expiresIn?: number, _password?: string): string {
        // 공유 기능이 제거되었으므로 빈 문자열 반환
        return '';
    }

    /**
     * 모달 창으로 자리 배치도 공유하기 (개선된 버전: QR 코드, 만료 시간, 비밀번호 지원)
     * @deprecated 공유 기능이 제거되었습니다. 이 메서드는 더 이상 사용되지 않습니다.
     */
    private async showShareModal(_shareUrl: string, _options?: {expiresIn?: number, password?: string}): Promise<void> {
        // 공유 기능이 제거되었으므로 아무 작업도 하지 않음
    }

    /**
     * QR 코드 인쇄
     * @deprecated 공유 기능이 제거되었습니다. 이 메서드는 더 이상 사용되지 않습니다.
     */
    private printQRCode(_url: string, _qrContainer: HTMLElement): void {
        // 공유 기능이 제거되었으므로 아무 작업도 하지 않음
    }

    /**
     * QR 코드 생성
     */
    /**
     * 사용설명서 모달 표시
     */
    private showUserManual(): void {
        // 모달 창 생성
        const modal = document.createElement('div');
        modal.id = 'user-manual-modal';
        modal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.6);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 10000;
            overflow-y: auto;
            padding: 20px;
        `;

        const modalContent = document.createElement('div');
        modalContent.style.cssText = `
            background: white;
            padding: 30px;
            border-radius: 12px;
            max-width: 800px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
        `;

        const title = document.createElement('h2');
        title.textContent = '📖 사용설명서';
        title.style.cssText = `
            margin-top: 0;
            margin-bottom: 20px;
            color: #333;
            font-size: 1.8em;
            border-bottom: 3px solid #667eea;
            padding-bottom: 10px;
        `;

        const content = document.createElement('div');
        content.innerHTML = `
            <div style="line-height: 1.8; color: #444;">
                <h3 style="color: #667eea; margin-top: 25px; margin-bottom: 10px; font-size: 1.3em;">1️⃣ 시작하기: 반 만들기 (필수)</h3>
                <ul style="padding-left: 25px; margin-bottom: 20px;">
                    <li><strong>✨ 반 만들기</strong>: 프로그램을 처음 사용하시면 상단 바의 "반 만들기" 영역이 하이라이트됩니다</li>
                    <li><strong>➕ 반 추가</strong>: 상단 바의 "반 만들기" 셀렉트 메뉴 옆 ➕ 버튼을 클릭하여 새 반을 추가하세요</li>
                    <li><strong>📚 반 선택</strong>: 셀렉트 메뉴에서 반을 선택하면 해당 반의 저장된 자리 배치도가 자동으로 불러와집니다</li>
                    <li><strong>💾 자동 저장</strong>: 자리를 확정하면 이 컴퓨터에 자동으로 저장됩니다. 따로 저장 버튼을 누르지 않아도 됩니다</li>
                    <li><strong>🗑️ 반 삭제</strong>: 반을 선택한 후 🗑️ 버튼을 클릭하면 해당 반과 저장된 자리 배치도가 삭제됩니다</li>
                    <li>각 반의 자리 배치도는 독립적으로 저장되므로, 여러 반의 자리 배치도를 관리할 수 있습니다</li>
                    <li><strong>⚠️ 중요</strong>: 반을 먼저 만들지 않으면 자리 배치 기능을 사용할 수 없습니다</li>
                </ul>

                <h3 style="color: #667eea; margin-top: 25px; margin-bottom: 10px; font-size: 1.3em;">2️⃣ 저장과 백업 (꼭 읽어주세요)</h3>
                <ul style="padding-left: 25px; margin-bottom: 20px;">
                    <li><strong>로그인이 필요 없습니다</strong>: 반과 학생 명단은 지금 쓰고 계신 <strong>이 컴퓨터의 브라우저</strong>에 저장됩니다</li>
                    <li><strong>⚠️ 브라우저 데이터를 지우면 함께 사라집니다</strong>: "인터넷 사용 기록 삭제"에서 쿠키 및 사이트 데이터를 지우면 명단도 없어집니다</li>
                    <li><strong>⚠️ 다른 컴퓨터에서는 보이지 않습니다</strong>: 학교와 집에서 함께 쓰시려면 아래 내보내기를 이용하세요</li>
                    <li><strong>💾 내보내기</strong>: 상단 바의 "💾 내보내기"를 누르면 모든 반과 명단이 파일 하나로 저장됩니다. USB나 학교 드라이브에 보관해두세요</li>
                    <li><strong>📂 불러오기</strong>: 다른 컴퓨터에서 "📂 불러오기"로 그 파일을 열면 반과 자리 배치가 그대로 복원됩니다</li>
                    <li><strong>같은 반은 덮어쓰고, 파일에 없는 반은 그대로 둡니다</strong>: 실수로 예전 파일을 불러와도 최근에 만든 반이 사라지지 않습니다</li>
                    <li><strong>학기 초·학기 말처럼 중요한 시점에는 내보내기를 해두시길 권합니다</strong></li>
                </ul>

                <h3 style="color: #667eea; margin-top: 25px; margin-bottom: 10px; font-size: 1.3em;">3️⃣ 기본 사용 방법</h3>
                <ol style="padding-left: 25px; margin-bottom: 20px;">
                    <li><strong>📋 옵션1: 좌석 배치 형태</strong> - 원하는 배치 유형을 선택하세요
                        <ul style="padding-left: 20px; margin-top: 8px;">
                            <li><strong>🪑 1명씩 한 줄로 배치</strong>: 개별 좌석을 분단별로 배치 (분단 수: 3~6)
                                <ul style="padding-left: 15px; margin-top: 5px;">
                                    <li>📐 <strong>기본 1줄 배치</strong>: 가로(행) 방향으로 남학생과 여학생을 교대로 순차 배치</li>
                                    <li>👥 <strong>남녀 1줄 배치</strong>: 세로(열) 방향으로 홀수 분단은 남학생, 짝수 분단은 여학생 배치</li>
                                    <li>⚖️ <strong>남녀 대칭 1줄 배치</strong>: 세로(열) 방향으로 남학생을 먼저 앞쪽 분단부터 배치, 그 다음 여학생 배치</li>
                                    <li>🔄 <strong>남녀 순서 바꾸기</strong>: 체크 시 여학생을 먼저 배치하고 남학생을 나중에 배치 (세 가지 옵션 모두 적용)</li>
                                </ul>
                            </li>
                            <li><strong>👫 2명씩 짝꿍 배치</strong>: 두 명이 나란히 앉는 형태 (분단 수: 3~5)
                                <ul style="padding-left: 15px; margin-top: 5px;">
                                    <li>💑 남녀 짝꿍하기: 남학생과 여학생을 짝지어 배치</li>
                                    <li>👨‍👨‍👧‍👧 같은 성끼리 짝꿍하기: 같은 성별끼리 짝지어 배치</li>
                                </ul>
                            </li>
                            <li><strong>👥 모둠 배치</strong>: 모둠 단위로 좌석 배치
                                <ul style="padding-left: 15px; margin-top: 5px;">
                                    <li>3명 모둠 배치: 2x2 그리드 (분단 수: 3~5)</li>
                                    <li>4명 모둠 배치: 2x2 그리드 (분단 수: 3~4)</li>
                                    <li>5명 모둠 배치: 2x3 그리드 (분단 수: 3~5)</li>
                                    <li>6명 모둠 배치: 2x3 그리드 (분단 수: 2~4)</li>
                                    <li>🔄 남녀 섞기: 모둠 내에서 남녀를 균형있게 섞어 배치</li>
                                </ul>
                            </li>
                            <li><strong>✏️ 사용자 구성 배치</strong>: 책상을 원하는 자리로 직접 끌어다 놓아 교실 모양을 만듭니다
                                <ul style="padding-left: 15px; margin-top: 5px;">
                                    <li>ㄷ자, 원형, 섬 모양 등 정해진 틀에 없는 배치를 만들 수 있습니다</li>
                                    <li>책상을 원하는 모양으로 놓은 뒤 <strong>자리 배치하기</strong>를 누르면 학생이 배정됩니다</li>
                                    <li>🧲 다른 책상 가까이 가면 자석처럼 달라붙습니다. 줄이 맞으면 분홍색 안내선이 나타납니다</li>
                                    <li>배정 전에는 카드를 끌면 <strong>책상이 이동</strong>하고, 배정 후에는 <strong>학생이 서로 교환</strong>됩니다</li>
                                    <li>만든 모양은 반별로 저장되어 다음에 열 때 그대로 나타납니다</li>
                                    <li>처음부터 다시 만들려면 "책상 위치 초기화"를 누르세요</li>
                                </ul>
                            </li>
                        </ul>
                    </li>
                    <li><strong>👨‍🎓 옵션2: 학생 자리 수</strong> - 남학생 수와 여학생 수를 입력하세요. 우측에 미리보기가 자동으로 표시됩니다.</li>
                    <li><strong>📏 옵션3: 분단 개수</strong> - 교실의 분단 수를 입력하세요 (선택한 배치 형태에 따라 가능한 범위가 다릅니다)</li>
                    <li><strong>⚙️ 옵션4: 맞춤 구성</strong> - 추가 옵션을 선택하세요
                        <ul style="padding-left: 20px; margin-top: 8px;">
                            <li>🎲 랜덤 배치: 완전 랜덤으로 좌석 배치</li>
                            <li>🔒 고정 좌석 지정 후 랜덤 배치: 특정 좌석을 고정하고 나머지만 랜덤 배치</li>
                        </ul>
                    </li>
                    <li><strong>🪑 좌석 배치하기</strong> - "학생 이름 입력하기" 버튼을 클릭하여 학생 정보를 입력한 후, "자리 배치 실행하기" 버튼을 클릭하면 좌석에 학생들이 배치됩니다</li>
                </ol>

                <h3 style="color: #667eea; margin-top: 25px; margin-bottom: 10px; font-size: 1.3em;">4️⃣ 학생 정보 입력</h3>
                <ul style="padding-left: 25px; margin-bottom: 20px;">
                    <li><strong>📝 학생 이름 입력하기</strong>: 버튼을 클릭하여 학생 정보 입력 테이블을 생성하세요</li>
                    <li><strong>📊 학생 이름 엑셀파일에서 가져오기</strong>: 엑셀 파일을 업로드하여 학생 정보를 한 번에 입력할 수 있습니다</li>
                    <li><strong>📥 학생 이름 양식 다운로드</strong>: 엑셀 양식 파일을 다운로드하여 학생 정보를 작성한 후 업로드하세요</li>
                    <li><strong>📂 우리 반 이름 불러오기</strong>: 이전에 저장한 반 학생 정보를 불러옵니다</li>
                    <li><strong>💾 우리반 학생으로 등록하기</strong>: 현재 입력한 학생 정보를 저장하여 다음에 불러올 수 있습니다</li>
                    <li><strong>➕ 행 추가</strong>: 학생 정보 입력 테이블에서 "행 추가" 버튼을 클릭하여 학생을 추가할 수 있습니다</li>
                    <li><strong>🗑️ 삭제</strong>: 각 행의 삭제 아이콘(🗑️)을 클릭하여 학생을 삭제할 수 있습니다</li>
                </ul>

                <h3 style="color: #667eea; margin-top: 25px; margin-bottom: 10px; font-size: 1.3em;">5️⃣ 고정 좌석 기능</h3>
                <ul style="padding-left: 25px; margin-bottom: 20px;">
                    <li><strong>🔒 고정 좌석 지정</strong>: "옵션4: 맞춤 구성"에서 "고정 좌석 지정 후 랜덤 배치" 옵션을 선택하세요</li>
                    <li>미리보기 화면에서 원하는 좌석 카드를 클릭하면 🔒 아이콘과 빨간 테두리가 표시됩니다</li>
                    <li>학생 정보 입력 테이블의 "고정 좌석" 드롭다운에서 고정된 좌석을 선택하여 학생을 연결하세요</li>
                    <li>고정 좌석이 선택된 행의 번호 셀은 파란색 배경으로 표시됩니다</li>
                    <li>고정 좌석을 제외한 나머지 좌석에만 학생들이 랜덤 배치됩니다</li>
                </ul>

                <h3 style="color: #667eea; margin-top: 25px; margin-bottom: 10px; font-size: 1.3em;">6️⃣ 자리 배치 옵션</h3>
                <ul style="padding-left: 25px; margin-bottom: 20px;">
                    <li><strong>🚫 이전 좌석 안 앉기</strong>: "확정된 자리 이력"에 저장된 이전 배치를 참고하여 같은 좌석에 배치되지 않도록 합니다</li>
                    <li><strong>👥 이전 짝 금지</strong>: "확정된 자리 이력"에 저장된 이전 배치를 참고하여 이전에 같은 짝이었던 학생과 다시 짝지어지지 않도록 합니다</li>
                    <li>두 옵션을 모두 체크하면 두 조건을 모두 만족하도록 배치됩니다</li>
                </ul>

                <h3 style="color: #667eea; margin-top: 25px; margin-bottom: 10px; font-size: 1.3em;">7️⃣ 확정된 자리 이력 (반별 관리)</h3>
                <ul style="padding-left: 25px; margin-bottom: 20px;">
                    <li><strong>✅ 자리 확정하기(저장) 💾</strong>: 자리 배치가 완료된 후 "✅ 자리 확정하기(저장) 💾" 버튼을 클릭하면 현재 배치가 해당 반의 이력에 저장되고 자동으로 저장됩니다</li>
                    <li><strong>⚠️ 중요</strong>: "자리 확정하기(저장) 💾" 버튼을 클릭하지 않으면 이력에 기록되지 않습니다</li>
                    <li><strong>📋 확정된 자리 이력</strong>: 상단 바의 "📋 확정된 자리 이력" 드롭다운에서 현재 선택된 반의 저장된 배치를 확인할 수 있습니다</li>
                    <li><strong>반별 이력 관리</strong>: 각 반의 확정된 자리 이력은 독립적으로 관리됩니다. 반을 변경하면 해당 반의 이력이 자동으로 표시됩니다</li>
                    <li>같은 날짜에 여러 개의 배치가 저장되면 번호가 표시됩니다 (예: 25-11-10 확정자리 (3), (2), (1))</li>
                    <li>이력 항목을 클릭하면 해당 배치를 불러올 수 있습니다</li>
                    <li>이력 항목 옆의 삭제 아이콘(🗑️)을 클릭하면 해당 이력을 삭제할 수 있습니다</li>
                    <li>드롭다운 외부를 클릭하면 드롭다운이 자동으로 닫힙니다</li>
                </ul>

                <h3 style="color: #667eea; margin-top: 25px; margin-bottom: 10px; font-size: 1.3em;">8️⃣ 옵션 설정 기억하기</h3>
                <ul style="padding-left: 25px; margin-bottom: 20px;">
                    <li><strong>💾 옵션 설정 기억하기</strong>: "초기화" 버튼 위의 "옵션 설정 기억하기" 버튼을 클릭하면 현재 설정(옵션1~옵션4)이 저장됩니다</li>
                    <li>다음에 프로그램을 실행하면 저장된 설정이 자동으로 적용됩니다</li>
                </ul>

                <h3 style="color: #667eea; margin-top: 25px; margin-bottom: 10px; font-size: 1.3em;">9️⃣ 자리 바꾸기</h3>
                <ul style="padding-left: 25px; margin-bottom: 20px;">
                    <li>자리 배치가 완료된 후, 좌석 카드를 드래그하여 다른 좌석에 드롭하면 자리를 바꿀 수 있습니다</li>
                    <li>두 카드를 서로 드래그 & 드롭하면 위치가 교환됩니다</li>
                </ul>

                <h3 style="color: #667eea; margin-top: 25px; margin-bottom: 10px; font-size: 1.3em;">🔟 공유 및 출력</h3>
                <ul style="padding-left: 25px; margin-bottom: 20px;">
                    <li><strong>🖨️ 인쇄하기</strong>: 현재 자리 배치도를 인쇄합니다</li>
                </ul>

                <h3 style="color: #667eea; margin-top: 25px; margin-bottom: 10px; font-size: 1.3em;">💡 유용한 팁</h3>
                <ul style="padding-left: 25px; margin-bottom: 20px;">
                    <li><strong>📚 반 관리 팁</strong>: 여러 반을 관리할 때는 각 반의 자리 배치도를 Firebase에 저장해두면 나중에 쉽게 불러올 수 있습니다</li>
                    <li><strong>💾 파일로 백업</strong>: 상단 바의 "💾 내보내기"로 모든 반과 명단을 파일 하나에 저장할 수 있습니다. 다른 컴퓨터에서는 "📂 불러오기"로 그대로 복원됩니다</li>
                    <li><strong>📋 반별 이력 관리</strong>: 각 반의 확정된 자리 이력은 독립적으로 관리되므로, 반을 변경하면 해당 반의 이력이 자동으로 표시됩니다</li>
                    <li>📊 학생 정보 입력 테이블 하단의 통계를 확인하여 남학생/여학생 수와 고정 좌석 수를 확인할 수 있습니다</li>
                    <li>🔒 고정 좌석 모드에서는 미리보기 화면에서 좌석을 클릭하여 고정할 수 있습니다</li>
                    <li>🔄 자리 배치 후에는 드래그 & 드롭으로 자유롭게 자리를 조정할 수 있습니다</li>
                    <li>👥 모둠 배치 시 "남녀 섞기" 옵션을 사용하면 모둠 내에서 남녀를 균형있게 배치할 수 있습니다</li>
                    <li>📐 "1명씩 한 줄로 배치" 옵션에서 "남녀 순서 바꾸기" 체크박스를 사용하면 여학생을 먼저 배치할 수 있습니다</li>
                    <li>⚖️ "남녀 대칭 1줄 배치"는 남학생을 앞쪽 분단부터 배치하고, 여학생을 나머지 자리에 배치하는 대칭적인 배치 방식입니다</li>
                    <li>◀ 좌측 사이드바의 토글 버튼(◀)을 클릭하면 사이드바를 접거나 펼칠 수 있습니다</li>
                    <li>↶↷ 상단 바의 되돌리기(↶)와 다시 실행하기(↷) 버튼으로 자리 배치 변경 이력을 관리할 수 있습니다</li>
                    <li><strong>✨ 반 만들기 하이라이트</strong>: 반이 없으면 상단 바의 "반 만들기" 영역이 하이라이트되어 먼저 반을 만들어야 함을 알려줍니다</li>
                </ul>

                <div style="margin-top: 30px; padding: 15px; background: #f0f8ff; border-left: 4px solid #667eea; border-radius: 4px;">
                    <strong style="color: #667eea;">제작자:</strong> 김신회<br>
                    <strong style="color: #667eea;">Copyright:</strong> Copyright (c) 2025 김신회
                </div>
            </div>
        `;

        const buttonContainer = document.createElement('div');
        buttonContainer.style.cssText = `
            margin-top: 25px;
            text-align: right;
        `;

        const closeButton = document.createElement('button');
        closeButton.textContent = '❌ 닫기';
        closeButton.className = 'primary-btn';
        closeButton.style.cssText = `
            padding: 10px 24px;
            font-size: 1em;
        `;

        // 모달 닫기 함수
        const closeModal = () => {
            try {
                if (modal && modal.parentNode) {
                    document.body.removeChild(modal);
                }
                document.removeEventListener('keydown', handleKeyDown);
            } catch (error) {
                logger.warn('모달 닫기 중 오류 (무시됨):', error);
            }
        };

        closeButton.onclick = closeModal;

        // ESC 키로 모달 닫기
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeModal();
            }
        };

        buttonContainer.appendChild(closeButton);

        modalContent.appendChild(title);
        modalContent.appendChild(content);
        modalContent.appendChild(buttonContainer);
        modal.appendChild(modalContent);
        document.body.appendChild(modal);

        this.addEventListenerSafe(document, 'keydown', handleKeyDown as (e: Event) => void);

        // 모달 배경 클릭으로 닫기
        modal.onclick = (e) => {
            if (e.target === modal) {
                closeModal();
            }
        };
    }

    /**
     * 사이드바 토글
     */
    private toggleSidebar(): void {
        const sidebar = document.getElementById('sidebar');
        const mainContainer = document.querySelector('.main-container');
        const overlay = document.getElementById('sidebar-overlay');
        const toggleBtn = document.getElementById('sidebar-toggle-btn');
        
        if (sidebar && mainContainer) {
            const isCollapsed = sidebar.classList.contains('collapsed');
            
            if (isCollapsed) {
                // 사이드바 열기
                sidebar.classList.remove('collapsed');
                sidebar.classList.add('open');
                mainContainer.classList.remove('sidebar-collapsed');
                
                // 모바일에서 오버레이 표시
                if (overlay && window.innerWidth <= 768) {
                    overlay.classList.add('active');
                    overlay.setAttribute('aria-hidden', 'false');
                }
                
                if (toggleBtn) {
                    toggleBtn.setAttribute('aria-expanded', 'true');
                }
            } else {
                // 사이드바 닫기
                sidebar.classList.add('collapsed');
                sidebar.classList.remove('open');
                mainContainer.classList.add('sidebar-collapsed');
                
                // 오버레이 숨기기
                if (overlay) {
                    overlay.classList.remove('active');
                    overlay.setAttribute('aria-hidden', 'true');
                }
                
                if (toggleBtn) {
                    toggleBtn.setAttribute('aria-expanded', 'false');
                }
            }
        }
    }

    /**
     * 모바일 반응형 초기화
     */
    private initializeMobileResponsive(): void {
        // 화면 크기 변경 감지
        let resizeTimer: number | null = null;
        this.addEventListenerSafe(window, 'resize', () => {
            if (resizeTimer) {
                clearTimeout(resizeTimer);
            }
            resizeTimer = window.setTimeout(() => {
                this.handleResize();
            }, 250);
        });

        // 초기 화면 크기에 따라 사이드바 상태 설정
        this.handleResize();

        // 오버레이 클릭 시 사이드바 닫기
        const overlay = document.getElementById('sidebar-overlay');
        if (overlay) {
            this.addEventListenerSafe(overlay, 'click', () => {
                this.toggleSidebar();
            });
        }
    }

    /**
     * 화면 크기 변경 처리
     */
    private handleResize(): void {
        const isMobile = window.innerWidth <= 768;
        const sidebar = document.getElementById('sidebar');
        const mainContainer = document.querySelector('.main-container');
        const overlay = document.getElementById('sidebar-overlay');
        
        if (!sidebar || !mainContainer) return;

        if (isMobile) {
            // 모바일: 사이드바는 기본적으로 닫힘
            if (!sidebar.classList.contains('collapsed') && !sidebar.classList.contains('open')) {
                sidebar.classList.add('collapsed');
                mainContainer.classList.add('sidebar-collapsed');
            }
        } else {
            // 데스크톱: 사이드바는 기본적으로 열림
            sidebar.classList.remove('open');
            if (overlay) {
                overlay.classList.remove('active');
                overlay.setAttribute('aria-hidden', 'true');
            }
        }
    }

    /**
     * 키보드 네비게이션 초기화
     */
    private initializeKeyboardNavigation(): void {
        // Tab 순서 최적화
        this.optimizeTabOrder();
        
        // 포커스 표시 개선
        this.enhanceFocusStyles();
        
        // 키보드 드래그&드롭 활성화
        this.setupKeyboardDragDrop();
    }

    /**
     * Tab 순서 최적화
     */
    private optimizeTabOrder(): void {
        // 주요 버튼들에 Tab 순서 설정
        const primaryButtons = [
            document.getElementById('arrange-seats'),
            document.getElementById('reset-app'),
            document.getElementById('save-options'),
            document.getElementById('sidebar-toggle-btn'),
            document.getElementById('user-manual-btn')
        ].filter(Boolean) as HTMLElement[];

        KeyboardNavigation.setTabOrder(primaryButtons, 1);

        // 입력 필드들에 Tab 순서 설정
        const inputFields = [
            document.getElementById('male-students'),
            document.getElementById('female-students'),
            document.getElementById('number-of-partitions')
        ].filter(Boolean) as HTMLElement[];

        KeyboardNavigation.setTabOrder(inputFields, 10);
    }

    /**
     * 포커스 표시 개선
     */
    private enhanceFocusStyles(): void {
        // 모든 포커스 가능한 요소에 접근성 속성 추가
        const focusableElements = document.querySelectorAll(
            'button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as NodeListOf<HTMLElement>;

        focusableElements.forEach((element) => {
            // aria-label이 없으면 title에서 가져오기
            if (!element.hasAttribute('aria-label') && element.hasAttribute('title')) {
                element.setAttribute('aria-label', element.getAttribute('title') || '');
            }
        });
    }

    /**
     * 키보드 드래그&드롭 설정
     */
    private setupKeyboardDragDrop(): void {
        const seatsArea = document.getElementById('seats-area');
        if (!seatsArea) return;

        // 키보드 드래그&드롭 매니저 초기화
        this.keyboardDragDropManager = new KeyboardDragDropManager(
            'seats-area',
            (sourceCard: HTMLElement, direction: 'up' | 'down' | 'left' | 'right') =>
                this.handleKeyboardSeatMove(sourceCard, direction),
            (seatId: number) => this.fixedSeatIds.has(seatId)
        );

        // 활성화는 한 번이면 된다.
        // KeyboardDragDropManager가 자체 MutationObserver로 카드 목록 변화를 따라가므로
        // 여기서 DOM 변경마다 enable()을 다시 부를 필요가 없다.
        // (그렇게 하면 리스너가 겹쳐 쌓여 키 입력이 두 번 처리된다)
        this.keyboardDragDropManager.enable();
    }

    /**
     * 키보드로 좌석 이동 처리
     */
    private handleKeyboardSeatMove(
        sourceCard: HTMLElement,
        direction: 'up' | 'down' | 'left' | 'right'
    ): HTMLElement | null {
        const sourceSeatIdStr = sourceCard.getAttribute('data-seat-id');
        if (!sourceSeatIdStr) return null;

        const sourceSeatId = parseInt(sourceSeatIdStr, 10);
        if (isNaN(sourceSeatId)) return null;

        // 방향에 따라 인접한 좌석 찾기
        const targetCard = this.findAdjacentSeat(sourceCard, direction);
        if (!targetCard) return null;

        const targetSeatIdStr = targetCard.getAttribute('data-seat-id');
        if (!targetSeatIdStr) return null;

        const targetSeatId = parseInt(targetSeatIdStr, 10);
        if (isNaN(targetSeatId)) return null;

        // 고정 좌석은 이동 불가
        if (this.fixedSeatIds.has(targetSeatId)) {
            return null;
        }

        // 좌석 교환
        this.swapSeats(sourceCard, targetCard);

        // 히스토리 저장
        this.setTimeoutSafe(() => {
            this.saveLayoutToHistory();
        }, 50);

        // 학생이 옮겨간 자리를 알려 포커스가 따라가도록 한다
        return targetCard;
    }

    /**
     * 인접한 좌석 찾기
     */
    private findAdjacentSeat(card: HTMLElement, direction: 'up' | 'down' | 'left' | 'right'): HTMLElement | null {
        const seatsArea = document.getElementById('seats-area');
        if (!seatsArea) return null;

        const allCards = Array.from(seatsArea.querySelectorAll('.student-seat-card')) as HTMLElement[];
        if (allCards.length === 0) return null;

        const cardRect = card.getBoundingClientRect();
        const cardCenterX = cardRect.left + cardRect.width / 2;
        const cardCenterY = cardRect.top + cardRect.height / 2;

        let bestMatch: HTMLElement | null = null;
        let minDistance = Infinity;

        // 진행 방향과 수직인 어긋남에 곱할 가중치
        const PERPENDICULAR_WEIGHT = 3;

        allCards.forEach((otherCard) => {
            if (otherCard === card || otherCard.classList.contains('fixed-seat')) return;

            const otherRect = otherCard.getBoundingClientRect();
            const otherCenterX = otherRect.left + otherRect.width / 2;
            const otherCenterY = otherRect.top + otherRect.height / 2;

            let isInDirection = false;
            let distance = 0;

            switch (direction) {
                // 진행 방향과 수직인 어긋남에 가중치를 준다.
                // 같은 가중치로 더하면 위쪽 바로 옆 칸보다 두 칸 옆의 카드가
                // 선택되는 일이 생겨, 화살표 방향과 다른 자리로 이동한다.
                case 'up':
                    isInDirection = otherCenterY < cardCenterY;
                    distance = Math.abs(otherCenterX - cardCenterX) * PERPENDICULAR_WEIGHT + (cardCenterY - otherCenterY);
                    break;
                case 'down':
                    isInDirection = otherCenterY > cardCenterY;
                    distance = Math.abs(otherCenterX - cardCenterX) * PERPENDICULAR_WEIGHT + (otherCenterY - cardCenterY);
                    break;
                case 'left':
                    isInDirection = otherCenterX < cardCenterX;
                    distance = Math.abs(otherCenterY - cardCenterY) * PERPENDICULAR_WEIGHT + (cardCenterX - otherCenterX);
                    break;
                case 'right':
                    isInDirection = otherCenterX > cardCenterX;
                    distance = Math.abs(otherCenterY - cardCenterY) * PERPENDICULAR_WEIGHT + (otherCenterX - cardCenterX);
                    break;
            }

            if (isInDirection && distance < minDistance) {
                minDistance = distance;
                bestMatch = otherCard;
            }
        });

        return bestMatch;
    }

    /**
     * 좌석 교환
     */
    private moveCardContentTo(
        allCards: HTMLElement[],
        source: HTMLElement,
        closestCard: HTMLElement,
        insertBefore: boolean
    ): void {
        // 고정 좌석이 섞여 있으면 회전으로 내용이 밀려 고정이 깨진다
        if (source.classList.contains('fixed-seat')) return;

        const sourceIndex = allCards.indexOf(source);
        const closestIndex = allCards.indexOf(closestCard);
        if (sourceIndex === -1 || closestIndex === -1) return;

        const targetIndex = SeatReorderService.resolveTargetIndex(closestIndex, insertBefore, sourceIndex);
        if (targetIndex === sourceIndex) return;

        // 이동 구간에 고정 좌석이 있으면 옮기지 않는다
        const [rangeStart, rangeEnd] = sourceIndex < targetIndex
            ? [sourceIndex, targetIndex]
            : [targetIndex, sourceIndex];
        for (let i = rangeStart; i <= rangeEnd; i++) {
            if (allCards[i].classList.contains('fixed-seat')) return;
        }

        const contents = allCards.map(card => this.readCardContent(card));
        const rotated = SeatReorderService.rotate(contents, sourceIndex, targetIndex);
        rotated.forEach((content, index) => this.writeCardContent(allCards[index], content));
    }

    /**
     * 카드에 표시된 내용(이름/성별) 읽기
     */
    private readCardContent(card: HTMLElement): { name: string; isMale: boolean; isFemale: boolean } {
        const nameEl = card.querySelector('.student-name') as HTMLElement | null;
        return {
            name: nameEl?.textContent || '',
            isMale: card.classList.contains('gender-m'),
            isFemale: card.classList.contains('gender-f')
        };
    }

    /**
     * 카드에 내용 쓰기 (자리 번호는 그대로 둔다)
     */
    private writeCardContent(
        card: HTMLElement,
        content: { name: string; isMale: boolean; isFemale: boolean }
    ): void {
        const nameEl = card.querySelector('.student-name') as HTMLElement | null;
        if (nameEl) nameEl.textContent = content.name;

        card.classList.toggle('gender-m', content.isMale);
        card.classList.toggle('gender-f', content.isFemale);

        const seatId = card.getAttribute('data-seat-id');
        if (seatId) {
            const genderLabel = content.isMale ? '남학생 ♂' : (content.isFemale ? '여학생 ♀' : '');
            const genderInfo = genderLabel ? ` (${genderLabel})` : '';
            const displayName = content.name || '빈 좌석';
            card.setAttribute('aria-label', `좌석 ${seatId}: ${displayName}${genderInfo}. 화살표 키로 이동, Enter로 선택`);
        }
    }

    /**
     * 두 좌석의 학생을 맞바꿈 (자리 번호는 자리에 남는다)
     */
    private swapSeats(sourceCard: HTMLElement, targetCard: HTMLElement): void {
        const srcNameEl = sourceCard.querySelector('.student-name') as HTMLElement | null;
        const tgtNameEl = targetCard.querySelector('.student-name') as HTMLElement | null;
        
        if (!srcNameEl || !tgtNameEl) return;

        // 이름 스왑
        const tmpName = srcNameEl.textContent || '';
        srcNameEl.textContent = tgtNameEl.textContent || '';
        tgtNameEl.textContent = tmpName;

        // 성별 배경 클래스 스왑
        const srcIsM = sourceCard.classList.contains('gender-m');
        const srcIsF = sourceCard.classList.contains('gender-f');
        const tgtIsM = targetCard.classList.contains('gender-m');
        const tgtIsF = targetCard.classList.contains('gender-f');

        sourceCard.classList.toggle('gender-m', tgtIsM);
        sourceCard.classList.toggle('gender-f', tgtIsF);
        targetCard.classList.toggle('gender-m', srcIsM);
        targetCard.classList.toggle('gender-f', srcIsF);

        // ARIA 레이블 업데이트 (성별 정보 포함)
        const srcSeatId = sourceCard.getAttribute('data-seat-id');
        const tgtSeatId = targetCard.getAttribute('data-seat-id');
        const srcName = srcNameEl.textContent || '빈 좌석';
        const tgtName = tgtNameEl.textContent || '빈 좌석';
        
        // 성별 정보 가져오기
        const srcIsMale = sourceCard.classList.contains('gender-m');
        const srcIsFemale = sourceCard.classList.contains('gender-f');
        const tgtIsMale = targetCard.classList.contains('gender-m');
        const tgtIsFemale = targetCard.classList.contains('gender-f');
        
        const srcGenderLabel = srcIsMale ? '남학생 ♂' : (srcIsFemale ? '여학생 ♀' : '');
        const tgtGenderLabel = tgtIsMale ? '남학생 ♂' : (tgtIsFemale ? '여학생 ♀' : '');

        if (srcSeatId) {
            const genderInfo = srcGenderLabel ? ` (${srcGenderLabel})` : '';
            sourceCard.setAttribute('aria-label', `좌석 ${srcSeatId}: ${tgtName}${genderInfo}. 화살표 키로 이동, Enter로 선택`);
        }
        if (tgtSeatId) {
            const genderInfo = tgtGenderLabel ? ` (${tgtGenderLabel})` : '';
            targetCard.setAttribute('aria-label', `좌석 ${tgtSeatId}: ${srcName}${genderInfo}. 화살표 키로 이동, Enter로 선택`);
        }

        // 성공 피드백
        targetCard.style.transform = 'scale(1.05)';
        setTimeout(() => {
            targetCard.style.transform = '';
        }, 200);
    }

    /**
     * 반 관리 초기화
     */
    private initializeClassManagement(): void {
        // 반 선택 셀렉트 메뉴 변경 이벤트
        const classSelect = document.getElementById('class-select') as HTMLSelectElement;
        if (classSelect) {
            this.addEventListenerSafe(classSelect, 'change', (e) => {
                const target = e.target as HTMLSelectElement;
                const classId = target.value;
                this.handleClassSelectChange(classId);
            });
        }

        // 반 목록 업데이트
        this.updateClassSelect();
        
        // 반이 없는 경우 '반 만들기' 하이라이트 애니메이션 적용
        this.checkAndHighlightClassCreation();
    }
    
    /**
     * 반이 없는 경우 '반 만들기' 하이라이트 애니메이션 적용
     * 처음 방문자뿐만 아니라 데이터가 없는 사용자도 하이라이트 표시
     */
    private checkAndHighlightClassCreation(): void {
        const classList = this.classManager.getClassList();
        const hasClasses = classList.length > 0;
        
        // 반이 있으면 하이라이트 제거
        if (hasClasses) {
            this.removeClassCreationHighlight();
            return;
        }
        
        // 반이 없으면 하이라이트 적용 (처음 방문자뿐만 아니라 데이터가 없는 모든 사용자)
        const classSelectContainer = document.getElementById('class-select-container');
        if (classSelectContainer) {
            classSelectContainer.classList.add('first-visit-highlight');
            logger.info('반이 없어서 반 만들기 하이라이트 적용');
        }
    }
    
    /**
     * 반 만들기 하이라이트 애니메이션 제거
     */
    private removeClassCreationHighlight(): void {
        const classSelectContainer = document.getElementById('class-select-container');
        if (classSelectContainer) {
            classSelectContainer.classList.remove('first-visit-highlight');
        }
    }

    /**
     * 반 선택 셀렉트 메뉴 업데이트
     */
    private updateClassSelect(): void {
        const classSelect = document.getElementById('class-select') as HTMLSelectElement;
        const deleteBtn = document.getElementById('delete-class-btn') as HTMLButtonElement;
        
        if (!classSelect) return;

        const classList = this.classManager.getClassList();
        const currentClassId = this.classManager.getCurrentClassId();

        // 기존 옵션 제거 (첫 번째 옵션 제외)
        while (classSelect.options.length > 1) {
            classSelect.remove(1);
        }

        // 반 목록 추가
        classList.forEach(classInfo => {
            const option = document.createElement('option');
            option.value = classInfo.id;
            option.textContent = classInfo.name;
            classSelect.appendChild(option);
        });

        // 현재 선택된 반 설정
        if (currentClassId) {
            classSelect.value = currentClassId;
        } else {
            classSelect.value = '';
        }

        // 반 삭제 버튼은 항상 보이되, 삭제할 반이 없으면 누를 수 없게 한다.
        // 숨겨 버리면 그런 기능이 있다는 것 자체를 알 수 없다.
        const hasSelection = classSelect.value !== '';
        if (deleteBtn) {
            deleteBtn.style.display = '';
            deleteBtn.disabled = !hasSelection;
            deleteBtn.title = hasSelection
                ? '선택한 반 삭제'
                : '삭제할 반을 먼저 선택하세요';
            deleteBtn.setAttribute('aria-label', deleteBtn.title);
        }
        
        // 반 목록 업데이트 후 하이라이트 상태 확인 (반이 삭제된 경우를 대비)
        this.checkAndHighlightClassCreation();
    }

    /**
     * 반 선택 변경 처리
     */
    private handleClassSelectChange(classId: string): void {
        const previousClassId = this.classManager.getCurrentClassId();

        // 반이 선택되지 않은 상태에서 입력하거나 엑셀로 불러온 명단은
        // 아직 어느 반에도 속해 있지 않다. 명단은 반마다 따로 관리되므로
        // 반을 선택하는 순간 화면의 명단은 지워진다.
        // 그대로 두면 방금 불러온 명단이 아무 안내 없이 사라지므로 먼저 확인한다.
        // (반이 이미 선택된 상태였다면 그 명단은 해당 반에 저장되므로 묻지 않는다)
        if (!previousClassId) {
            const unsavedCount = this.countEnteredStudents();
            if (unsavedCount > 0) {
                const proceed = confirm(
                    `현재 입력된 학생 ${unsavedCount}명의 명단이 지워집니다.\n` +
                    '학생 명단은 반마다 따로 관리되기 때문입니다.\n\n' +
                    '계속하시려면 확인을 누르세요.\n' +
                    '명단을 지키려면 취소를 누른 뒤, 반을 먼저 선택하고 명단을 다시 불러오세요.'
                );

                if (!proceed) {
                    // 선택을 원래대로 되돌린다
                    const classSelect = document.getElementById('class-select') as HTMLSelectElement | null;
                    if (classSelect) {
                        classSelect.value = '';
                    }
                    return;
                }
            }
        }

        // 반이 바뀌면 되돌리기 이력은 더 이상 유효하지 않다.
        // 비우지 않으면 다른 반에서 Ctrl+Z를 눌렀을 때
        // 이전 반의 자리 배치가 현재 반 화면에 복원된다.
        this.resetHistory();

        if (!classId || classId === '') {
            // 선택 해제
            this.classManager.selectClass(null);
            this.updateClassSelect();
            // 반이 선택되지 않았으므로 이력 드롭다운 업데이트 (빈 상태로)
            this.updateHistoryDropdown();
            // 읽기 전용 모드 해제
            this.disableReadOnlyMode();
            return;
        }

        // 반 선택
        this.classManager.selectClass(classId);
        this.updateClassSelect();
        
        // 반이 변경되었으므로 해당 반의 이력 드롭다운 업데이트
        this.updateHistoryDropdown();

        // 읽기 전용 모드 해제 (새 반 선택 시)
        this.disableReadOnlyMode();

        // 현재 화면의 자리 배치도 지우기
        this.clearCurrentLayout();

        // 먼저 확정된 자리 이력의 최신 항목을 불러오기 시도
        // 명시적으로 반 ID를 전달하여 해당 반의 이력만 가져오기
        const history = this.getSeatHistory(classId);
        
        // 디버깅: 반 ID와 이력 개수 로그
        logger.info('반 선택 시 이력 확인:', {
            classId,
            historyCount: history.length,
            historyItems: history.map(item => ({
                id: item.id,
                date: item.date,
                layoutType: item.layoutType,
                singleMode: item.singleMode,
                pairMode: item.pairMode,
                classId: item.classId
            }))
        });
        
        if (history.length > 0) {
            // 최신 확정된 자리 배치도 불러오기 (읽기 전용)
            const latestHistoryItem = history[0]; // 이미 timestamp 기준 내림차순 정렬됨
            
            // 추가 검증: 이력 항목의 classId가 현재 반 ID와 일치하는지 확인
            if (latestHistoryItem.classId && latestHistoryItem.classId !== classId) {
                logger.error('이력 항목의 반 ID가 일치하지 않음:', {
                    expectedClassId: classId,
                    actualClassId: latestHistoryItem.classId,
                    historyId: latestHistoryItem.id
                });
                // 잘못된 이력이면 저장된 자리 배치도 불러오기 시도
                this.classManager.loadLayout(classId).then((loaded) => {
                    if (!loaded) {
                        // 저장된 배치도도 없으면 기본 1줄 배치 화면 표시
                        this.showInitialLayout();
                    }
                });
                return;
            }
            
            logger.info('반 선택 시 최신 확정된 자리 배치도 불러오기:', {
                classId,
                historyId: latestHistoryItem.id,
                date: latestHistoryItem.date,
                layoutType: latestHistoryItem.layoutType,
                singleMode: latestHistoryItem.singleMode,
                pairMode: latestHistoryItem.pairMode
            });
            
            this.loadHistoryItem(latestHistoryItem.id).catch((error) => {
                logger.error('확정된 자리 배치도 불러오기 실패:', error);
                // 실패 시 저장된 자리 배치도 불러오기 시도
                this.classManager.loadLayout(classId).then((loaded) => {
                    if (!loaded) {
                        // 저장된 배치도도 없으면 기본 1줄 배치 화면 표시
                        this.showInitialLayout();
                    }
                });
            });
        } else {
            // 확정된 자리 이력이 없으면 저장된 자리 배치도 불러오기 시도
            this.classManager.loadLayout(classId).then((loaded) => {
                if (!loaded) {
                    // 저장된 배치도도 없으면 기본 1줄 배치 화면 표시
                    this.showInitialLayout();
                }
            });
        }
    }

    /**
     * 화면의 명렬표에 실제로 입력된 학생 수
     */
    private countEnteredStudents(): number {
        const outputSection = document.getElementById('output-section');
        if (!outputSection) return 0;

        const rows = outputSection.querySelectorAll('.student-input-table tbody tr');
        let count = 0;

        rows.forEach(row => {
            const nameInput = row.querySelector('.student-name-input') as HTMLInputElement | null;
            if (nameInput && nameInput.value.trim()) {
                count++;
            }
        });

        return count;
    }

    /**
     * 현재 화면의 자리 배치도 지우기
     */
    private clearCurrentLayout(): void {
        try {
            // 좌석 영역 초기화
            const seatsArea = document.getElementById('seats-area');
            if (seatsArea) {
                seatsArea.innerHTML = '';
            }

            // 내부 상태 초기화
            this.students = [];
            this.seats = [];
            this.nextSeatId = 1;
            // 반마다 교실 모양이 다르므로 이전 반의 배치를 물려받지 않는다
            this.customPositions = null;
            this.pendingCustomPositions = null;
            this.fixedSeatIds.clear();

            // 학생 테이블 제거 (존재한다면)
            const outputSection = document.getElementById('output-section');
            if (outputSection) {
                const tables = outputSection.querySelectorAll('table');
                tables.forEach(t => t.remove());
            }

            // 액션 버튼 숨김
            const actionButtons = document.getElementById('layout-action-buttons');
            if (actionButtons) {
                actionButtons.style.display = 'none';
            }

            logger.info('현재 화면의 자리 배치도 지우기 완료');
        } catch (error) {
            logger.error('현재 화면 지우기 중 오류:', error);
        }
    }

    /**
     * 초기 화면의 자리 배치도 표시 (1명씩 한 줄로 배치 --> 기본 1줄 배치)
     */
    private showInitialLayout(): void {
        try {
            // 현재 화면이 이미 지워졌는지 확인 (반 선택 시 clearCurrentLayout 호출됨)
            // 하지만 혹시 모를 경우를 대비해 다시 확인
            
            // 기본 옵션 설정
            const maleInput = document.getElementById('male-students') as HTMLInputElement;
            const femaleInput = document.getElementById('female-students') as HTMLInputElement;
            const partitionInput = document.getElementById('number-of-partitions') as HTMLInputElement;
            
            // 기본값 설정 (초기값)
            if (maleInput) maleInput.value = '12';
            if (femaleInput) femaleInput.value = '12';
            if (partitionInput) partitionInput.value = '5';

            // layout-type: 1명씩 한 줄로 배치 (single-uniform)
            const singleUniformRadio = document.querySelector('input[name="layout-type"][value="single-uniform"]') as HTMLInputElement;
            if (singleUniformRadio) {
                singleUniformRadio.checked = true;
                // 이벤트 발생 없이 설정 (자동 배치 방지)
            }

            // single-mode: 기본 1줄 배치 (basic-row)
            const basicRowRadio = document.querySelector('input[name="single-mode"][value="basic-row"]') as HTMLInputElement;
            if (basicRowRadio) {
                basicRowRadio.checked = true;
            }

            // 서브메뉴 표시 설정
            const singleSubmenu = document.getElementById('single-submenu');
            const pairSubmenu = document.getElementById('pair-submenu');
            if (singleSubmenu) {
                singleSubmenu.style.display = 'block';
            }
            if (pairSubmenu) {
                pairSubmenu.style.display = 'none';
            }

            // 좌석 영역이 비어있는지 확인 (이미 clearCurrentLayout에서 지워졌을 수 있음)
            // 하지만 확실하게 하기 위해 다시 확인
            const seatsArea = document.getElementById('seats-area');
            if (seatsArea) {
                seatsArea.innerHTML = '';
            }

            // card-layout-container 표시
            const cardContainer = document.getElementById('card-layout-container');
            if (cardContainer) {
                cardContainer.style.display = 'block';
            }

            // 인원수 입력값에 맞춰 미리보기를 생성한다.
            //
            // renderExampleCards()는 this.students를 그리기만 할 뿐 만들지 않는다.
            // 바로 앞에서 clearCurrentLayout()이 this.students를 비웠으므로,
            // 이것만 호출하면 분단 레이블만 있고 자리는 하나도 없는 화면이 된다.
            // 인원수로부터 미리보기 학생과 좌석을 만드는 것은
            // updatePreviewForGenderCounts()이며, 좌석 배치 형태 라디오를 누르면
            // 실행되는 것도 이 함수다. (그래서 라디오를 누르면 화면이 정상으로 돌아왔다)
            this.nextSeatId = 1;
            this.updatePreviewForGenderCounts();

            logger.info('초기 화면의 자리 배치도 표시 완료');
        } catch (error) {
            logger.error('초기 화면 표시 중 오류:', error);
            this.outputModule.showError('초기 화면 표시 중 오류가 발생했습니다.');
        }
    }

    /**
     * 새 반 추가 처리
     */
    private handleAddClass(): void {
        const className = prompt('반 이름을 입력하세요:');
        if (!className) {
            return;
        }

        // 비동기 처리
        this.classManager.addClass(className).then((classId) => {
            if (classId) {
                // 반 목록 업데이트
                this.updateClassSelect();
                
                // 새로 추가된 반 선택
                const classSelect = document.getElementById('class-select') as HTMLSelectElement;
                if (classSelect) {
                    classSelect.value = classId;
                    this.handleClassSelectChange(classId);
                }
                
                // 방문 기록 저장 (하이라이트는 updateClassSelect에서 자동으로 제거됨)
                this.storageManager.safeSetItem('hasVisitedBefore', 'true');
                
                // 사용자에게 안내 메시지 표시
                this.outputModule.showSuccess(`"${className}" 반이 생성되었습니다! 이제 좌측 사이드바에서 학생 정보를 입력하고 자리 배치를 실행하세요.`);
            }
        });
    }

    /**
     * 반 삭제 처리
     */
    private handleDeleteClass(): void {
        const currentClassId = this.classManager.getCurrentClassId();
        if (!currentClassId) {
            this.outputModule.showError('삭제할 반이 선택되지 않았습니다.');
            return;
        }

        const className = this.classManager.getClassName(currentClassId);
        if (!className) {
            this.outputModule.showError('반 정보를 찾을 수 없습니다.');
            return;
        }

        if (confirm(`"${className}" 반을 삭제하시겠습니까?\n저장된 자리 배치도도 함께 삭제됩니다.`)) {
            // 비동기 처리
            this.classManager.deleteClass(currentClassId).then((deleted) => {
                if (deleted) {
                    // 반 목록 업데이트
                    this.updateClassSelect();
                    
                    // 현재 배치도 초기화
                    this.seats = [];
                    this.students = [];
                    const seatsArea = document.getElementById('seats-area');
                    if (seatsArea) {
                        seatsArea.innerHTML = '';
                    }
                }
            });
        }
    }

    /**
     * 현재 반의 자리 배치도 저장 처리
     * @deprecated '저장하기' 버튼이 제거되어 자리 확정하기에서 통합되었습니다. 더 이상 사용되지 않습니다.
     */
    // private handleSaveClassLayout(): void {
    //     // 비동기 처리
    //     this.classManager.saveCurrentLayout().then((saved) => {
    //         // 저장 성공 시 하이라이트 제거
    //         if (saved) {
    //             this.removeSaveButtonHighlight();
    //             
    //             // 확정된 자리 이력도 Firebase에 저장
    //             const currentClassId = this.classManager?.getCurrentClassId();
    //             if (currentClassId && this.firebaseStorageManager?.getIsAuthenticated()) {
    //                 const history = this.getSeatHistory(currentClassId);
    //                 if (history.length > 0) {
    //                     this.firebaseStorageManager.saveSeatHistory(currentClassId, history).then((firebaseSaved) => {
    //                         if (firebaseSaved) {
    //                             logger.info('✅ 저장하기 클릭 시 Firebase에 확정된 자리 이력 저장 완료');
    //                         } else {
    //                             logger.warn('⚠️ 저장하기 클릭 시 Firebase에 확정된 자리 이력 저장 실패');
    //                         }
    //                     }).catch((error) => {
    //                         logger.error('❌ 저장하기 클릭 시 Firebase에 확정된 자리 이력 저장 실패:', error);
    //                     });
    //                 }
    //             }
    //         }
    //         // 저장 성공 메시지는 ClassManager에서 표시됨
    //     });
    // }
    
    /**
     * '저장하기' 버튼 하이라이트 (자리 확정했지만 아직 저장하지 않음을 표시)
     * @deprecated '저장하기' 버튼이 제거되어 더 이상 사용되지 않습니다.
     */
    // private highlightSaveButton(): void {
    //     const saveBtn = document.getElementById('save-layout-btn') as HTMLButtonElement;
    //     if (saveBtn) {
    //         // 노란색 테두리 하이라이트 스타일 추가
    //         saveBtn.style.border = '2px solid #ffeb3b';
    //         saveBtn.style.boxShadow = '0 0 15px rgba(255, 235, 59, 0.8)';
    //         saveBtn.style.animation = 'saveButtonBlink 1s infinite';
    //         
    //         // CSS 깜박이는 애니메이션 추가 (없는 경우)
    //         if (!document.getElementById('save-button-blink-animation')) {
    //             const style = document.createElement('style');
    //             style.id = 'save-button-blink-animation';
    //             style.textContent = `
    //                 @keyframes saveButtonBlink {
    //                     0%, 100% {
    //                         border-color: #ffeb3b;
    //                         box-shadow: 0 0 15px rgba(255, 235, 59, 0.8);
    //                         opacity: 1;
    //                     }
    //                     50% {
    //                         border-color: #ffc107;
    //                         box-shadow: 0 0 25px rgba(255, 193, 7, 1);
    //                         opacity: 0.7;
    //                     }
    //                 }
    //             `;
    //             document.head.appendChild(style);
    //         }
    //     }
    // }
    
    /**
     * '저장하기' 버튼 하이라이트 제거
     * @deprecated '저장하기' 버튼이 제거되어 더 이상 사용되지 않습니다.
     */
    // private removeSaveButtonHighlight(): void {
    //     const saveBtn = document.getElementById('save-layout-btn') as HTMLButtonElement;
    //     if (saveBtn) {
    //         // 하이라이트 스타일 제거
    //         saveBtn.style.border = '';
    //         saveBtn.style.boxShadow = '';
    //         saveBtn.style.animation = '';
    //     }
    // }
}
